import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const I18nContext = createContext({ t: (key, fallback) => fallback, language: 'en', setLanguage: () => {} });

const translations = {
  en: {
    nav: {
      dashboard: 'Dashboard',
      aiInsights: 'AI Insights',
      contacts: 'Contacts',
      companies: 'Companies',
      deals: 'Deals',
      marketing: 'Marketing',
      tasks: 'Tasks',
      reports: 'Reports',
      activities: 'Activities',
      settings: 'Settings',
      billing: 'Billing',
      automations: 'Automations',
      importExport: 'Import/Export',
      calendar: 'Calendar',
    },
    header: {
      searchPlaceholder: 'Search contacts, companies, deals...'
    },
    auth: {
      authenticating: 'Authenticating...'
    },
    landing: {
      heroBadge: 'Where Heritage Meets AI',
      heroTitlePrefix: 'Build Relationships',
      heroTitleHighlight: ' Like the Bissa',
      ctaPrimary: 'Get Started Free',
      ctaSecondary: 'See Features',
      badgeNoCard: 'No credit card required',
      badgeFreeForever: 'Free forever',
      previewTitle: 'Bissa.ai Dashboard',
      previewConnections: 'Connections',
      previewRevenue: 'Revenue',
      signIn: 'Sign In',
      featuresTitle: 'Built for Connection',
      featuresSubtitle: 'Powerful tools that honor the art of relationship-building, enhanced by AI intelligence',
      cardRelationship: 'Relationship Management',
      cardTrustSales: 'Trust-Based Sales',
      cardOutreach: 'Meaningful Outreach',
      cardInsights: 'Connection Insights',
      pricingTitle: 'Relationships That Scale With You',
      pricingSubtitle: 'Start building connections for free. Grow as your community grows. No per-user fees.',
      free: 'Free',
      growth: 'Growth',
      pro: 'Pro',
      business: 'Business',
      mostPopular: 'Most Popular',
      perMonth: '/month',
      btnGetStarted: 'Get Started',
      btnStartTrial: 'Start Free Trial',
      btnContactSales: 'Contact Sales',
      allPlansNote: 'All plans include 14-day free trial. No credit card required.',
      fallbackImage: 'Professional CRM Workspace',
      storyBadge: 'Our Story',
      storyTitle: 'Where Tradition Meets Innovation',
      storyP1: 'The Bissa people of Burkina Faso have thrived for centuries through strong networks, resilient communities, and trust-based relationships in trade and commerce.',
      storyP2: 'Today, Bissa.ai carries forward this legacy — empowering modern businesses to build lasting relationships through intelligent connection and AI-driven insights.',
      legacyTitle: 'The Bissa Legacy',
      legacyP1: 'Known for their adaptability and entrepreneurial spirit, the Bissa people built prosperous trading networks across West Africa through deep relationships, trust, and cultural exchange.',
      legacyQuote: 'Just as the Bissa thrived through strong networks and connections, Bissa.ai empowers businesses to thrive through smarter relationships.',
      legacyTagline: 'Heritage • Innovation • Connection',
      footerProduct: 'Product',
      footerFeatures: 'Features',
      footerPricing: 'Pricing',
      footerSignUp: 'Sign Up',
      footerSignIn: 'Sign In',
      footerLegal: 'Legal',
      footerTerms: 'Terms of Service',
      footerPrivacy: 'Privacy Policy',
      footerCookies: 'Cookie Policy',
      footerRefunds: 'Refund Policy',
      footerDpa: 'Data Processing',
      footerInspire: 'Inspired by the Bissa heritage of trust-based commerce and community building.',
      footerCopyright: '© 2024 Bissa.ai',
      footerMade: 'Made with ❤️ for small businesses',
      footerDescription: 'Build relationships like the Bissa people of Burkina Faso — through trust, connection, and AI-driven insights that strengthen every business relationship.',
      heroDescription: 'Just as the Bissa people of Burkina Faso built thriving communities through trust and connection, Bissa.ai empowers your business with AI-driven insights that strengthen every relationship.',
      cardRelationshipDesc: 'Build deeper connections with context-rich contact profiles, interaction history, and personal notes.',
      cardTrustSalesDesc: 'Track relationships through every stage with visual pipeline that honors the journey of trust-building.',
      cardOutreachDesc: 'Connect authentically with personalized campaigns that respect cultural diversity and individual preferences.',
      cardInsightsDesc: 'Understand relationship strength, engagement patterns, and community growth with AI-powered analytics.',
      legacyDescription: 'Known for their adaptability and entrepreneurial spirit, the Bissa people built prosperous trading networks across West Africa through deep relationships, trust, and cultural exchange.',
      bottomPrivacy: 'Privacy',
      bottomTerms: 'Terms',
      bottomRefunds: 'Refunds',
      pricingFeatures: {
        free: {
          contacts: '100 contacts',
          users: '1 user',
          crm: 'Basic CRM',
          tasks: '50 tasks/month',
          support: 'Community support'
        },
        growth: {
          contacts: '5,000 contacts',
          users: '3 users',
          tasks: '500 tasks/month',
          campaigns: '25 campaigns',
          integration: 'Email integration',
          support: 'Email support'
        },
        pro: {
          contacts: '10,000 contacts',
          users: '10 users',
          everything: 'Everything in Growth',
          api: 'API access',
          reports: 'Advanced reports',
          support: 'Priority support'
        },
        business: {
          contacts: 'Unlimited contacts',
          users: 'Unlimited users',
          everything: 'Everything in Pro',
          whiteLabel: 'White-label option',
          integrations: 'Custom integrations',
          manager: 'Dedicated manager'
        }
      }
    },
    dashboard: {
      title: 'Dashboard',
      welcome: "Welcome back! Here's what's happening with your business.",
      totalContacts: 'Total Contacts',
      companies: 'Companies',
      activeDeals: 'Active Deals',
      pendingTasks: 'Pending Tasks',
      totalRevenue: 'Total Revenue',
      dealValue: 'Deal Value',
      conversionRate: 'Conversion Rate',
      campaignsSent: 'Campaigns Sent',
      quickActions: 'Quick Actions',
      addContact: 'Add New Contact',
      createTask: 'Create Task',
      createDeal: 'Create Deal',
      newCampaign: 'New Campaign',
      recentActivity: 'Recent Activity',
      noRecent: 'No recent activities',
      due: 'Due:'
    },
    pages:{
      marketing:{
        title:'Marketing Hub',
        subtitle:'Create and manage email campaigns',
        createBtn:'Create Campaign',
        dialogTitle:'Create New Campaign',
        nameLabel:'Campaign Name *',
        subjectLabel:'Email Subject *',
        contentLabel:'Email Content *',
        statusLabel:'Status',
        draft:'Draft',scheduled:'Scheduled',sent:'Sent',
        cancel:'Cancel',
        noItems:'No campaigns found. Create your first campaign!',
        sentLabel:'Sent',openRate:'Open Rate',viewDetails:'View Details'
      },
      contacts:{ title:'Contacts', subtitle:'Manage your contacts and relationships', addBtn:'Add Contact', noItems:'No contacts found. Create your first contact!' },
      companies:{ title:'Companies', subtitle:'Manage your company accounts', addBtn:'Add Company', noItems:'No companies found. Create your first company!', searchPlaceholder:'Search companies by name, domain, or industry...' },
      deals:{ title:'Sales Pipeline', subtitle:'Track and manage your deals', addBtn:'Add Deal', noItems:'No deals', stages:{ lead:'Lead', qualified:'Qualified', proposal:'Proposal', negotiation:'Negotiation', closedWon:'Closed Won', closedLost:'Closed Lost' } },
      tasks:{ title:'Tasks & Reminders', subtitle:'Stay organized and never miss a deadline', newBtn:'New Task' },
      activities:{ title:'Activity Feed', subtitle:'Track all activities across your account', empty:'No activities yet. Start by creating contacts, deals, or campaigns!' },
      billing:{ title:'Billing & Usage', subtitle:'Manage your subscription and track usage', currentUsage:'Current Usage', subscription:'Subscription Details', features:'Features', availablePlans:'Available Plans', currentPlan:'Current Plan', upgrade:'Upgrade', downgrade:'Downgrade', approaching:'Approaching Usage Limits', approachingDesc:"You're approaching your plan limits. Consider upgrading to continue using all features without interruption." },
      calendar:{
        title:'Calendar',
        subtitle:'View and create Google Calendar events',
        refresh:'Refresh',
        createQuick:'Create Quick Event',
        titleLabel:'Title',
        startIso:'Start (ISO)',
        endIso:'End (ISO)',
        create:'Create',
        upcoming:'Upcoming Events',
        noEvents:'No events.',
        integrations:'Calendar Integrations',
        googleConnected:'Connected to Google Calendar',
        googleDisconnected:'Not connected to Google Calendar',
        connectGoogle:'Connect Google',
        disconnect:'Disconnect',
        outlookComingSoon:'Outlook integration coming soon',
        comingSoon:'Coming Soon'
      },
      importExport:{ title:'Import & Export', subtitle:'Bulk manage your data with CSV files', contacts:'Contacts', companies:'Companies', importContacts:'Import Contacts', importCompanies:'Import Companies', uploadCsv:'Upload CSV', importing:'Importing...', template:'Template', exportContacts:'Export Contacts', exportCompanies:'Export Companies', exportCsv:'Export to CSV', importComplete:'Import Complete!', importFailed:'Import Failed', exportComplete:'Export Complete!', exportFailed:'Export Failed', templateDownloaded:'Template Downloaded', uploadHelp:'Upload a CSV file to import multiple contacts at once', uploadCompaniesHelp:'Upload a CSV file to import multiple companies at once' },
      reports:{ title:'Reports & Analytics', subtitle:'Track performance and make data-driven decisions', overview:'Overview', sales:'Sales Pipeline', contacts:'Contacts', email:'Email Performance', export:'Export Data', revenueByMonth:'Revenue by Month', dealsByStage:'Deals by Stage', pipelineValue:'Pipeline Value by Stage', activity30:'Activity (Last 30 Days)', contactsByStatus:'Contacts by Status', recentContacts:'Recent Contacts', exportReports:'Export Reports', dealsReport:'Deals Report', contactsReport:'Contacts Report' },
      aiInsights:{ title:'AI Insights', subtitle:'AI-powered analysis of your business relationships and opportunities', getInsights:'Get AI Insights', startAnalysis:'Start AI Analysis', welcome:'Welcome to AI Insights, {name}!', description:'Get AI-powered analysis of your {count} contacts and business opportunities. Click "Get AI Insights" above to start your analysis.', authRequired:'Authentication Required', authDescription:'Please sign in to access AI-powered analysis.', goToLogin:'Go to Login', businessInsights:'AI Business Insights', contactsEngagement:'Your CRM shows {count} contacts with strong engagement potential.', keyInsights:'Key Insights:', highPriorityContacts:'You have {count} high-priority contacts that need immediate follow-up', activeSectors:'Most active contacts are in technology and consulting sectors', responseRate:'Average response rate is {rate}% - above industry standard', aiRecommendations:'AI Recommendations:', focusContacts:'Focus on the {count} contacts with highest engagement scores', scheduleFollowups:'Schedule follow-ups with prospects who opened your last emails', leadScoring:'Lead Scoring Results - {count} contacts analyzed', hot:'HOT', warm:'WARM', cold:'COLD', recentEngagement:'Recent engagement, High value', multipleInteractions:'Multiple interactions, Decision maker', showedInterest:'Showed interest, Budget confirmed' },
      automations:{ title:'Automations', subtitle:'Create simple rules to automate tasks when key events happen.', createTitle:'Create Rule', name:'Name', trigger:'Trigger', triggerContact:'Contact: Status Changed', triggerDeal:'Deal: Stage Changed', enabled:'Enabled', newStatus:'New Status', newStage:'New Stage', action:'Action', createTask:'Create Task', sendEmail:'Send Email', dueDays:'Due Days', taskTitle:'Task Title', priority:'Priority', low:'Low', medium:'Medium', high:'High', emailSubject:'Email Subject', templateId:'Template ID (optional)', bodyHtml:'Body (HTML, optional)', createBtn:'Create Rule', yourRules:'Your Rules', loading:'Loading…', noRules:'No rules yet.', enabledBadge:'ENABLED', disabledBadge:'DISABLED', disable:'Disable', enable:'Enable', delete:'Delete', deleteConfirm:'Delete this rule?', emptyTitle:'Automate Your Workflow in Minutes', emptySubtitle:'Choose a template below or create a custom automation', useTemplate:'Use Template', showLess:'Show Less', viewAllTemplates:'View All Templates', createCustom:'Create Custom Rule', templates:'Automation Templates', customizeTemplate:'Customize Template: {name}', wizardDescription:'Follow the steps to create your automation', conditionsOptional:'Conditions are optional. Leave empty to trigger on all events.', conditionsComingSoon:'Advanced conditions coming soon. For now, automation will trigger on all matching events.', noActions:'No actions yet. Click "Add Action" to get started.', createRule:'Create Rule' },
      help:{ title:'Help & Support', searchPlaceholder:'Search help articles...', quickStart:'Quick Start Guide', contactSupport:'Contact Support', learnBasics:'Learn the basics in 5 minutes', getHelp:'Get help from our team', commonQuestions:'Common Questions', howToImport:'How do I import my contacts?', howToCreateDeal:'How do I create a deal?', canIUpgrade:'Can I upgrade my plan?', importAnswer:'Go to Import/Export page and upload your CSV file. We support standard formats from most CRMs.', dealAnswer:'Use the Quick Add button or go to the Deals page and click "Add Deal".', upgradeAnswer:'Yes! Go to Settings to view and change your subscription plan anytime.' },
      support:{ title:'Contact Support', name:'Name', email:'Email', subject:'Subject', category:'Category', message:'Message', priority:'Priority', general:'General Question', technical:'Technical Issue', billing:'Billing & Account', feature:'Feature Request', bug:'Bug Report', low:'Low - General inquiry', medium:'Medium - Standard support', high:'High - Urgent issue', send:'Send Message', sending:'Sending...', success:'Message sent successfully!', error:'Failed to send message. Please try again.' },
      tutorial:{ title:'Welcome to Bissa!', subtitle:"Let's build meaningful relationships together", ready:'Ready to build connections like the Bissa?', description:'This tutorial will show you how Bissa.ai empowers your business with relationship-building tools that honor tradition while embracing AI innovation. Learn to strengthen every connection.', timeRequired:'Time required: 5 minutes', letsStart:'Let\'s Start!', next:'Next', previous:'Previous', finish:'Finish', close:'Close' }
    },
    onboarding:{
      welcomeTitle:'Welcome to Bissa.ai!',
      welcomeSubtitle:"Let's get you set up for success",
      roleTitle:"What's your role?",
      roleSubtitle:'Help us personalize your experience',
      companyTitle:'Tell us about your company',
      companySubtitle:'This helps us customize features for you',
      goalsTitle:"What's your primary goal?",
      goalsSubtitle:'We\'ll focus on features that matter most',
      setupTitle:"Let's create your first records",
      setupSubtitle:'Get started with some sample data',
      completeTitle:"You're all set!",
      completeSubtitle:'Welcome to the Bissa.ai community',
      step:'Step', of:'of', back:'Back', next:'Next', getStarted:'Get Started',
      welcomeMessage:'Welcome!',
      welcomeDescription:'Just as the Bissa people built thriving communities through strong relationships, we\'ll help you build lasting business connections with AI-powered insights.',
      timeEstimate:'✨ This will take about 2 minutes'
    }
  },
  es: {
    nav: {
      dashboard: 'Panel',
      aiInsights: 'Ideas IA',
      contacts: 'Contactos',
      companies: 'Compañías',
      deals: 'Oportunidades',
      marketing: 'Marketing',
      tasks: 'Tareas',
      reports: 'Informes',
      activities: 'Actividades',
      settings: 'Configuración',
      billing: 'Facturación',
      automations: 'Automatizaciones',
      importExport: 'Importar/Exportar',
      calendar: 'Calendario',
    },
    header: {
      searchPlaceholder: 'Buscar contactos, compañías, oportunidades...'
    },
    auth: {
      authenticating: 'Autenticando...'
    },
    landing: {
      heroBadge: 'Donde la herencia se une a la IA',
      heroTitlePrefix: 'Construye Relaciones',
      heroTitleHighlight: ' Como los Bissa',
      ctaPrimary: 'Comienza Gratis',
      ctaSecondary: 'Ver funciones',
      badgeNoCard: 'No se requiere tarjeta',
      badgeFreeForever: 'Gratis para siempre',
      previewTitle: 'Panel de Bissa.ai',
      previewConnections: 'Conexiones',
      previewRevenue: 'Ingresos',
      signIn: 'Iniciar sesión',
      featuresTitle: 'Hecho para la conexión',
      featuresSubtitle: 'Potentes herramientas que honran el arte de construir relaciones, mejoradas con IA',
      cardRelationship: 'Gestión de relaciones',
      cardTrustSales: 'Ventas basadas en la confianza',
      cardOutreach: 'Alcance significativo',
      cardInsights: 'Ideas de conexión',
      pricingTitle: 'Relaciones que crecen contigo',
      pricingSubtitle: 'Comienza gratis. Crece a medida que crece tu comunidad. Sin tarifas por usuario.',
      free: 'Gratis',
      growth: 'Crecimiento',
      pro: 'Pro',
      business: 'Negocios',
      mostPopular: 'Más popular',
      perMonth: '/mes',
      btnGetStarted: 'Comenzar',
      btnStartTrial: 'Empezar prueba gratis',
      btnContactSales: 'Contactar ventas',
      allPlansNote: 'Todos los planes incluyen 14 días de prueba. No se requiere tarjeta.',
      fallbackImage: 'Espacio de trabajo CRM profesional',
      storyBadge: 'Nuestra historia',
      storyTitle: 'Donde la tradición se encuentra con la innovación',
      storyP1: 'El pueblo Bissa de Burkina Faso ha prosperado durante siglos gracias a redes sólidas, comunidades resilientes y relaciones comerciales basadas en la confianza.',
      storyP2: 'Hoy, Bissa.ai continúa este legado: ayudar a las empresas modernas a construir relaciones duraderas mediante conexiones inteligentes y conocimientos impulsados por IA.',
      legacyTitle: 'El legado Bissa',
      legacyP1: 'Conocidos por su adaptabilidad y espíritu empresarial, el pueblo Bissa construyó prósperas redes comerciales en toda África Occidental a través de relaciones profundas, confianza e intercambio cultural.',
      legacyQuote: 'Así como los Bissa prosperaron mediante redes y conexiones sólidas, Bissa.ai ayuda a las empresas a prosperar mediante relaciones más inteligentes.',
      legacyTagline: 'Herencia • Innovación • Conexión',
      footerProduct: 'Producto',
      footerFeatures: 'Funciones',
      footerPricing: 'Precios',
      footerSignUp: 'Registrarse',
      footerSignIn: 'Iniciar sesión',
      footerLegal: 'Legal',
      footerTerms: 'Términos del servicio',
      footerPrivacy: 'Política de privacidad',
      footerCookies: 'Política de cookies',
      footerRefunds: 'Política de reembolsos',
      footerDpa: 'Tratamiento de datos',
      footerInspire: 'Inspirado por la herencia Bissa del comercio basado en la confianza y la construcción comunitaria.',
      footerCopyright: '© 2024 Bissa.ai',
      footerMade: 'Hecho con ❤️ para pequeñas empresas',
      footerDescription: 'Construye relaciones como el pueblo Bissa de Burkina Faso — a través de la confianza, conexión e insights impulsados por IA que fortalecen cada relación comercial.',
      heroDescription: 'Así como el pueblo Bissa de Burkina Faso construyó comunidades prósperas a través de la confianza y conexión, Bissa.ai empodera tu negocio con insights impulsados por IA que fortalecen cada relación.',
      cardRelationshipDesc: 'Construye conexiones más profundas con perfiles de contacto ricos en contexto, historial de interacciones y notas personales.',
      cardTrustSalesDesc: 'Rastrea relaciones a través de cada etapa con pipeline visual que honra el viaje de construcción de confianza.',
      cardOutreachDesc: 'Conecta auténticamente con campañas personalizadas que respetan la diversidad cultural y preferencias individuales.',
      cardInsightsDesc: 'Comprende la fortaleza de las relaciones, patrones de engagement y crecimiento comunitario con análisis impulsados por IA.',
      legacyDescription: 'Conocidos por su adaptabilidad y espíritu emprendedor, el pueblo Bissa construyó redes comerciales prósperas en África Occidental a través de relaciones profundas, confianza e intercambio cultural.',
      bottomPrivacy: 'Privacidad',
      bottomTerms: 'Términos',
      bottomRefunds: 'Reembolsos',
      pricingFeatures: {
        free: {
          contacts: '100 contactos',
          users: '1 usuario',
          crm: 'CRM básico',
          tasks: '50 tareas/mes',
          support: 'Soporte comunitario'
        },
        growth: {
          contacts: '5,000 contactos',
          users: '3 usuarios',
          tasks: '500 tareas/mes',
          campaigns: '25 campañas',
          integration: 'Integración de email',
          support: 'Soporte por email'
        },
        pro: {
          contacts: '10,000 contactos',
          users: '10 usuarios',
          everything: 'Todo en Growth',
          api: 'Acceso API',
          reports: 'Reportes avanzados',
          support: 'Soporte prioritario'
        },
        business: {
          contacts: 'Contactos ilimitados',
          users: 'Usuarios ilimitados',
          everything: 'Todo en Pro',
          whiteLabel: 'Opción white-label',
          integrations: 'Integraciones personalizadas',
          manager: 'Gerente dedicado'
        }
      }
    },
    dashboard: {
      title: 'Panel',
      welcome: '¡Bienvenido! Esto es lo que sucede con tu negocio.',
      totalContacts: 'Contactos Totales',
      companies: 'Compañías',
      activeDeals: 'Oportunidades Activas',
      pendingTasks: 'Tareas Pendientes',
      totalRevenue: 'Ingresos Totales',
      dealValue: 'Valor de Oportunidades',
      conversionRate: 'Tasa de Conversión',
      campaignsSent: 'Campañas Enviadas',
      quickActions: 'Acciones Rápidas',
      addContact: 'Agregar Nuevo Contacto',
      createTask: 'Crear Tarea',
      createDeal: 'Crear Oportunidad',
      newCampaign: 'Nueva Campaña',
      recentActivity: 'Actividad Reciente',
      noRecent: 'Sin actividades recientes',
      due: 'Vence:'
    },
    pages:{
      marketing:{
        title:'Centro de Marketing',
        subtitle:'Crea y gestiona campañas de correo',
        createBtn:'Crear campaña',
        dialogTitle:'Crear nueva campaña',
        nameLabel:'Nombre de la campaña *',
        subjectLabel:'Asunto del correo *',
        contentLabel:'Contenido del correo *',
        statusLabel:'Estado',
        draft:'Borrador',scheduled:'Programada',sent:'Enviada',
        cancel:'Cancelar',
        noItems:'No hay campañas. ¡Crea tu primera campaña!',
        sentLabel:'Enviados',openRate:'Tasa de apertura',viewDetails:'Ver detalles'
      },
      contacts:{ title:'Contactos', subtitle:'Gestiona tus contactos y relaciones', addBtn:'Añadir contacto', noItems:'No hay contactos. ¡Crea tu primer contacto!' },
      companies:{ title:'Compañías', subtitle:'Gestiona tus cuentas de empresa', addBtn:'Añadir compañía', noItems:'No hay compañías. ¡Crea tu primera compañía!', searchPlaceholder:'Buscar compañías por nombre, dominio o industria...' },
      deals:{ title:'Embudo de ventas', subtitle:'Sigue y gestiona tus oportunidades', addBtn:'Añadir oportunidad', noItems:'Sin oportunidades', stages:{ lead:'Prospecto', qualified:'Calificado', proposal:'Propuesta', negotiation:'Negociación', closedWon:'Cerrado Ganado', closedLost:'Cerrado Perdido' } },
      tasks:{ title:'Tareas y Recordatorios', subtitle:'Mantente organizado y no pierdas plazos', newBtn:'Nueva tarea' },
      activities:{ title:'Actividad', subtitle:'Sigue todas las actividades de tu cuenta', empty:'Aún no hay actividades. ¡Crea contactos, oportunidades o campañas!' },
      billing:{ title:'Facturación y uso', subtitle:'Gestiona tu suscripción y uso', currentUsage:'Uso actual', subscription:'Detalles de suscripción', features:'Funciones', availablePlans:'Planes disponibles', currentPlan:'Plan actual', upgrade:'Mejorar', downgrade:'Reducir', approaching:'Límites cercanos', approachingDesc:'Estás cerca de tus límites. Considera mejorar tu plan para continuar sin interrupciones.' },
      calendar:{
        title:'Calendar',
        subtitle:'View and create Google Calendar events',
        refresh:'Refresh',
        createQuick:'Create Quick Event',
        titleLabel:'Title',
        startIso:'Start (ISO)',
        endIso:'End (ISO)',
        create:'Create',
        upcoming:'Upcoming Events',
        noEvents:'No events.',
        integrations:'Calendar Integrations',
        googleConnected:'Connected to Google Calendar',
        googleDisconnected:'Not connected to Google Calendar',
        connectGoogle:'Connect Google',
        disconnect:'Disconnect',
        outlookComingSoon:'Outlook integration coming soon',
        comingSoon:'Coming Soon'
      },
      importExport:{ title:'Importar y Exportar', subtitle:'Gestiona tus datos por lotes con CSV', contacts:'Contactos', companies:'Compañías', importContacts:'Importar contactos', importCompanies:'Importar compañías', uploadCsv:'Subir CSV', importing:'Importando...', template:'Plantilla', exportContacts:'Exportar contactos', exportCompanies:'Exportar compañías', exportCsv:'Exportar a CSV', importComplete:'¡Importación completa!', importFailed:'Importación fallida', exportComplete:'¡Exportación completa!', exportFailed:'Exportación fallida', templateDownloaded:'Plantilla descargada', uploadHelp:'Sube un archivo CSV para importar múltiples contactos de una vez', uploadCompaniesHelp:'Sube un archivo CSV para importar múltiples compañías de una vez' },
      reports:{ title:'Informes y Analítica', subtitle:'Sigue el desempeño y decide con datos', overview:'Resumen', sales:'Embudo de ventas', contacts:'Contactos', email:'Rendimiento de correo', export:'Exportar datos', revenueByMonth:'Ingresos por mes', dealsByStage:'Oportunidades por etapa', pipelineValue:'Valor por etapa', activity30:'Actividad (últimos 30 días)', contactsByStatus:'Contactos por estado', recentContacts:'Contactos recientes', exportReports:'Exportar informes', dealsReport:'Informe de oportunidades', contactsReport:'Informe de contactos' },
      aiInsights:{ title:'Ideas IA', subtitle:'Análisis impulsado por IA de tus relaciones comerciales y oportunidades', getInsights:'Obtener Ideas IA', startAnalysis:'Iniciar Análisis IA', welcome:'¡Bienvenido a Ideas IA, {name}!', description:'Obtén análisis impulsado por IA de tus {count} contactos y oportunidades comerciales. Haz clic en "Obtener Ideas IA" arriba para comenzar tu análisis.', authRequired:'Autenticación Requerida', authDescription:'Por favor inicia sesión para acceder al análisis impulsado por IA.', goToLogin:'Ir al Login', businessInsights:'Ideas Comerciales IA', leadScoring:'Puntuación de Leads', contactHealth:'Salud de Contactos', contactsEngagement:'Tu CRM muestra {count} contactos con fuerte potencial de participación.', keyInsights:'Ideas Clave:', highPriorityContacts:'Tienes {count} contactos de alta prioridad que necesitan seguimiento inmediato', activeSectors:'Los contactos más activos están en los sectores de tecnología y consultoría', responseRate:'La tasa de respuesta promedio es {rate}% - por encima del estándar de la industria', aiRecommendations:'Recomendaciones IA:', focusContacts:'Enfócate en los {count} contactos con puntuaciones de participación más altas', scheduleFollowups:'Programa seguimientos con prospectos que abrieron tus últimos emails', hot:'CALIENTE', warm:'TIBIO', cold:'FRÍO', recentEngagement:'Participación reciente, Alto valor', multipleInteractions:'Múltiples interacciones, Tomador de decisiones', showedInterest:'Mostró interés, Presupuesto confirmado' },
      automations:{ title:'Automatizaciones', subtitle:'Crea reglas simples para automatizar tareas cuando ocurran eventos clave.', createTitle:'Crear Regla', name:'Nombre', trigger:'Disparador', triggerContact:'Contacto: Estado Cambiado', triggerDeal:'Oportunidad: Etapa Cambiada', enabled:'Habilitado', newStatus:'Nuevo Estado', newStage:'Nueva Etapa', action:'Acción', createTask:'Crear Tarea', sendEmail:'Enviar Email', dueDays:'Días de Vencimiento', taskTitle:'Título de Tarea', priority:'Prioridad', low:'Baja', medium:'Media', high:'Alta', emailSubject:'Asunto del Email', templateId:'ID de Plantilla (opcional)', bodyHtml:'Cuerpo (HTML, opcional)', createBtn:'Crear Regla', yourRules:'Tus Reglas', loading:'Cargando…', noRules:'Aún no hay reglas.', enabledBadge:'HABILITADO', disabledBadge:'DESHABILITADO', disable:'Deshabilitar', enable:'Habilitar', delete:'Eliminar', deleteConfirm:'¿Eliminar esta regla?' },
      help:{ title:'Ayuda y Soporte', searchPlaceholder:'Buscar artículos de ayuda...', quickStart:'Guía de Inicio Rápido', contactSupport:'Contactar Soporte', learnBasics:'Aprende lo básico en 5 minutos', getHelp:'Obtén ayuda de nuestro equipo', commonQuestions:'Preguntas Frecuentes', howToImport:'¿Cómo importo mis contactos?', howToCreateDeal:'¿Cómo creo una oportunidad?', canIUpgrade:'¿Puedo mejorar mi plan?', importAnswer:'Ve a la página Importar/Exportar y sube tu archivo CSV. Soportamos formatos estándar de la mayoría de CRMs.', dealAnswer:'Usa el botón Agregar Rápido o ve a la página Oportunidades y haz clic en "Agregar Oportunidad".', upgradeAnswer:'¡Sí! Ve a Configuración para ver y cambiar tu plan de suscripción en cualquier momento.' },
      support:{ title:'Contactar Soporte', name:'Nombre', email:'Email', subject:'Asunto', category:'Categoría', message:'Mensaje', priority:'Prioridad', general:'Pregunta General', technical:'Problema Técnico', billing:'Facturación y Cuenta', feature:'Solicitud de Función', bug:'Reporte de Error', low:'Baja - Consulta general', medium:'Media - Soporte estándar', high:'Alta - Problema urgente', send:'Enviar Mensaje', sending:'Enviando...', success:'¡Mensaje enviado exitosamente!', error:'Error al enviar mensaje. Por favor intenta de nuevo.' },
      tutorial:{ title:'¡Bienvenido a Bissa!', subtitle:'Construyamos relaciones significativas juntos', ready:'¿Listo para construir conexiones como los Bissa?', description:'Este tutorial te mostrará cómo Bissa.ai empodera tu negocio con herramientas de construcción de relaciones que honran la tradición mientras abrazan la innovación de IA. Aprende a fortalecer cada conexión.', timeRequired:'Tiempo requerido: 5 minutos', letsStart:'¡Empecemos!', next:'Siguiente', previous:'Anterior', finish:'Finalizar', close:'Cerrar' }
    },
    onboarding:{
      welcomeTitle:'¡Bienvenido a Bissa.ai!',
      welcomeSubtitle:'Vamos a configurarte para el éxito',
      roleTitle:'¿Cuál es tu rol?',
      roleSubtitle:'Ayúdanos a personalizar tu experiencia',
      companyTitle:'Cuéntanos sobre tu empresa',
      companySubtitle:'Esto nos ayuda a personalizar funciones para ti',
      goalsTitle:'¿Cuál es tu objetivo principal?',
      goalsSubtitle:'Nos centraremos en lo que más importa',
      setupTitle:'Creemos tus primeros registros',
      setupSubtitle:'Comienza con algunos datos de ejemplo',
      completeTitle:'¡Todo listo!',
      completeSubtitle:'Bienvenido a la comunidad Bissa.ai',
      step:'Paso', of:'de', back:'Atrás', next:'Siguiente', getStarted:'Empezar',
      welcomeMessage:'¡Bienvenido!',
      welcomeDescription:'Así como el pueblo Bissa construyó comunidades prósperas a través de relaciones sólidas, te ayudaremos a construir conexiones comerciales duraderas con insights impulsados por IA.',
      timeEstimate:'✨ Esto tomará unos 2 minutos'
    }
  },
  fr: {
    nav: {
      dashboard: 'Tableau de bord',
      aiInsights: 'Idées IA',
      contacts: 'Contacts',
      companies: 'Sociétés',
      deals: 'Affaires',
      marketing: 'Marketing',
      tasks: 'Tâches',
      reports: 'Rapports',
      activities: 'Activités',
      settings: 'Paramètres',
      billing: 'Facturation',
      automations: 'Automatisations',
      importExport: 'Importer/Exporter',
      calendar: 'Calendrier',
    },
    header: {
      searchPlaceholder: 'Rechercher contacts, sociétés, affaires...'
    },
    auth: {
      authenticating: 'Authentification...'
    },
    landing: {
      heroBadge: "Là où l'héritage rencontre l'IA",
      heroTitlePrefix: 'Tisser des relations',
      heroTitleHighlight: ' Comme les Bissa',
      ctaPrimary: 'Commencer gratuitement',
      ctaSecondary: 'Voir les fonctionnalités',
      badgeNoCard: 'Aucune carte requise',
      badgeFreeForever: 'Gratuit pour toujours',
      previewTitle: 'Tableau de bord Bissa.ai',
      previewConnections: 'Connexions',
      previewRevenue: 'Revenus',
      signIn: 'Se connecter',
      featuresTitle: 'Conçu pour la connexion',
      featuresSubtitle: 'Des outils puissants qui respectent l\'art de la relation, améliorés par l\'IA',
      cardRelationship: 'Gestion de la relation',
      cardTrustSales: 'Ventes fondées sur la confiance',
      cardOutreach: 'Approche pertinente',
      cardInsights: 'Analyses de connexion',
      pricingTitle: 'Des relations qui évoluent avec vous',
      pricingSubtitle: 'Commencez gratuitement. Grandissez avec votre communauté. Sans frais par utilisateur.',
      free: 'Gratuit',
      growth: 'Croissance',
      pro: 'Pro',
      business: 'Entreprise',
      mostPopular: 'Le plus populaire',
      perMonth: '/mois',
      btnGetStarted: 'Commencer',
      btnStartTrial: 'Démarrer l\'essai gratuit',
      btnContactSales: 'Contacter les ventes',
      allPlansNote: 'Tous les plans incluent 14 jours d\'essai. Aucune carte requise.',
      fallbackImage: 'Espace de travail CRM professionnel',
      storyBadge: 'Notre histoire',
      storyTitle: 'Là où la tradition rencontre l\'innovation',
      storyP1: 'Le peuple Bissa du Burkina Faso a prospéré grâce à des réseaux solides, des communautés résilientes et des relations commerciales fondées sur la confiance.',
      storyP2: 'Aujourd\'hui, Bissa.ai perpétue cet héritage en aidant les entreprises à tisser des relations durables grâce à des connexions intelligentes et des insights IA.',
      legacyTitle: 'L\'héritage Bissa',
      legacyP1: 'Connus pour leur adaptabilité et leur esprit entrepreneurial, le peuple Bissa a construit de prospères réseaux commerciaux à travers l\'Afrique de l\'Ouest grâce à des relations profondes, la confiance et les échanges culturels.',
      legacyQuote: 'Tout comme les Bissa ont prospéré grâce à de solides réseaux, Bissa.ai aide les entreprises à prospérer grâce à des relations plus intelligentes.',
      legacyTagline: 'Héritage • Innovation • Connexion',
      footerProduct: 'Produit',
      footerFeatures: 'Fonctionnalités',
      footerPricing: 'Tarifs',
      footerSignUp: 'Inscription',
      footerSignIn: 'Connexion',
      footerLegal: 'Mentions légales',
      footerTerms: 'Conditions d\'utilisation',
      footerPrivacy: 'Politique de confidentialité',
      footerCookies: 'Politique des cookies',
      footerRefunds: 'Politique de remboursement',
      footerDpa: 'Traitement des données',
      footerInspire: 'Inspiré par l\'héritage Bissa du commerce fondé sur la confiance et du lien communautaire.',
      footerCopyright: '© 2024 Bissa.ai',
      footerMade: 'Fait avec ❤️ pour les petites entreprises',
      footerDescription: 'Construisez des relations comme le peuple Bissa du Burkina Faso — par la confiance, la connexion et les insights alimentés par l\'IA qui renforcent chaque relation commerciale.',
      heroDescription: 'Tout comme le peuple Bissa du Burkina Faso a construit des communautés prospères par la confiance et la connexion, Bissa.ai autonomise votre entreprise avec des insights alimentés par l\'IA qui renforcent chaque relation.',
      cardRelationshipDesc: 'Construisez des connexions plus profondes avec des profils de contact riches en contexte, l\'historique des interactions et les notes personnelles.',
      cardTrustSalesDesc: 'Suivez les relations à travers chaque étape avec un pipeline visuel qui honore le parcours de construction de confiance.',
      cardOutreachDesc: 'Connectez-vous authentiquement avec des campagnes personnalisées qui respectent la diversité culturelle et les préférences individuelles.',
      cardInsightsDesc: 'Comprenez la force des relations, les modèles d\'engagement et la croissance communautaire avec des analyses alimentées par l\'IA.',
      legacyDescription: 'Connus pour leur adaptabilité et leur esprit entrepreneurial, le peuple Bissa a construit des réseaux commerciaux prospères à travers l\'Afrique de l\'Ouest par des relations profondes, la confiance et l\'échange culturel.',
      bottomPrivacy: 'Confidentialité',
      bottomTerms: 'Conditions',
      bottomRefunds: 'Remboursements',
      pricingFeatures: {
        free: {
          contacts: '100 contacts',
          users: '1 utilisateur',
          crm: 'CRM de base',
          tasks: '50 tâches/mois',
          support: 'Support communautaire'
        },
        growth: {
          contacts: '5,000 contacts',
          users: '3 utilisateurs',
          tasks: '500 tâches/mois',
          campaigns: '25 campagnes',
          integration: 'Intégration email',
          support: 'Support email'
        },
        pro: {
          contacts: '10,000 contacts',
          users: '10 utilisateurs',
          everything: 'Tout dans Growth',
          api: 'Accès API',
          reports: 'Rapports avancés',
          support: 'Support prioritaire'
        },
        business: {
          contacts: 'Contacts illimités',
          users: 'Utilisateurs illimités',
          everything: 'Tout dans Pro',
          whiteLabel: 'Option white-label',
          integrations: 'Intégrations personnalisées',
          manager: 'Gestionnaire dédié'
        }
      }
    },
    dashboard: {
      title: 'Tableau de bord',
      welcome: 'Bon retour ! Voici l\'activité de votre entreprise.',
      totalContacts: 'Contacts Totaux',
      companies: 'Sociétés',
      activeDeals: 'Affaires Actives',
      pendingTasks: 'Tâches en Attente',
      totalRevenue: 'Revenu Total',
      dealValue: 'Valeur des Affaires',
      conversionRate: 'Taux de Conversion',
      campaignsSent: 'Campagnes Envoyées',
      quickActions: 'Actions Rapides',
      addContact: 'Ajouter un Contact',
      createTask: 'Créer une Tâche',
      createDeal: 'Créer une Affaire',
      newCampaign: 'Nouvelle Campagne',
      recentActivity: 'Activité Récente',
      noRecent: 'Aucune activité récente',
      due: 'Échéance :'
    },
    pages:{
      marketing:{
        title:'Hub Marketing',
        subtitle:'Créez et gérez des campagnes email',
        createBtn:'Créer une campagne',
        dialogTitle:'Créer une nouvelle campagne',
        nameLabel:'Nom de la campagne *',
        subjectLabel:"Objet de l'email *",
        contentLabel:"Contenu de l'email *",
        statusLabel:'Statut',
        draft:'Brouillon',scheduled:'Planifiée',sent:'Envoyée',
        cancel:'Annuler',
        noItems:"Aucune campagne. Créez votre première campagne !",
        sentLabel:'Envoyés',openRate:"Taux d'ouverture",viewDetails:'Voir les détails'
      },
      contacts:{ title:'Contacts', subtitle:'Gérez vos contacts et relations', addBtn:'Ajouter un contact', noItems:'Aucun contact. Créez votre premier contact !' },
      companies:{ title:'Sociétés', subtitle:'Gérez vos comptes entreprise', addBtn:'Ajouter une société', noItems:'Aucune société. Créez votre première société !', searchPlaceholder:'Rechercher des sociétés par nom, domaine ou secteur...' },
      deals:{ title:'Pipeline des ventes', subtitle:'Suivez et gérez vos affaires', addBtn:'Ajouter une affaire', noItems:"Aucune affaire", stages:{ lead:'Prospect', qualified:'Qualifié', proposal:'Proposition', negotiation:'Négociation', closedWon:'Fermé Gagné', closedLost:'Fermé Perdu' } },
      tasks:{ title:'Tâches et rappels', subtitle:'Restez organisé et ne manquez aucun délai', newBtn:'Nouvelle tâche' },
      activities:{ title:"Flux d\'activité", subtitle:'Suivez toutes les activités de votre compte', empty:"Aucune activité pour l\'instant. Créez des contacts, affaires ou campagnes !" },
      billing:{ title:'Facturation et utilisation', subtitle:'Gérez votre abonnement et votre usage', currentUsage:'Utilisation actuelle', subscription:"Détails de l\'abonnement", features:'Fonctionnalités', availablePlans:'Offres disponibles', currentPlan:'Offre actuelle', upgrade:'Mettre à niveau', downgrade:'Rétrograder', approaching:'Limites proches', approachingDesc:"Vous approchez de vos limites. Pensez à mettre à niveau pour continuer sans interruption." },
      calendar:{ title:'Calendrier', subtitle:'Voir et créer des événements Google Calendar', refresh:'Rafraîchir', createQuick:"Créer un événement rapide", titleLabel:'Titre', startIso:'Début (ISO)', endIso:'Fin (ISO)', create:'Créer', upcoming:'Événements à venir', noEvents:'Aucun événement.' },
      importExport:{ title:'Importer & Exporter', subtitle:'Gérez vos données par lots via CSV', contacts:'Contacts', companies:'Sociétés', importContacts:'Importer des contacts', importCompanies:'Importer des sociétés', uploadCsv:'Téléverser CSV', importing:'Importation…', template:'Modèle', exportContacts:'Exporter les contacts', exportCompanies:'Exporter les sociétés', exportCsv:'Exporter en CSV', importComplete:"Importation terminée !", importFailed:"Échec de l\'import", exportComplete:'Exportation terminée !', exportFailed:"Échec de l\'export", templateDownloaded:'Modèle téléchargé', uploadHelp:'Téléversez un fichier CSV pour importer plusieurs contacts à la fois', uploadCompaniesHelp:'Téléversez un fichier CSV pour importer plusieurs sociétés à la fois' },
      reports:{ title:'Rapports et analyses', subtitle:'Suivez les performances et décidez avec des données', overview:'Aperçu', sales:'Pipeline des ventes', contacts:'Contacts', email:'Performance email', export:'Exporter les données', revenueByMonth:'Revenus par mois', dealsByStage:'Affaires par étape', pipelineValue:'Valeur par étape', activity30:'Activité (30 derniers jours)', contactsByStatus:'Contacts par statut', recentContacts:'Contacts récents', exportReports:'Exporter des rapports', dealsReport:"Rapport d\'affaires", contactsReport:'Rapport des contacts' },
      aiInsights:{ title:'Idées IA', subtitle:'Analyse alimentée par IA de vos relations commerciales et opportunités', getInsights:'Obtenir des Idées IA', startAnalysis:'Démarrer l\'Analyse IA', welcome:'Bienvenue dans les Idées IA, {name} !', description:'Obtenez une analyse alimentée par IA de vos {count} contacts et opportunités commerciales. Cliquez sur "Obtenir des Idées IA" ci-dessus pour commencer votre analyse.', authRequired:'Authentification Requise', authDescription:'Veuillez vous connecter pour accéder à l\'analyse alimentée par IA.', goToLogin:'Aller à la Connexion', businessInsights:'Idées Commerciales IA', contactsEngagement:'Votre CRM montre {count} contacts avec un fort potentiel d\'engagement.', keyInsights:'Idées Clés :', highPriorityContacts:'Vous avez {count} contacts haute priorité qui nécessitent un suivi immédiat', activeSectors:'Les contacts les plus actifs sont dans les secteurs technologique et conseil', responseRate:'Le taux de réponse moyen est de {rate}% - au-dessus de la norme de l\'industrie', aiRecommendations:'Recommandations IA :', focusContacts:'Concentrez-vous sur les {count} contacts avec les scores d\'engagement les plus élevés', scheduleFollowups:'Planifiez des suivis avec les prospects qui ont ouvert vos derniers emails', leadScoring:'Résultats de Notation des Prospects - {count} contacts analysés', hot:'CHAUD', warm:'TIÈDE', cold:'FROID', recentEngagement:'Engagement récent, Haute valeur', multipleInteractions:'Interactions multiples, Décideur', showedInterest:'A montré de l\'intérêt, Budget confirmé' },
      automations:{ title:'Automatisations', subtitle:'Créez des règles simples pour automatiser les tâches lors d\'événements clés.', createTitle:'Créer une règle', name:'Nom', trigger:'Déclencheur', triggerContact:'Contact : Statut modifié', triggerDeal:'Affaire : Étape modifiée', enabled:'Activé', newStatus:'Nouveau statut', newStage:'Nouvelle étape', action:'Action', createTask:'Créer une tâche', sendEmail:'Envoyer un email', dueDays:'Jours d\'échéance', taskTitle:'Titre de la tâche', priority:'Priorité', low:'Basse', medium:'Moyenne', high:'Haute', emailSubject:'Objet de l\'email', templateId:'ID du modèle (optionnel)', bodyHtml:'Corps (HTML, optionnel)', createBtn:'Créer une règle', yourRules:'Vos règles', loading:'Chargement…', noRules:'Aucune règle pour le moment.', enabledBadge:'ACTIVÉ', disabledBadge:'DÉSACTIVÉ', disable:'Désactiver', enable:'Activer', delete:'Supprimer', deleteConfirm:'Supprimer cette règle ?' },
      help:{ title:'Aide et Support', searchPlaceholder:'Rechercher des articles d\'aide...', quickStart:'Guide de Démarrage Rapide', contactSupport:'Contacter le Support', learnBasics:'Apprenez les bases en 5 minutes', getHelp:'Obtenez de l\'aide de notre équipe', commonQuestions:'Questions Fréquentes', howToImport:'Comment importer mes contacts?', howToCreateDeal:'Comment créer une affaire?', canIUpgrade:'Puis-je améliorer mon plan?', importAnswer:'Allez à la page Importer/Exporter et téléchargez votre fichier CSV. Nous supportons les formats standard de la plupart des CRMs.', dealAnswer:'Utilisez le bouton Ajouter Rapide ou allez à la page Affaires et cliquez sur "Ajouter une Affaire".', upgradeAnswer:'Oui! Allez aux Paramètres pour voir et changer votre plan d\'abonnement à tout moment.' },
      support:{ title:'Contacter le Support', name:'Nom', email:'Email', subject:'Sujet', category:'Catégorie', message:'Message', priority:'Priorité', general:'Question Générale', technical:'Problème Technique', billing:'Facturation et Compte', feature:'Demande de Fonctionnalité', bug:'Rapport de Bug', low:'Basse - Demande générale', medium:'Moyenne - Support standard', high:'Haute - Problème urgent', send:'Envoyer le Message', sending:'Envoi en cours...', success:'Message envoyé avec succès!', error:'Échec de l\'envoi du message. Veuillez réessayer.' },
      tutorial:{ title:'Bienvenue chez Bissa!', subtitle:'Construisons des relations significatives ensemble', ready:'Prêt à construire des connexions comme les Bissa?', description:'Ce tutoriel vous montrera comment Bissa.ai autonomise votre entreprise avec des outils de construction de relations qui honorent la tradition tout en embrassant l\'innovation IA. Apprenez à renforcer chaque connexion.', timeRequired:'Temps requis: 5 minutes', letsStart:'Commençons!', next:'Suivant', previous:'Précédent', finish:'Terminer', close:'Fermer' }
    },
    onboarding:{
      welcomeTitle:'Bienvenue sur Bissa.ai !',
      welcomeSubtitle:"Configurons votre réussite",
      roleTitle:'Quel est votre rôle ?',
      roleSubtitle:'Aidons-nous à personnaliser votre expérience',
      companyTitle:"Parlez-nous de votre entreprise",
      companySubtitle:'Cela nous aide à adapter les fonctionnalités',
      goalsTitle:'Quel est votre objectif principal ?',
      goalsSubtitle:'Nous nous concentrerons sur l\'essentiel',
      setupTitle:'Créons vos premiers enregistrements',
      setupSubtitle:'Commencez avec des données exemples',
      completeTitle:"C'est tout bon !",
      completeSubtitle:'Bienvenue dans la communauté Bissa.ai',
      step:'Étape', of:'sur', back:'Retour', next:'Suivant', getStarted:'Commencer',
      welcomeMessage:'Bienvenue !',
      welcomeDescription:'Tout comme le peuple Bissa a construit des communautés prospères grâce à des relations solides, nous vous aiderons à construire des connexions commerciales durables avec des insights alimentés par l\'IA.',
      timeEstimate:'✨ Cela prendra environ 2 minutes'
    }
  },
  sw: {
    nav: {
      dashboard: 'Dashibodi',
      aiInsights: 'Maarifa ya AI',
      contacts: 'Wasiliani',
      companies: 'Kampuni',
      deals: 'Mikakati',
      marketing: 'Masoko',
      tasks: 'Kazi',
      reports: 'Ripoti',
      activities: 'Shughuli',
      settings: 'Mipangilio',
      billing: 'Malipo',
      automations: 'Uendeshaji Kiotomatiki',
      importExport: 'Ingiza/Safirisha',
      calendar: 'Kalenda',
    },
    header: {
      searchPlaceholder: 'Tafuta wasiliani, kampuni, mikakati...'
    },
    auth: {
      authenticating: 'Inathibitisha...'
    },
    landing: {
      heroBadge: 'Ambapo urithi unakutana na AI',
      heroTitlePrefix: 'Jenga Mahusiano',
      heroTitleHighlight: ' Kama Bissa',
      ctaPrimary: 'Anza Bure',
      ctaSecondary: 'Tazama Vipengele',
      badgeNoCard: 'Hakuna kadi ya mkopo inahitajika',
      badgeFreeForever: 'Bure milele',
      previewTitle: 'Dashibodi ya Bissa.ai',
      previewConnections: 'Mawasiliano',
      previewRevenue: 'Mapato',
      signIn: 'Ingia',
      featuresTitle: 'Imetengenezwa kwa Uunganisho',
      featuresSubtitle: 'Zana zenye nguvu zinazoheshimu sanaa ya kujenga mahusiano, zikiimarishwa na AI',
      cardRelationship: 'Usimamizi wa Mahusiano',
      cardTrustSales: 'Mauzo yanayotegemea uaminifu',
      cardOutreach: 'Ufikaji wa maana',
      cardInsights: 'Maarifa ya Uunganisho',
      pricingTitle: 'Mahusiano yanayokua pamoja nawe',
      pricingSubtitle: 'Anza bure. Kua kadri jamii yako inavyokua. Hakuna ada kwa kila mtumiaji.',
      free: 'Bure',
      growth: 'Kukua',
      pro: 'Pro',
      business: 'Biashara',
      mostPopular: 'Maarufu zaidi',
      perMonth: '/mwezi',
      btnGetStarted: 'Anza',
      btnStartTrial: 'Anza majaribio bure',
      btnContactSales: 'Wasiliana na mauzo',
      allPlansNote: 'Mipango yote inajumuisha majaribio ya siku 14. Hakuna kadi inayohitajika.',
      fallbackImage: 'Eneo la kazi la CRM la kitaaluma',
      storyBadge: 'Hadithi Yetu',
      storyTitle: 'Ambapo jadi hukutana na ubunifu',
      storyP1: 'Watu wa Bissa nchini Burkina Faso wamefanikiwa kwa karne nyingi kupitia mitandao imara, jamii thabiti, na mahusiano ya biashara yanayotegemea uaminifu.',
      storyP2: 'Leo, Bissa.ai inaendeleza urithi huu — kusaidia biashara kujenga mahusiano ya kudumu kupitia uunganisho wenye akili na maarifa ya AI.',
      legacyTitle: 'Urithi wa Bissa',
      legacyP1: 'Wamejulikana kwa uwezo wao wa kukabiliana na roho ya ujasiriamali, watu wa Bissa walijenga mitandao ya biashara yenye mafanikio katika Afrika ya Magharibi kupitia uhusiano wa kina, uaminifu, na mabadiliano ya kitamaduni.',
      legacyQuote: 'Kama vile Bissa walivyofanikiwa kupitia mitandao imara, Bissa.ai husaidia biashara kufanikiwa kupitia mahusiano bora.',
      legacyTagline: 'Urithi • Ubunifu • Uunganisho',
      footerProduct: 'Bidhaa',
      footerFeatures: 'Vipengele',
      footerPricing: 'Bei',
      footerSignUp: 'Jisajili',
      footerSignIn: 'Ingia',
      footerLegal: 'Kisheria',
      footerTerms: 'Vigezo vya Huduma',
      footerPrivacy: 'Sera ya Faragha',
      footerCookies: 'Sera ya Vidakuzi',
      footerRefunds: 'Sera ya Marejesho',
      footerDpa: 'Uchakataji wa Data',
      footerInspire: 'Imechochewa na urithi wa Bissa wa biashara ya kuaminiana na ujenzi wa jamii.',
      footerCopyright: '© 2024 Bissa.ai',
      footerMade: 'Imetengenezwa kwa ❤️ kwa biashara ndogo ndogo',
      footerDescription: 'Jenga mahusiano kama watu wa Bissa wa Burkina Faso — kupitia uaminifu, uunganisho na maarifa ya AI ambayo inaimarisha kila mahusiano ya biashara.',
      heroDescription: 'Kama vile watu wa Bissa wa Burkina Faso walivyojenga jamii zenye ustawi kupitia uaminifu na uunganisho, Bissa.ai inawezesha biashara yako na maarifa ya AI ambayo inaimarisha kila mahusiano.',
      cardRelationshipDesc: 'Jenga mahusiano ya kina zaidi na wasifu wa wasiliani wenye muktadha, historia ya mwingiliano, na maelezo ya kibinafsi.',
      cardTrustSalesDesc: 'Fuatilia mahusiano kupitia kila hatua na mfumo wa kuona ambao unastahili safari ya kujenga uaminifu.',
      cardOutreachDesc: 'Unganisha kwa kweli na kampeni za kibinafsi ambazo zinastahili utofauti wa kitamaduni na mapendeleo ya kibinafsi.',
      cardInsightsDesc: 'Elewa nguvu ya mahusiano, mifumo ya ushiriki, na ukuaji wa jami',
      dealValue: 'Thamani ya Mikakati',
      conversionRate: 'Kiwango cha Ubadilishaji',
      campaignsSent: 'Kampeni Zilizotumwa',
      quickActions: 'Vitendo vya Haraka',
      addContact: 'Ongeza Mwasiliani Mpya',
      createTask: 'Unda Kazi',
      createDeal: 'Unda Mkakati',
      newCampaign: 'Kampeni Mpya',
      recentActivity: 'Shughuli za Hivi Karibuni',
      noRecent: 'Hakuna shughuli za hivi karibuni',
      due: 'Tarehe ya Mwisho:'
    },
    pages:{
      marketing:{
        title:'Kituo cha Masoko',
        subtitle:'Unda na dhibiti kampeni za barua pepe',
        createBtn:'Unda kampeni',
        dialogTitle:'Unda Kampeni Mpya',
        nameLabel:'Jina la kampeni *',
        subjectLabel:'Mada ya barua *',
        contentLabel:'Yaliyomo ya barua *',
        statusLabel:'Hali',
        draft:'Rasimu',scheduled:'Imepangwa',sent:'Imetumwa',
        cancel:'Ghairi',
        noItems:'Hakuna kampeni. Unda kampeni yako ya kwanza!',
        sentLabel:'Zilizotumwa',openRate:'Kiwango cha kufunguliwa',viewDetails:'Tazama maelezo'
      },
      contacts:{ title:'Wasiliani', subtitle:'Dhibiti wasiliani na mahusiano', addBtn:'Ongeza mwasiliani', noItems:'Hakuna wasiliani. Ongeza wa kwanza!' },
      companies:{ title:'Kampuni', subtitle:'Dhibiti akaunti za kampuni', addBtn:'Ongeza kampuni', noItems:'Hakuna kampuni. Ongeza ya kwanza!', searchPlaceholder:'Tafuta kampuni kwa jina, kikoa au sekta...' },
      deals:{ title:'Mkondo wa mauzo', subtitle:'Fuatilia na dhibiti mikakati', addBtn:'Ongeza mkakati', noItems:'Hakuna mikakati', stages:{ lead:'Mteja wa Uwezo', qualified:'Imethibitishwa', proposal:'Pendekezo', negotiation:'Mazungumzo', closedWon:'Imefungwa Imeshinda', closedLost:'Imefungwa Imeshindwa' } },
      tasks:{ title:'Kazi na vikumbusho', subtitle:'Panga kazi zako usikose muda', newBtn:'Kazi mpya' },
      activities:{ title:'Mtiririko wa Shughuli', subtitle:'Fuatilia shughuli zote kwenye akaunti yako', empty:'Hakuna shughuli bado. Anza kwa kuunda wasiliani, mikakati au kampeni!' },
      billing:{ title:'Malipo na matumizi', subtitle:'Dhibiti usajili na ufuatilie matumizi', currentUsage:'Matumizi ya sasa', subscription:'Maelezo ya usajili', features:'Vipengele', availablePlans:'Mipango inayopatikana', currentPlan:'Mpango wa sasa', upgrade:'Boresha', downgrade:'Punguza', approaching:'Kukaribia mipaka', approachingDesc:'Unakaribia mipaka ya mpango wako. Fikiria kuboresha ili kuendelea kutumia vipengele vyote bila usumbufu.' },
      calendar:{ title:'Kalenda', subtitle:'Tazama na unda matukio ya Google Calendar', refresh:'Onyesha upya', createQuick:'Unda Tukio la Haraka', titleLabel:'Kichwa', startIso:'Mwanzo (ISO)', endIso:'Mwisho (ISO)', create:'Unda', upcoming:'Matukio yajayo', noEvents:'Hakuna matukio.' },
      importExport:{ title:'Ingiza & Safirisha', subtitle:'Dhibiti data kwa mafaili ya CSV', contacts:'Wasiliani', companies:'Kampuni', importContacts:'Ingiza wasiliani', importCompanies:'Ingiza kampuni', uploadCsv:'Pakia CSV', importing:'Inasafirisha...', template:'Kigezo', exportContacts:'Safirisha wasiliani', exportCompanies:'Safirisha kampuni', exportCsv:'Safirisha CSV', importComplete:'Uingizaji umekamilika!', importFailed:'Uingizaji umeshindikana', exportComplete:'Usafirishaji umekamilika!', exportFailed:'Usafirishaji umeshindikana', templateDownloaded:'Kigezo kimopakuliwa', uploadHelp:'Pakia faili ya CSV ili kuleta wasiliani wengi mara moja', uploadCompaniesHelp:'Pakia faili ya CSV ili kuleta kampuni nyingi mara moja' },
      reports:{ title:'Ripoti na uchanganuzi', subtitle:'Fuatilia utendaji na ufanye maamuzi kwa data', overview:'Muhtasari', sales:'Mkondo wa mauzo', contacts:'Wasiliani', email:'Utendaji wa barua pepe', export:'Safirisha data', revenueByMonth:'Mapato kwa mwezi', dealsByStage:'Mikakati kwa hatua', pipelineValue:'Thamani kwa hatua', activity30:'Shughuli (siku 30 zilizopita)', contactsByStatus:'Wasiliani kwa hali', recentContacts:'Wasiliani wa hivi karibuni', exportReports:'Safirisha ripoti', dealsReport:'Ripoti ya mikakati', contactsReport:'Ripoti ya wasiliani' },
      aiInsights:{ title:'Mawazo ya AI', subtitle:'Uchanganuzi wa AI wa mahusiano ya biashara na fursa', getInsights:'Pata Mawazo ya AI', startAnalysis:'Anza Uchanganuzi wa AI', welcome:'Karibu katika Mawazo ya AI, {name}!', description:'Pata uchanganuzi wa AI wa wasiliani wako {count} na fursa za biashara. Bonyeza "Pata Mawazo ya AI" hapo juu kuanza uchanganuzi wako.', authRequired:'Uthibitishaji Unahitajika', authDescription:'Tafadhali ingia ili kupata uchanganuzi wa AI.', goToLogin:'Nenda kwenye Ingia', businessInsights:'Mawazo ya Biashara ya AI', contactsEngagement:'CRM yako inaonyesha wasiliani {count} wenye uwezo mkubwa wa kushiriki.', keyInsights:'Mawazo Muhimu:', highPriorityContacts:'Una wasiliani {count} wa kipaumbele cha juu ambao wanahitaji ufuatiliaji wa haraka', activeSectors:'Wasiliani wengi wanaofanya kazi ni katika sekta za teknolojia na ushauri', responseRate:'Kiwango cha kujibu cha wastani ni {rate}% - juu ya kiwango cha tasnia', aiRecommendations:'Mapendekezo ya AI:', focusContacts:'Lenga wasiliani {count} wenye alama za juu za kushiriki', scheduleFollowups:'Panga ufuatiliaji na wanaotarajiwa ambao walifungua barua pepe zako za mwisho', leadScoring:'Matokeo ya Alama za Wanaotarajiwa - wasiliani {count} wamechanganuliwa', hot:'MOTO', warm:'JOTO', cold:'BARIDI', recentEngagement:'Kushiriki hivi karibuni, Thamani ya juu', multipleInteractions:'Mwingiliano mwingi, Mtu wa kufanya maamuzi', showedInterest:'Alionyesha hamu, Bajeti imethibitishwa' },
      automations:{ title:'Otomatiki', subtitle:'Unda sheria rahisi za kufanya kazi moja kwa moja wakati matukio muhimu yanatokea.', createTitle:'Unda Sheria', name:'Jina', trigger:'Kichocheo', triggerContact:'Mwasiliani: Hali Imebadilika', triggerDeal:'Mkakati: Hatua Imebadilika', enabled:'Imeamilishwa', newStatus:'Hali Mpya', newStage:'Hatua Mpya', action:'Hatua', createTask:'Unda Kazi', sendEmail:'Tuma Barua Pepe', dueDays:'Siku za Muda', taskTitle:'Kichwa cha Kazi', priority:'Kipaumbele', low:'Chini', medium:'Kati', high:'Juu', emailSubject:'Mada ya Barua Pepe', templateId:'Kitambulisho cha Kigezo (hiari)', bodyHtml:'Mwili (HTML, hiari)', createBtn:'Unda Sheria', yourRules:'Sheria Zako', loading:'Inapakia…', noRules:'Hakuna sheria bado.', enabledBadge:'IMEAMILISHWA', disabledBadge:'IMEZIMWA', disable:'Zima', enable:'Amilisha', delete:'Futa', deleteConfirm:'Futa sheria hii?' },
      help:{ title:'Msaada na Usaidizi', searchPlaceholder:'Tafuta makala za msaada...', quickStart:'Mwongozo wa Kuanza Haraka', contactSupport:'Wasiliana na Usaidizi', learnBasics:'Jifunze mambo ya msingi kwa dakika 5', getHelp:'Pata msaada kutoka kwa timu yetu', commonQuestions:'Maswali ya Kawaida', howToImport:'Je, ninawezaje kuagiza wasiliani wangu?', howToCreateDeal:'Je, ninawezaje kuunda mkakati?', canIUpgrade:'Je, naweza kuboresha mpango wangu?', importAnswer:'Nenda kwenye ukurasa wa Kuagiza/Kusafirisha na upakia faili yako ya CSV. Tunasaidia miundo ya kawaida kutoka kwa CRMs nyingi.', dealAnswer:'Tumia kitufe cha Ongeza Haraka au nenda kwenye ukurasa wa Mikakati na bonyeza "Ongeza Mkakati".', upgradeAnswer:'Ndiyo! Nenda kwenye Mipangilio ili kuona na kubadilisha mpango wako wa usajili wakati wowote.' },
      support:{ title:'Wasiliana na Usaidizi', name:'Jina', email:'Barua Pepe', subject:'Mada', category:'Kategoria', message:'Ujumbe', priority:'Kipaumbele', general:'Swali la Jumla', technical:'Tatizo la Kiufundi', billing:'Ulipaji na Akaunti', feature:'Ombi la Kipengele', bug:'Ripoti ya Hitilafu', low:'Chini - Uchunguzi wa jumla', medium:'Kati - Usaidizi wa kawaida', high:'Juu - Tatizo la haraka', send:'Tuma Ujumbe', sending:'Inatumwa...', success:'Ujumbe umetumwa kwa mafanikio!', error:'Imeshindwa kutuma ujumbe. Tafadhali jaribu tena.' },
      tutorial:{ title:'Karibu Bissa!', subtitle:'Tujenge mahusiano ya maana pamoja', ready:'Tayari kujenga miunganisho kama Bissa?', description:'Mafunzo haya yatakuonyesha jinsi Bissa.ai inawezesha biashara yako kwa vifaa vya kujenga mahusiano ambavyo vinahonoi jadi wakati vinakumbatia ubunifu wa AI. Jifunze kuimarisha kila muunganisho.', timeRequired:'Muda unaohitajika: dakika 5', letsStart:'Tuanze!', next:'Ifuatayo', previous:'Iliyotangulia', finish:'Maliza', close:'Funga' }
    },
    onboarding:{
      welcomeTitle:'Karibu Bissa.ai!',
      welcomeSubtitle:'Tukusanue kwa mafanikio',
      roleTitle:'Nafasi yako ni ipi?',
      roleSubtitle:'Tusaidie kubinafsisha uzoefu wako',
      companyTitle:'Tuambie kuhusu kampuni yako',
      companySubtitle:'Hili hutusaidia kuboresha vipengele kwako',
      goalsTitle:'Lengo lako kuu ni lipi?',
      goalsSubtitle:'Tutaangazia vipengele muhimu zaidi',
      setupTitle:'Tengeneza rekodi zako za kwanza',
      setupSubtitle:'Anza na data ya mfano',
      completeTitle:'Uko tayari!',
      completeSubtitle:'Karibu kwenye jamii ya Bissa.ai',
      step:'Hatua', of:'ya', back:'Nyuma', next:'Mbele', getStarted:'Anza',
      welcomeMessage:'Karibu!',
      welcomeDescription:'Kama vile watu wa Bissa walijenga jamii zenye ufanisi kupitia mahusiano mazuri, tutakusaidia kujenga uhusiano wa kibiashara wa kudumu na maarifa ya AI.',
      timeEstimate:'✨ Hii itachukua dakika 2'
    }
  },
};
// Add Tier 1 African languages dynamically to avoid large inline edits
const extraTranslations = {
  ar: {
    nav: {
      dashboard: 'لوحة التحكم',
      aiInsights: 'رؤى الذكاء الاصطناعي',
      contacts: 'جهات الاتصال',
      companies: 'الشركات',
      deals: 'الصفقات',
      marketing: 'التسويق',
      tasks: 'المهام',
      reports: 'التقارير',
      activities: 'الأنشطة',
      settings: 'الإعدادات',
      billing: 'الفوترة',
      automations: 'الأتمتة',
      importExport: 'استيراد/تصدير',
      calendar: 'التقويم',
    },
    header: { searchPlaceholder: 'ابحث في جهات الاتصال والشركات والصفقات...' },
    auth: {
      authenticating: 'جاري المصادقة...'
    },
    landing: {
      heroBadge: 'حيث يلتقي التراث بالذكاء الاصطناعي',
      heroTitle: 'Bissa.ai: CRM ذكي للشركات الصغيرة',
      heroSubtitle: 'إدارة جهات الاتصال والصفقات والحملات بالذكاء الاصطناعي. مستوحى من تراث البيسا في التجارة القائمة على الثقة.',
      heroCta: 'ابدأ مجانًا',
      heroSecondary: 'عرض العرض التوضيحي',
      featuresTitle: 'لماذا تختار Bissa.ai؟',
      featuresSubtitle: 'كل ما تحتاجه لتنمية عملك',
      feature1Title: 'ذكاء اصطناعي متكامل',
      feature1Desc: 'رؤى ذكية وأتمتة لتحسين المبيعات',
      feature2Title: 'إدارة شاملة',
      feature2Desc: 'جهات الاتصال والشركات والصفقات والحملات في مكان واحد',
      feature3Title: 'سهل الاستخدام',
      feature3Desc: 'واجهة بديهية تعمل على أي جهاز',
      pricingTitle: 'خطط بسيطة',
      pricingSubtitle: 'اختر الخطة المناسبة لعملك',
      pricingFree: 'مجاني',
      pricingFreeDesc: 'مثالي للبداية',
      pricingGrowth: 'نمو',
      pricingGrowthDesc: 'للشركات المتوسعة',
      pricingOwner: 'مالك',
      pricingOwnerDesc: 'ميزات غير محدودة',
      free: 'مجاني',
      growth: 'النمو',
      pro: 'برو',
      business: 'الأعمال',
      mostPopular: 'الأكثر شعبية',
      perMonth: '/شهر',
      btnGetStarted: 'ابدأ',
      btnStartTrial: 'ابدأ التجربة المجانية',
      btnContactSales: 'اتصل بالمبيعات',
      allPlansNote: 'جميع الخطط تشمل تجربة مجانية لمدة 14 يوماً. لا توجد بطاقة مطلوبة.',
      fallbackImage: 'مساحة عمل CRM احترافية',
      footerProduct: 'المنتج',
      footerFeatures: 'الميزات',
      footerPricing: 'الأسعار',
      footerSignUp: 'التسجيل',
      footerSignIn: 'تسجيل الدخول',
      footerLegal: 'قانوني',
      footerTerms: 'شروط الخدمة',
      footerPrivacy: 'سياسة الخصوصية',
      footerCookies: 'سياسة ملفات تعريف الارتباط',
      footerRefunds: 'سياسة الاسترداد',
      footerDpa: 'معالجة البيانات',
      heroBadge: 'حيث يلتقي التراث بالذكاء الاصطناعي',
      heroTitlePrefix: 'ابني العلاقات',
      heroTitleHighlight: ' مثل البيسا',
      ctaPrimary: 'ابدأ مجاناً',
      ctaSecondary: 'تعرف على المزيد',
      noCardRequired: 'لا توجد بطاقة مطلوبة',
      freeForever: 'مجاني للأبد',
      madeForConnection: 'مصمم للاتصال',
      madeForConnectionDesc: 'أدوات قوية تحترم فن بناء العلاقات، معززة بالذكاء الاصطناعي',
      cardRelationship: 'إدارة العلاقات',
      cardTrustSales: 'مبيعات قائمة على الثقة',
      cardOutreach: 'الوصول الهادف',
      cardInsights: 'رؤى الاتصال',
      pricingTitle: 'العلاقات التي تنمو معك',
      pricingSubtitle: 'ابدأ مجاناً. انمو مع نمو مجتمعك. لا توجد رسوم لكل مستخدم.',
      ourStory: 'قصتنا',
      storyTitle: 'حيث تلتقي التقاليد بالابتكار',
      storyP1: 'شعب البيسا في بوركينا فاسو نجح لقرون من خلال الشبكات القوية والمجتمعات المستقرة والعلاقات التجارية القائمة على الثقة.',
      storyP2: 'اليوم، Bissa.ai تواصل هذا الإرث — مساعدة الشركات على بناء علاقات دائمة من خلال الاتصالات الذكية ورؤى الذكاء الاصطناعي.',
      legacyTitle: 'إرث البيسا',
      legacyP1: 'معروفون بقدرتهم على التكيف وروحهم الريادية، بنى شعب البيسا شبكات تجارية مزدهرة عبر غرب إفريقيا من خلال علاقات عميقة وثقة وتبادل ثقافي.',
      legacyQuote: 'تماماً كما نجح البيسا من خلال الشبكات والاتصالات القوية، Bissa.ai تساعد الشركات على النجاح من خلال علاقات أكثر ذكاءً.',
      buildRelationships: 'ابني العلاقات مثل البيسا؟',
      footerInspire: 'مستوحى من تراث البيسا في التجارة القائمة على الثقة وبناء المجتمع.',
      footerCopyright: '© 2024 Bissa.ai',
      footerMade: 'صُنع بـ ❤️ للشركات الصغيرة',
      footerDescription: 'ابني علاقات مثل شعب البيسا في بوركينا فاسو — من خلال الثقة والاتصال والرؤى المدعومة بالذكاء الاصطناعي التي تقوي كل علاقة تجارية.',
      heroDescription: 'تماماً كما بنى شعب البيسا في بوركينا فاسو مجتمعات مزدهرة من خلال الثقة والاتصال، Bissa.ai تمكن عملك برؤى مدعومة بالذكاء الاصطناعي تقوي كل علاقة.',
      cardRelationshipDesc: 'ابني اتصالات أعمق مع ملفات جهات اتصال غنية بالسياق وتاريخ التفاعل والملاحظات الشخصية.',
      cardTrustSalesDesc: 'تتبع العلاقات عبر كل مرحلة مع خط أنابيب مرئي يكرم رحلة بناء الثقة.',
      cardOutreachDesc: 'تواصل بصدق مع حملات مخصصة تحترم التنوع الثقافي والتفضيلات الفردية.',
      cardInsightsDesc: 'افهم قوة العلاقات وأنماط المشاركة ونمو المجتمع مع التحليلات المدعومة بالذكاء الاصطناعي.',
      legacyDescription: 'معروفون بقدرتهم على التكيف وروحهم الريادية، بنى شعب البيسا شبكات تجارية مزدهرة عبر غرب أفريقيا من خلال علاقات عميقة وثقة وتبادل ثقافي.',
      bottomPrivacy: 'الخصوصية',
      bottomTerms: 'الشروط',
      bottomRefunds: 'الاسترداد',
      storyBadge: 'قصتنا',
      legacyTagline: 'التراث • الابتكار • الاتصال',
      badgeNoCard: 'لا حاجة لبطاقة ائتمان',
      badgeFreeForever: 'مجاني للأبد',
      previewTitle: 'لوحة تحكم Bissa.ai',
      previewConnections: 'الاتصالات',
      previewRevenue: 'الإيرادات',
      signIn: 'تسجيل الدخول',
      featuresTitle: 'مصمم للاتصال',
      featuresSubtitle: 'أدوات قوية تحترم فن بناء العلاقات، معززة بذكاء الذكاء الاصطناعي',
      pricingFeatures: {
        free: {
          contacts: '100 جهة اتصال',
          users: 'مستخدم واحد',
          crm: 'نظام إدارة علاقات العملاء الأساسي',
          tasks: '50 مهمة/شهر',
          support: 'دعم المجتمع'
        },
        growth: {
          contacts: '5,000 جهة اتصال',
          users: '3 مستخدمين',
          tasks: '500 مهمة/شهر',
          campaigns: '25 حملة',
          integration: 'تكامل البريد الإلكتروني',
          support: 'دعم البريد الإلكتروني'
        },
        pro: {
          contacts: '10,000 جهة اتصال',
          users: '10 مستخدمين',
          everything: 'كل شيء في النمو',
          api: 'وصول API',
          reports: 'تقارير متقدمة',
          support: 'دعم أولوية'
        },
        business: {
          contacts: 'جهات اتصال غير محدودة',
          users: 'مستخدمين غير محدودين',
          everything: 'كل شيء في Pro',
          whiteLabel: 'خيار العلامة البيضاء',
          integrations: 'تكاملات مخصصة',
          manager: 'مدير مخصص'
        }
      }
    },
    dashboard: {
      title: 'لوحة التحكم',
      welcome: 'مرحبًا بعودتك! إليك ما يحدث في عملك.',
      totalContacts: 'إجمالي جهات الاتصال',
      companies: 'الشركات',
      activeDeals: 'صفقات نشطة',
      pendingTasks: 'مهام قيد الانتظار',
      totalRevenue: 'إجمالي الإيرادات',
      dealValue: 'قيمة الصفقات',
      conversionRate: 'معدل التحويل',
      campaignsSent: 'حملات مُرسلة',
      quickActions: 'إجراءات سريعة',
      addContact: 'إضافة جهة اتصال',
      createTask: 'إنشاء مهمة',
      createDeal: 'إنشاء صفقة',
      newCampaign: 'حملة جديدة',
      recentActivity: 'النشاط الأخير',
      noRecent: 'لا يوجد نشاط حديث',
      due: 'تاريخ الاستحقاق:'
    },
    pages: {
      marketing: {
        title: 'مركز التسويق',
        subtitle: 'إنشاء وإدارة حملات البريد الإلكتروني',
        createBtn: 'إنشاء حملة',
        dialogTitle: 'إنشاء حملة جديدة',
        nameLabel: 'اسم الحملة *',
        subjectLabel: 'موضوع البريد *',
        contentLabel: 'محتوى البريد *',
        statusLabel: 'الحالة',
        draft: 'مسودة', scheduled: 'مجدولة', sent: 'مرسلة',
        cancel: 'إلغاء',
        noItems: 'لا توجد حملات. أنشئ أول حملة لك!',
        sentLabel: 'مُرسلة', openRate: 'معدل الفتح', viewDetails: 'عرض التفاصيل'
      },
      contacts: { title: 'جهات الاتصال', subtitle: 'إدارة جهات الاتصال والعلاقات', addBtn: 'إضافة جهة اتصال', noItems: 'لا توجد جهات اتصال. أنشئ الأولى!' },
      companies: { title: 'الشركات', subtitle: 'إدارة حسابات الشركات', addBtn: 'إضافة شركة', noItems: 'لا توجد شركات.', searchPlaceholder: 'البحث عن الشركات بالاسم أو النطاق أو الصناعة...' },
      deals: { title: 'قناة المبيعات', subtitle: 'تتبع وإدارة الصفقات', addBtn: 'إضافة صفقة', noItems: 'لا توجد صفقات' },
      tasks: { title: 'المهام والتذكيرات', subtitle: 'ابق منظمًا ولا تفوت المواعيد', newBtn: 'مهمة جديدة' },
      activities: { title: 'سجل النشاط', subtitle: 'تتبع جميع الأنشطة عبر حسابك', empty: 'لا يوجد نشاط بعد. ابدأ بإنشاء جهات اتصال أو صفقات أو حملات!' },
      billing: { title: 'الفوترة والاستخدام', subtitle: 'إدارة الاشتراك وتتبع الاستخدام', currentUsage: 'الاستخدام الحالي', subscription: 'تفاصيل الاشتراك', features: 'الميزات', availablePlans: 'الخطط المتاحة', currentPlan: 'الخطة الحالية', upgrade: 'ترقية', downgrade: 'تخفيض', approaching: 'الاقتراب من الحدود', approachingDesc: 'أنت تقترب من حدود خطتك. فكر في الترقية لمواصلة الاستخدام دون انقطاع.' },
      calendar: {
        title: 'التقويم',
        subtitle: 'عرض وإنشاء أحداث تقويم Google',
        refresh: 'تحديث',
        createQuick: 'إنشاء حدث سريع',
        titleLabel: 'العنوان',
        startIso: 'البداية (ISO)',
        endIso: 'النهاية (ISO)',
        create: 'إنشاء',
        upcoming: 'الأحداث القادمة',
        noEvents: 'لا توجد أحداث.',
        integrations: 'تكاملات التقويم',
        googleConnected: 'متصل بتقويم Google',
        googleDisconnected: 'غير متصل بتقويم Google',
        connectGoogle: 'اتصال Google',
        disconnect: 'قطع الاتصال',
        outlookComingSoon: 'تكامل Outlook قريبًا',
        comingSoon: 'قريبًا'
      },
      importExport: { title: 'الاستيراد والتصدير', subtitle: 'إدارة البيانات مجمّعًا عبر CSV', contacts: 'جهات الاتصال', companies: 'الشركات', importContacts: 'استيراد جهات الاتصال', importCompanies: 'استيراد الشركات', uploadCsv: 'رفع CSV', importing: 'جاري الاستيراد...', template: 'قالب', exportContacts: 'تصدير جهات الاتصال', exportCompanies: 'تصدير الشركات', exportCsv: 'تصدير CSV', importComplete: 'اكتمل الاستيراد!', importFailed: 'فشل الاستيراد', exportComplete: 'اكتمل التصدير!', exportFailed: 'فشل التصدير', templateDownloaded: 'تم تنزيل القالب', uploadHelp: 'ارفع ملف CSV لاستيراد عدة جهات اتصال مرة واحدة', uploadCompaniesHelp: 'ارفع ملف CSV لاستيراد عدة شركات مرة واحدة' },
      reports: { title: 'التقارير والتحليلات', subtitle: 'تتبع الأداء واتخذ قرارات مبنية على البيانات', overview: 'نظرة عامة', sales: 'قناة المبيعات', contacts: 'جهات الاتصال', email: 'أداء البريد', export: 'تصدير البيانات', revenueByMonth: 'الإيرادات حسب الشهر', dealsByStage: 'الصفقات حسب المرحلة', pipelineValue: 'القيمة حسب المرحلة', activity30: 'نشاط (آخر 30 يومًا)', contactsByStatus: 'جهات الاتصال حسب الحالة', recentContacts: 'جهات الاتصال الحديثة', exportReports: 'تصدير التقارير', dealsReport: 'تقرير الصفقات', contactsReport: 'تقرير جهات الاتصال' },
      aiInsights: { 
        title: 'رؤى الذكاء الاصطناعي', 
        subtitle: 'تحليل مدعوم بالذكاء الاصطناعي لعلاقاتك التجارية والفرص', 
        getInsights: 'الحصول على رؤى الذكاء الاصطناعي', 
        startAnalysis: 'بدء التحليل بالذكاء الاصطناعي', 
        welcome: 'مرحبًا بك في رؤى الذكاء الاصطناعي، {name}!', 
        description: 'احصل على تحليل مدعوم بالذكاء الاصطناعي لـ {count} جهة اتصال وفرصك التجارية. انقر على "الحصول على رؤى الذكاء الاصطناعي" أعلاه لبدء تحليلك.', 
        authRequired: 'المصادقة مطلوبة', 
        authDescription: 'يرجى تسجيل الدخول للوصول إلى التحليل المدعوم بالذكاء الاصطناعي.', 
        goToLogin: 'الذهاب إلى تسجيل الدخول', 
        businessInsights: 'رؤى الأعمال بالذكاء الاصطناعي', 
        contactsEngagement: 'نظام إدارة العملاء الخاص بك يظهر {count} جهة اتصال بإمكانات مشاركة قوية.', 
        keyInsights: 'الرؤى الرئيسية:', 
        highPriorityContacts: 'لديك {count} جهة اتصال ذات أولوية عالية تحتاج إلى متابعة فورية', 
        activeSectors: 'معظم جهات الاتصال النشطة في قطاعات التكنولوجيا والاستشارات', 
        responseRate: 'معدل الاستجابة المتوسط هو {rate}% - أعلى من المعيار الصناعي', 
        aiRecommendations: 'توصيات الذكاء الاصطناعي:', 
        focusContacts: 'ركز على {count} جهة اتصال بأعلى درجات المشاركة', 
        scheduleFollowups: 'جدول المتابعات مع العملاء المحتملين الذين فتحوا رسائل البريد الإلكتروني الأخيرة', 
        leadScoring: 'نتائج تسجيل العملاء المحتملين - تم تحليل {count} جهة اتصال', 
        hot: 'ساخن', 
        warm: 'دافئ', 
        cold: 'بارد', 
        recentEngagement: 'مشاركة حديثة، قيمة عالية', 
        multipleInteractions: 'تفاعلات متعددة، صانع قرار', 
        showedInterest: 'أظهر اهتمامًا، تم تأكيد الميزانية' 
      },
      automations: { title: 'الأتمتة', subtitle: 'إنشاء قواعد بسيطة لأتمتة المهام عند حدوث أحداث رئيسية.', createTitle: 'إنشاء قاعدة', name: 'الاسم', trigger: 'المشغل', triggerContact: 'جهة الاتصال: تغيير الحالة', triggerDeal: 'الصفقة: تغيير المرحلة', enabled: 'مفعل', newStatus: 'الحالة الجديدة', newStage: 'المرحلة الجديدة', action: 'الإجراء', createTask: 'إنشاء مهمة', sendEmail: 'إرسال بريد إلكتروني', dueDays: 'أيام الاستحقاق', taskTitle: 'عنوان المهمة', priority: 'الأولوية', low: 'منخفضة', medium: 'متوسطة', high: 'عالية', emailSubject: 'موضوع البريد الإلكتروني', templateId: 'معرف القالب (اختياري)', bodyHtml: 'المحتوى (HTML، اختياري)', createBtn: 'إنشاء قاعدة', yourRules: 'قواعدك', loading: 'جاري التحميل…', noRules: 'لا توجد قواعد بعد.', enabledBadge: 'مفعل', disabledBadge: 'معطل', disable: 'تعطيل', enable: 'تفعيل', delete: 'حذف', deleteConfirm: 'حذف هذه القاعدة؟' },
      help: { title: 'المساعدة والدعم', searchPlaceholder: 'البحث في مقالات المساعدة...', quickStart: 'دليل البدء السريع', contactSupport: 'اتصل بالدعم', learnBasics: 'تعلم الأساسيات في 5 دقائق', getHelp: 'احصل على المساعدة من فريقنا', commonQuestions: 'الأسئلة الشائعة', howToImport: 'كيف أقوم باستيراد جهات الاتصال؟', howToCreateDeal: 'كيف أقوم بإنشاء صفقة؟', canIUpgrade: 'هل يمكنني ترقية خطتي؟', importAnswer: 'اذهب إلى صفحة الاستيراد/التصدير وارفع ملف CSV الخاص بك. ندعم التنسيقات القياسية من معظم أنظمة إدارة العملاء.', dealAnswer: 'استخدم زر الإضافة السريع أو اذهب إلى صفحة الصفقات وانقر على "إضافة صفقة".', upgradeAnswer: 'نعم! اذهب إلى الإعدادات لعرض وتغيير خطة الاشتراك الخاصة بك في أي وقت.' },
      support: { title: 'اتصل بالدعم', name: 'الاسم', email: 'البريد الإلكتروني', subject: 'الموضوع', category: 'الفئة', message: 'الرسالة', priority: 'الأولوية', general: 'سؤال عام', technical: 'مشكلة تقنية', billing: 'الفوترة والحساب', feature: 'طلب ميزة', bug: 'تقرير خطأ', low: 'منخفض - استفسار عام', medium: 'متوسط - دعم قياسي', high: 'عالي - مشكلة عاجلة', send: 'إرسال الرسالة', sending: 'جاري الإرسال...', success: 'تم إرسال الرسالة بنجاح!', error: 'فشل في إرسال الرسالة. يرجى المحاولة مرة أخرى.' },
      tutorial: { title: 'مرحبًا بك في Bissa!', subtitle: 'دعنا نبني علاقات ذات معنى معًا', ready: 'مستعد لبناء اتصالات مثل البيسا؟', description: 'هذا البرنامج التعليمي سيظهر لك كيف تمكن Bissa.ai عملك بأدوات بناء العلاقات التي تحترم التقاليد بينما تحتضن الابتكار بالذكاء الاصطناعي. تعلم تقوية كل اتصال.', timeRequired: 'الوقت المطلوب: 5 دقائق', letsStart: 'دعنا نبدأ!', next: 'التالي', previous: 'السابق', finish: 'إنهاء', close: 'إغلاق' }
    },
    onboarding: {
      welcomeTitle: 'مرحبًا بك في Bissa.ai!',
      welcomeSubtitle: 'لنجهزك للنجاح',
      roleTitle: 'ما هو دورك؟',
      roleSubtitle: 'ساعدنا على تخصيص تجربتك',
      companyTitle: 'أخبرنا عن شركتك',
      companySubtitle: 'يساعدنا هذا على تخصيص الميزات لك',
      goalsTitle: 'ما هو هدفك الرئيسي؟',
      goalsSubtitle: 'سنركز على ما يهم أكثر',
      setupTitle: 'لننشئ سجلاتك الأولى',
      setupSubtitle: 'ابدأ ببيانات نموذجية',
      completeTitle: 'أنت جاهز!',
      completeSubtitle: 'مرحبًا بك في مجتمع Bissa.ai',
      step: 'خطوة', of: 'من', back: 'رجوع', next: 'التالي', getStarted: 'ابدأ',
      welcomeMessage: 'مرحباً!',
      welcomeDescription: 'تماماً كما بنى شعب البيسا مجتمعات مزدهرة من خلال العلاقات القوية، سنساعدك في بناء روابط تجارية دائمة مع رؤى مدعومة بالذكاء الاصطناعي.',
      timeEstimate: '✨ سيستغرق هذا حوالي دقيقتين'
    }
  },
  pt: {
    nav: {
      dashboard: 'Painel',
      aiInsights: 'Insights IA',
      contacts: 'Contatos',
      companies: 'Empresas',
      deals: 'Negócios',
      marketing: 'Marketing',
      tasks: 'Tarefas',
      reports: 'Relatórios',
      activities: 'Atividades',
      settings: 'Configurações',
      billing: 'Cobrança',
      automations: 'Automações',
      importExport: 'Importar/Exportar',
      calendar: 'Calendário',
    },
    header: {
      searchPlaceholder: 'Buscar contatos, empresas, negócios...',
      notifications: 'Notificações',
      profile: 'Perfil',
      settings: 'Configurações',
      logout: 'Sair',
      language: 'Idioma'
    },
    auth: {
      authenticating: 'Autenticando...'
    },
    landing: {
      heroBadge: "Onde a herança encontra a IA",
      heroTitle: "Bissa.ai: CRM Inteligente para Pequenas Empresas",
      heroSubtitle: "Gerencie contatos, negócios e campanhas com IA. Inspirado na herança Bissa de comércio baseado em confiança.",
      heroCta: "Começar Grátis",
      heroSecondary: "Ver Demonstração",
      featuresTitle: "Por que escolher Bissa.ai?",
      featuresSubtitle: "Tudo que você precisa para crescer seu negócio",
      feature1Title: "IA Integrada",
      feature1Desc: "Insights inteligentes e automação para otimizar vendas",
      feature2Title: "Gestão Completa",
      feature2Desc: "Contatos, empresas, negócios e campanhas em um lugar",
      feature3Title: "Fácil de Usar",
      feature3Desc: "Interface intuitiva que funciona em qualquer dispositivo",
      pricingTitle: "Planos Simples",
      pricingSubtitle: "Escolha o plano certo para seu negócio",
      pricingFree: "Grátis",
      pricingFreeDesc: "Perfeito para começar",
      pricingGrowth: "Crescimento",
      pricingGrowthDesc: "Para empresas em expansão",
      pricingOwner: "Proprietário",
      pricingOwnerDesc: "Recursos ilimitados",
      footerFeatures: 'Recursos',
      footerPricing: 'Preços',
      footerSignUp: 'Cadastrar',
      footerSignIn: 'Entrar',
      footerLegal: 'Legal',
      footerTerms: 'Termos de serviço',
      footerPrivacy: 'Política de privacidade',
      footerCookies: 'Política de cookies',
      footerRefunds: 'Política de reembolsos',
      footerDpa: 'Tratamento de dados',
      footerInspire: 'Inspirado na herança Bissa de comércio baseado em confiança e construção comunitária.',
      allPlansNote: 'Todos os planos incluem 14 dias de teste. Nenhum cartão necessário.',
      fallbackImage: 'Espaço de trabalho CRM profissional',
      footerCopyright: '© 2024 Bissa.ai',
      footerMade: 'Feito com ❤️ para pequenas empresas',
      footerDescription: 'Construa relacionamentos como o povo Bissa de Burkina Faso — através da confiança, conexão e insights alimentados por IA que fortalecem cada relacionamento comercial.',
      heroDescription: 'Assim como o povo Bissa de Burkina Faso construiu comunidades prósperas através da confiança e conexão, Bissa.ai capacita seu negócio com insights alimentados por IA que fortalecem cada relacionamento.',
      cardRelationshipDesc: 'Construa conexões mais profundas com perfis de contato ricos em contexto, histórico de interações e notas pessoais.',
      cardTrustSalesDesc: 'Rastreie relacionamentos através de cada estágio com pipeline visual que honra a jornada de construção de confiança.',
      cardOutreachDesc: 'Conecte-se autenticamente com campanhas personalizadas que respeitam a diversidade cultural e preferências individuais.',
      cardInsightsDesc: 'Compreenda a força dos relacionamentos, padrões de engajamento e crescimento comunitário com análises alimentadas por IA.',
      legacyDescription: 'Conhecidos por sua adaptabilidade e espírito empreendedor, o povo Bissa construiu redes comerciais prósperas em toda a África Ocidental através de relacionamentos profundos, confiança e intercâmbio cultural.',
      bottomPrivacy: 'Privacidade',
      bottomTerms: 'Termos',
      bottomRefunds: 'Reembolsos',
      heroTitlePrefix: 'Construa Relacionamentos',
      heroTitleHighlight: ' Como os Bissa',
      ctaPrimary: 'Começar Grátis',
      ctaSecondary: 'Ver Recursos',
      badgeNoCard: 'Sem cartão necessário',
      badgeFreeForever: 'Grátis para sempre',
      previewTitle: 'Painel Bissa.ai',
      previewConnections: 'Conexões',
      previewRevenue: 'Receita',
      signIn: 'Entrar',
      featuresTitle: 'Feito para Conexão',
      featuresSubtitle: 'Ferramentas poderosas que honram a arte de construir relacionamentos, aprimoradas com inteligência de IA',
      cardRelationship: 'Gestão de Relacionamento',
      cardTrustSales: 'Vendas Baseadas em Confiança',
      cardOutreach: 'Alcance Significativo',
      cardInsights: 'Insights de Conexão',
      pricingTitle: 'Relacionamentos Que Escalam Com Você',
      pricingSubtitle: 'Comece a construir conexões gratuitamente. Cresça conforme sua comunidade cresce. Sem taxas por usuário.',
      free: 'Grátis',
      growth: 'Crescimento',
      pro: 'Pro',
      business: 'Negócios',
      mostPopular: 'Mais Popular',
      perMonth: '/mês',
      btnGetStarted: 'Começar',
      btnStartTrial: 'Iniciar Teste Gratuito',
      btnContactSales: 'Contatar Vendas',
      storyBadge: 'Nossa História',
      storyTitle: 'Onde a Tradição Encontra a Inovação',
      storyP1: 'O povo Bissa de Burkina Faso prosperou por séculos através de redes fortes, comunidades resilientes e relacionamentos comerciais baseados em confiança.',
      storyP2: 'Hoje, Bissa.ai leva esse legado adiante — capacitando empresas modernas a construir relacionamentos duradouros através de conexão inteligente e insights impulsionados por IA.',
      legacyTitle: 'O Legado Bissa',
      legacyP1: 'Conhecidos por sua adaptabilidade e espírito empreendedor, o povo Bissa construiu redes comerciais prósperas em toda a África Ocidental através de relacionamentos profundos, confiança e intercâmbio cultural.',
      legacyQuote: 'Assim como os Bissa prosperaram através de redes e conexões fortes, Bissa.ai capacita empresas a prosperar através de relacionamentos mais inteligentes.',
      legacyTagline: 'Herança • Inovação • Conexão',
      footerProduct: 'Produto',
      pricingFeatures: {
        free: {
          contacts: '100 contatos',
          users: '1 usuário',
          crm: 'CRM básico',
          tasks: '50 tarefas/mês',
          support: 'Suporte da comunidade'
        },
        growth: {
          contacts: '5,000 contatos',
          users: '3 usuários',
          tasks: '500 tarefas/mês',
          campaigns: '25 campanhas',
          integration: 'Integração de email',
          support: 'Suporte por email'
        },
        pro: {
          contacts: '10,000 contatos',
          users: '10 usuários',
          everything: 'Tudo no Growth',
          api: 'Acesso API',
          reports: 'Relatórios avançados',
          support: 'Suporte prioritário'
        },
        business: {
          contacts: 'Contatos ilimitados',
          users: 'Usuários ilimitados',
          everything: 'Tudo no Pro',
          whiteLabel: 'Opção white-label',
          integrations: 'Integrações personalizadas',
          manager: 'Gerente dedicado'
        }
      }
    },
    dashboard: {
      title: 'Painel',
      welcome: 'Bem-vindo! Aqui está o que está acontecendo com seu negócio.',
      totalContacts: 'Total de Contatos',
      companies: 'Empresas',
      activeDeals: 'Negócios Ativos',
      pendingTasks: 'Tarefas Pendentes',
      totalRevenue: 'Receita Total',
      dealValue: 'Valor dos Negócios',
      conversionRate: 'Taxa de Conversão',
      campaignsSent: 'Campanhas Enviadas',
      quickActions: 'Ações Rápidas',
      addContact: 'Adicionar Novo Contato',
      createTask: 'Criar Tarefa',
      createDeal: 'Criar Negócio',
      newCampaign: 'Nova Campanha',
      recentActivity: 'Atividade Recente',
      noRecent: 'Nenhuma atividade recente',
      due: 'Vence:'
    },
    pages: {
      marketing: {
        title: 'Centro de Marketing',
        subtitle: 'Crie e gerencie campanhas de email',
        createBtn: 'Criar campanha',
        dialogTitle: 'Criar nova campanha',
        nameLabel: 'Nome da campanha *',
        subjectLabel: 'Assunto do email *',
        contentLabel: 'Conteúdo do email *',
        statusLabel: 'Status',
        draft: 'Rascunho',
        scheduled: 'Agendada',
        sent: 'Enviada',
        cancel: 'Cancelar',
        noItems: 'Nenhuma campanha. Crie sua primeira campanha!',
        sentLabel: 'Enviados',
        openRate: 'Taxa de abertura',
        viewDetails: 'Ver detalhes'
      },
      contacts: {
        title: 'Contatos',
        subtitle: 'Gerencie seus contatos e relacionamentos',
        addBtn: 'Adicionar contato',
        noItems: 'Nenhum contato. Crie seu primeiro contato!'
      },
      companies: {
        title: 'Empresas',
        subtitle: 'Gerencie suas contas empresariais',
        addBtn: 'Adicionar empresa',
        noItems: 'Nenhuma empresa. Crie sua primeira empresa!',
        searchPlaceholder: 'Buscar empresas por nome, domínio ou setor...'
      },
      deals: {
        title: 'Funil de Vendas',
        subtitle: 'Acompanhe e gerencie suas oportunidades',
        addBtn: 'Adicionar negócio',
        noItems: 'Nenhum negócio'
      },
      tasks: {
        title: 'Tarefas e Lembretes',
        subtitle: 'Mantenha-se organizado e não perca prazos',
        newBtn: 'Nova tarefa'
      },
      activities: {
        title: 'Atividade',
        subtitle: 'Acompanhe todas as atividades da sua conta',
        empty: 'Ainda não há atividades. Crie contatos, negócios ou campanhas!'
      },
      billing: {
        title: 'Cobrança e Uso',
        subtitle: 'Gerencie sua assinatura e uso',
        currentUsage: 'Uso atual',
        subscription: 'Detalhes da assinatura',
        features: 'Recursos',
        availablePlans: 'Planos disponíveis',
        currentPlan: 'Plano atual',
        upgrade: 'Melhorar',
        downgrade: 'Reduzir',
        approaching: 'Limites próximos',
        approachingDesc: 'Você está próximo dos seus limites. Considere melhorar seu plano para continuar sem interrupções.'
      },
      calendar: {
        title: 'Calendário',
        subtitle: 'Ver e criar eventos do Google Calendar',
        refresh: 'Atualizar',
        createQuick: 'Criar evento rápido',
        titleLabel: 'Título',
        startIso: 'Início (ISO)',
        endIso: 'Fim (ISO)',
        create: 'Criar',
        upcoming: 'Próximos eventos',
        noEvents: 'Nenhum evento.'
      },
      importExport: {
        title: 'Importar e Exportar',
        subtitle: 'Gerencie seus dados em lote com CSV',
        contacts: 'Contatos',
        companies: 'Empresas',
        importContacts: 'Importar contatos',
        importCompanies: 'Importar empresas',
        uploadCsv: 'Carregar CSV',
        importing: 'Importando...',
        template: 'Modelo',
        exportContacts: 'Exportar contatos',
        exportCompanies: 'Exportar empresas',
        exportCsv: 'Exportar para CSV',
        importComplete: 'Importação completa!',
        importFailed: 'Importação falhou',
        exportComplete: 'Exportação completa!',
        exportFailed: 'Exportação falhou',
        templateDownloaded: 'Modelo baixado',
        uploadHelp: 'Carregue um arquivo CSV para importar múltiplos contatos de uma vez',
        uploadCompaniesHelp: 'Carregue um arquivo CSV para importar múltiplas empresas de uma vez'
      },
      reports: {
        title: 'Relatórios e Análise',
        subtitle: 'Acompanhe o desempenho e decida com dados',
        overview: 'Visão geral',
        sales: 'Funil de vendas',
        contacts: 'Contatos',
        email: 'Desempenho do email',
        export: 'Exportar dados',
        revenueByMonth: 'Receita por mês',
        dealsByStage: 'Negócios por estágio',
        pipelineValue: 'Valor por estágio',
        activity30: 'Atividade (últimos 30 dias)',
        contactsByStatus: 'Contatos por status',
        recentContacts: 'Contatos recentes',
        exportReports: 'Exportar relatórios',
        dealsReport: 'Relatório de negócios',
        contactsReport: 'Relatório de contatos'
      },
      aiInsights: { 
        title: 'Insights de IA', 
        subtitle: 'Análise alimentada por IA de seus relacionamentos comerciais e oportunidades', 
        getInsights: 'Obter Insights de IA', 
        startAnalysis: 'Iniciar Análise de IA', 
        welcome: 'Bem-vindo aos Insights de IA, {name}!', 
        description: 'Obtenha análise alimentada por IA de seus {count} contatos e oportunidades comerciais. Clique em "Obter Insights de IA" acima para iniciar sua análise.', 
        authRequired: 'Autenticação Necessária', 
        authDescription: 'Por favor, faça login para acessar análise alimentada por IA.', 
        goToLogin: 'Ir para Login', 
        businessInsights: 'Insights Comerciais de IA', 
        contactsEngagement: 'Seu CRM mostra {count} contatos com forte potencial de engajamento.', 
        keyInsights: 'Insights Principais:', 
        highPriorityContacts: 'Você tem {count} contatos de alta prioridade que precisam de acompanhamento imediato', 
        activeSectors: 'A maioria dos contatos ativos estão nos setores de tecnologia e consultoria', 
        responseRate: 'A taxa de resposta média é {rate}% - acima do padrão da indústria', 
        aiRecommendations: 'Recomendações de IA:', 
        focusContacts: 'Foque nos {count} contatos com maiores pontuações de engajamento', 
        scheduleFollowups: 'Agende acompanhamentos com prospects que abriram seus últimos emails', 
        leadScoring: 'Resultados de Pontuação de Leads - {count} contatos analizados', 
        hot: 'QUENTE', 
        warm: 'MORNO', 
        cold: 'FRIO', 
        recentEngagement: 'Engajamento recente, Alto valor', 
        multipleInteractions: 'Múltiplas interações, Tomador de decisão', 
        showedInterest: 'Mostrou interesse, Orçamento confirma'
      },
      automations: {
        title: 'Automações',
        subtitle: 'Crie regras simples para automatizar tarefas quando eventos importantes acontecem.',
        createTitle: 'Criar Regra',
        name: 'Nome',
        trigger: 'Gatilho',
        triggerContact: 'Contato: Status Alterado',
        triggerDeal: 'Negócio: Estágio Alterado',
        enabled: 'Habilitado',
        newStatus: 'Novo Status',
        newStage: 'Novo Estágio',
        action: 'Ação',
        createTask: 'Criar Tarefa',
        sendEmail: 'Enviar Email',
        dueDays: 'Dias de Vencimento',
        taskTitle: 'Título da Tarefa',
        priority: 'Prioridade',
        low: 'Baixa',
        medium: 'Média',
        high: 'Alta',
        emailSubject: 'Assunto do Email',
        templateId: 'ID do Modelo (opcional)',
        bodyHtml: 'Corpo (HTML, opcional)',
        createBtn: 'Criar Regra',
        yourRules: 'Suas Regras',
        loading: 'Carregando…',
        noRules: 'Nenhuma regra ainda.',
        enabledBadge: 'HABILITADO',
        disabledBadge: 'DESABILITADO',
        disable: 'Desabilitar',
        enable: 'Habilitar',
        delete: 'Excluir',
        deleteConfirm: 'Excluir esta regra?'
      },
      help: {
        title: 'Ajuda e Suporte',
        searchPlaceholder: 'Buscar artigos de ajuda...',
        quickStart: 'Guia de Início Rápido',
        contactSupport: 'Contatar Suporte',
        learnBasics: 'Aprenda o básico em 5 minutos',
        getHelp: 'Obtenha ajuda da nossa equipe',
        commonQuestions: 'Perguntas Frequentes',
        howToImport: 'Como importo meus contatos?',
        howToCreateDeal: 'Como crio um negócio?',
        canIUpgrade: 'Posso atualizar meu plano?',
        importAnswer: 'Vá para a página Importar/Exportar e carregue seu arquivo CSV. Suportamos formatos padrão da maioria dos CRMs.',
        dealAnswer: 'Use o botão Adicionar Rápido ou vá para a página Negócios e clique em "Adicionar Negócio".',
        upgradeAnswer: 'Sim! Vá para Configurações para ver e alterar seu plano de assinatura a qualquer momento.'
      },
      support: {
        title: 'Contatar Suporte',
        name: 'Nome',
        email: 'Email',
        subject: 'Assunto',
        category: 'Categoria',
        message: 'Mensagem',
        priority: 'Prioridade',
        general: 'Pergunta Geral',
        technical: 'Problema Técnico',
        billing: 'Cobrança e Conta',
        feature: 'Solicitação de Recurso',
        bug: 'Relatório de Bug',
        low: 'Baixa - Consulta geral',
        medium: 'Média - Suporte padrão',
        high: 'Alta - Problema urgente',
        send: 'Enviar Mensagem',
        sending: 'Enviando...',
        success: 'Mensagem enviada com sucesso!',
        error: 'Falha ao enviar mensagem. Tente novamente.'
      },
      tutorial: {
        title: 'Bem-vindo ao Bissa!',
        subtitle: 'Vamos construir relacionamentos significativos juntos',
        ready: 'Pronto para construir conexões como os Bissa?',
        description: 'Este tutorial mostrará como o Bissa.ai capacita seu negócio com ferramentas de construção de relacionamentos que honram a tradição enquanto abraçam a inovação de IA. Aprenda a fortalecer cada conexão.',
        timeRequired: 'Tempo necessário: 5 minutos',
        letsStart: 'Vamos começar!',
        next: 'Próximo',
        previous: 'Anterior',
        finish: 'Finalizar',
        close: 'Fechar'
      }
    },
    onboarding: {
      welcomeTitle: 'Bem-vindo ao Bissa.ai!',
      welcomeSubtitle: 'Vamos configurá-lo para o sucesso',
      roleTitle: 'Qual é o seu papel?',
      roleSubtitle: 'Ajude-nos a personalizar sua experiência',
      companyTitle: 'Conte-nos sobre sua empresa',
      companySubtitle: 'Isso nos ajuda a personalizar recursos para você',
      goalsTitle: 'Qual é o seu objetivo principal?',
      goalsSubtitle: 'Focaremos no que mais importa',
      setupTitle: 'Vamos criar seus primeiros registros',
      setupSubtitle: 'Comece com alguns dados de exemplo',
      completeTitle: 'Tudo pronto!',
      completeSubtitle: 'Bem-vindo à comunidade Bissa.ai',
      step: 'Passo',
      of: 'de',
      back: 'Voltar',
      next: 'Próximo',
      getStarted: 'Começar',
      welcomeMessage: 'Bem-vindo!',
      welcomeDescription: 'Assim como o povo Bissa construiu comunidades prósperas através de relacionamentos fortes, ajudaremos você a construir conexões comerciais duradouras com insights alimentados por IA.',
      timeEstimate: '✨ Isso levará cerca de 2 minutos'
    }
  },
  ha: {
    nav: {
      dashboard: 'Allo',
      aiInsights: 'Hasashen AI',
      contacts: 'Lambobin hulɗa',
      companies: 'Kamfanoni',
      deals: 'Ma\'amaloli',
      marketing: 'Talla',
      tasks: 'Ayyuka',
      reports: 'Rahotanni',
      activities: 'Ayyuka',
      settings: 'Saituna',
      billing: 'Biya',
      automations: 'Na\'ura kai tsaye',
      importExport: 'Shigo/Fitar',
      calendar: 'Kalandar',
    },
    header: {
      searchPlaceholder: 'Nema hulɗa, kamfanoni, ma\'amaloli...',
      notifications: 'Sanarwa',
      profile: 'Bayanin mai amfani',
      settings: 'Saituna',
      logout: 'Fita',
      language: 'Harshe'
    },
    auth: {
      authenticating: 'Ana tabbatarwa...'
    },
    landing: {
      heroBadge: "Inda gadon gado ya hadu da AI",
      heroTitle: "Bissa.ai: CRM mai hankali don Kananan Kasuwanci",
      heroSubtitle: "Sarrafa hulɗa, ma\'amaloli da yakin neman zabe tare da AI. An yi wahayi daga gadon Bissa na kasuwanci bisa aminci.",
      heroCta: "Fara Kyauta",
      heroSecondary: "Duba Demo",
      featuresTitle: "Me ya sa za ka zaɓi Bissa.ai?",
      featuresSubtitle: "Duk abin da kake bukata don bunkasa kasuwancin ka",
      feature1Title: "AI da aka haɗa",
      feature1Desc: "Fahimta mai hankali da sarrafa kansa don inganta tallace-tallace",
      feature2Title: "Sarrafa cikakke",
      feature2Desc: "Hulɗa, kamfanoni, ma\'amaloli da yakin neman zabe a wuri daya",
      feature3Title: "Sauƙi don amfani",
      feature3Desc: "Fasalin da ke da hankali wanda ke aiki a kowane na'ura",
      pricingTitle: "Tsare-tsare masu sauƙi",
      pricingSubtitle: "Zaɓi tsarin da ya dace da kasuwancin ka",
      pricingFree: "Kyauta",
      pricingFreeDesc: "Ya dace don farawa",
      pricingGrowth: "Girma",
      pricingGrowthDesc: "Don kamfanoni masu faɗaɗa",
      pricingOwner: "Mai shi",
      pricingOwnerDesc: "Albarkatu marasa iyaka",
      footerFeatures: 'Albarkatu',
      footerPricing: 'Farashin',
      footerSignUp: 'Yi rajista',
      footerSignIn: 'Shiga',
      footerLegal: 'Doka',
      footerTerms: 'Sharuɗɗan sabis',
      footerPrivacy: 'Manufofin sirri',
      footerCookies: 'Manufofin cookies',
      footerRefunds: 'Manufofin mayar da kuɗi',
      footerDpa: 'Sarrafa bayanai',
      footerInspire: 'An yi wahayi daga gadon Bissa na kasuwanci bisa aminci da gina al\'umma.',
      allPlansNote: 'Duk shirye-shiryen sun haɗa da gwaji na kwanaki 14. Ba a buƙatar kati.',
      fallbackImage: 'Filin aiki na CRM na ƙwararru',
      footerCopyright: '© 2024 Bissa.ai',
      footerMade: 'An yi da ❤️ don kananan kasuwanci',
      footerDescription: 'Gina alaƙa kamar mutanen Bissa na Burkina Faso — ta hanyar aminci, haɗin kai da fahimtar AI waɗanda ke ƙarfafa kowace alaƙar kasuwanci.',
      heroDescription: 'Kamar yadda mutanen Bissa na Burkina Faso suka gina al\'umma masu wadata ta hanyar aminci da haɗin kai, Bissa.ai yana ƙarfafa kasuwancin ku da fahimtar AI waɗanda ke ƙarfafa kowace alaƙa.',
      cardRelationshipDesc: 'Gina alaƙa mai zurfi tare da bayanan lambobin sadarwa masu cike da mahallin, tarihin hulɗa, da bayanan sirri.',
      cardTrustSalesDesc: 'Bi alaƙa ta kowace mataki tare da tsarin gani wanda ke girmama tafiya na gina aminci.',
      cardOutreachDesc: 'Haɗa kai da gaske tare da yaƙin neman zabe na musamman waɗanda ke mutunta bambancin al\'ada da abubuwan da mutum ya fi so.',
      cardInsightsDesc: 'Fahimci ƙarfin alaƙa, tsarin shiga ciki, da haɓakar al\'umma tare da nazarin AI.',
      legacyDescription: 'An san su da iyawarsu na daidaitawa da ruhin kasuwanci, mutanen Bissa sun gina cibiyoyin sadarwar kasuwanci masu wadata a cikin Yammacin Afirka ta hanyar alaƙa mai zurfi, aminci, da musayar al\'ada.',
      bottomPrivacy: 'Sirri',
      bottomTerms: 'Sharuɗɗan',
      bottomRefunds: 'Mayar da kuɗi',
      heroTitlePrefix: 'Gina Dangantaka',
      heroTitleHighlight: ' Kamar Bissa',
      ctaPrimary: 'Fara Kyauta',
      ctaSecondary: 'Duba Abubuwan',
      badgeNoCard: 'Ba a buƙatar katin kuɗi',
      badgeFreeForever: 'Kyauta har abada',
      previewTitle: 'Allo na Bissa.ai',
      previewConnections: 'Haɗin kai',
      previewRevenue: 'Kudaden shiga',
      signIn: 'Shiga',
      featuresTitle: 'An Gina Don Haɗin Kai',
      featuresSubtitle: 'Kayan aiki masu ƙarfi waɗanda ke girmama fasahar gina alaƙa, waɗanda aka inganta da hankali na AI',
      cardRelationship: 'Sarrafa Dangantaka',
      cardTrustSales: 'Tallace-tallace Bisa Aminci',
      cardOutreach: 'Isar da Ma\'ana',
      cardInsights: 'Fahimtar Haɗin Kai',
      pricingTitle: 'Dangantaka Da Ke Girma Tare Da Ku',
      pricingSubtitle: 'Fara gina haɗin kai kyauta. Ku girma yayin da al\'ummarku ke girma. Babu kuɗin kowane mai amfani.',
      free: 'Kyauta',
      growth: 'Girma',
      pro: 'Pro',
      business: 'Kasuwanci',
      mostPopular: 'Mafi Shahara',
      perMonth: '/wata',
      btnGetStarted: 'Fara',
      btnStartTrial: 'Fara Gwajin Kyauta',
      btnContactSales: 'Tuntuɓi Tallace-tallace',
      storyBadge: 'Tarihinmu',
      storyTitle: 'Inda Al\'ada Ke Haduwa Da Sabuwa',
      storyP1: 'Mutanen Bissa na Burkina Faso sun ci gaba da tsawon ƙarni ta hanyar cibiyoyin sadarwa masu ƙarfi, al\'ummomi masu juriya, da dangantaka ta kasuwanci bisa aminci.',
      storyP2: 'A yau, Bissa.ai yana ci gaba da wannan al\'ada — yana ba kamfanoni na zamani ikon gina dangantaka mai dorewa ta hanyar haɗin kai mai hankali da fahimtar AI.',
      legacyTitle: 'Al\'adar Bissa',
      legacyP1: 'An san su da kwarewar su da ruhin kasuwanci, mutanen Bissa sun gina hanyoyin sadarwar kasuwanci masu wadata a duk faɗin Yammacin Afirka ta hanyar dangantaka mai zurfi, aminci, da musayar al\'adu.',
      legacyQuote: 'Kamar yadda Bissa suka ci gaba ta hanyar cibiyoyin sadarwa da haɗin kai masu ƙarfi, Bissa.ai yana ƙarfafa kamfanoni su ci gaba ta hanyar dangantaka mafi wayo.',
      legacyTagline: 'Al\'ada • Sabuwa • Haɗin Kai',
      footerProduct: 'Samfur',
      pricingFeatures: {
        free: {
          contacts: '100 lambobin sadarwa',
          users: 'Mai amfani 1',
          crm: 'CRM na asali',
          tasks: 'Ayyuka 50/wata',
          support: 'Taimakon al\'umma'
        },
        growth: {
          contacts: 'Lambobin sadarwa 5,000',
          users: 'Masu amfani 3',
          tasks: 'Ayyuka 500/wata',
          campaigns: 'Yakin neman zabe 25',
          integration: 'Haɗin imel',
          support: 'Taimakon imel'
        },
        pro: {
          contacts: 'Lambobin sadarwa 10,000',
          users: 'Masu amfani 10',
          everything: 'Komai a cikin Growth',
          api: 'Samun API',
          reports: 'Rahotanni na ci gaba',
          support: 'Taimako na farko'
        },
        business: {
          contacts: 'Lambobin sadarwa marasa iyaka',
          users: 'Masu amfani marasa iyaka',
          everything: 'Komai a cikin Pro',
          whiteLabel: 'Zaɓin white-label',
          integrations: 'Haɗin kai na musamman',
          manager: 'Mai gudanarwa na musamman'
        }
      }
    },
    dashboard: {
      title: 'Allo',
      welcome: 'Barka da zuwa! Ga abin da ke faruwa da kasuwancin ka.',
      totalContacts: 'Jimlar Hulɗa',
      companies: 'Kamfanoni',
      activeDeals: 'Ma\'amaloli Masu Aiki',
      pendingTasks: 'Ayyuka da ke Jiran',
      totalRevenue: 'Jimlar Kudi',
      dealValue: 'Darajar Ma\'amaloli',
      conversionRate: 'Adadin Canji',
      campaignsSent: 'Yakin Neman Zabe da aka Aika',
      quickActions: 'Ayyuka da Sauri',
      addContact: 'Ƙara Sabon Hulɗa',
      createTask: 'Ƙirƙiri Aiki',
      createDeal: 'Ƙirƙiri Ma\'amala',
      newCampaign: 'Sabon Yakin Neman Zabe',
      recentActivity: 'Aiki na Kwanan Nan',
      noRecent: 'Babu ayyuka na kwanan nan',
      due: 'Ya kamata:'
    },
    pages: {
      marketing: {
        title: 'Cibiyar Talla',
        subtitle: 'Ƙirƙiri da sarrafa yakin neman zabe na imel',
        createBtn: 'Ƙirƙiri yakin neman zabe',
        dialogTitle: 'Ƙirƙiri sabon yakin neman zabe',
        nameLabel: 'Sunan yakin neman zabe *',
        subjectLabel: 'Batun imel *',
        contentLabel: 'Abun ciki na imel *',
        statusLabel: 'Matsayi',
        draft: 'Daftarin aiki',
        scheduled: 'An tsara',
        sent: 'An aika',
        cancel: 'Soke',
        noItems: 'Babu yakin neman zabe. Ƙirƙiri yakin neman zabe na farko!',
        sentLabel: 'An aika',
        openRate: 'Adadin buɗewa',
        viewDetails: 'Duba cikakkun bayanai'
      },
      contacts: {
        title: 'Hulɗa',
        subtitle: 'Sarrafa hulɗa da dangantaka',
        addBtn: 'Ƙara hulɗa',
        noItems: 'Babu hulɗa. Ƙirƙiri hulɗa ta farko!'
      },
      companies: {
        title: 'Kamfanoni',
        subtitle: 'Sarrafa asusun kamfanoni',
        addBtn: 'Ƙara kamfani',
        noItems: 'Babu kamfani. Ƙirƙiri kamfani na farko!',
        searchPlaceholder: 'Nema kamfanoni ta suna, yanki, ko masana\'antu...'
      },
      deals: {
        title: 'Funnel na Tallace-tallace',
        subtitle: 'Bi da sarrafa damar tallace-tallace',
        addBtn: 'Ƙara ma\'amala',
        noItems: 'Babu ma\'amaloli'
      },
      tasks: {
        title: 'Ayyuka da Tunatarwa',
        subtitle: 'Tsare kanka kuma kada ka rasa lokaci',
        newBtn: 'Sabon aiki'
      },
      activities: {
        title: 'Ayyuka',
        subtitle: 'Bi duk ayyukan asusun ka',
        empty: 'Har yanzu babu ayyuka. Ƙirƙiri hulɗa, ma\'amaloli ko yakin neman zabe!'
      },
      billing: {
        title: 'Biya da Amfani',
        subtitle: 'Sarrafa biyan kuɗi da amfani',
        currentUsage: 'Amfani na yanzu',
        subscription: 'Cikakkun bayanai na biyan kuɗi',
        features: 'Albarkatu',
        availablePlans: 'Tsare-tsare da ake samu',
        currentPlan: 'Tsarin na yanzu',
        upgrade: 'Haɓaka',
        downgrade: 'Rage',
        approaching: 'Iyaka kusa',
        approachingDesc: 'Kana kusa da iyakokin ka. Ka yi la\'akari da haɓaka tsarin ka don ci gaba ba tare da katsewa ba.'
      },
      calendar: {
        title: 'Kalandar',
        subtitle: 'Duba da ƙirƙira abubuwan Google Calendar',
        refresh: 'Sabunta',
        createQuick: 'Ƙirƙiri abu mai sauri',
        titleLabel: 'Take',
        startIso: 'Fara (ISO)',
        endIso: 'Ƙarshe (ISO)',
        create: 'Ƙirƙiri',
        upcoming: 'Abubuwa masu zuwa',
        noEvents: 'Babu abubuwa.',
        integrations: 'Takammanin Kalandar',
        googleConnected: 'An jira da Kalandar Google',
        googleDisconnected: 'Ba a jira da Kalandar Google',
        connectGoogle: 'Jira Google',
        disconnect: 'Kusa Jirar',
        outlookComingSoon: 'Takammanin Outlook yana zuwa',
        comingSoon: 'Yana zuwa'
      },
      importExport: {
        title: 'Shigo da Fitar',
        subtitle: 'Sarrafa bayanan ka tare da CSV',
        contacts: 'Hulɗa',
        companies: 'Kamfanoni',
        importContacts: 'Shigo da hulɗa',
        importCompanies: 'Shigo da kamfanoni',
        uploadCsv: 'Loda CSV',
        importing: 'Ana shigo...',
        template: 'Samfuri',
        exportContacts: 'Fitar hulɗa',
        exportCompanies: 'Fitar kamfanoni',
        exportCsv: 'Fitar zuwa CSV',
        importComplete: 'Shigo ya cika!',
        importFailed: 'Shigo ya gaza',
        exportComplete: 'Fitar ya cika!',
        exportFailed: 'Fitar ya gaza',
        templateDownloaded: 'An saukar da samfuri',
        uploadHelp: 'Loda fayil na CSV don shigo da hulɗa da yawa lokaci guda',
        uploadCompaniesHelp: 'Loda fayil na CSV don shigo da kamfanoni da yawa lokaci guda'
      },
      reports: {
        title: 'Rahotanni da Nazari',
        subtitle: 'Bi aiki da yanke shawara da bayanai',
        overview: 'Bayanin gaba ɗaya',
        sales: 'Funnel na tallace-tallace',
        contacts: 'Hulɗa',
        email: 'Aikin imel',
        export: 'Fitar bayanai',
        revenueByMonth: 'Kudi a kowane wata',
        dealsByStage: 'Ma\'amaloli a kowane mataki',
        pipelineValue: 'Daraja a kowane mataki',
        activity30: 'Ayyuka (kwanaki 30 na ƙarshe)',
        contactsByStatus: 'Hulɗa ta matsayi',
        recentContacts: 'Hulɗa na kwanan nan',
        exportReports: 'Fitar rahotanni',
        dealsReport: 'Rahoton ma\'amaloli',
        contactsReport: 'Rahoton hulɗa'
      },
      aiInsights: { 
        title: 'Mawazo na AI', 
        subtitle: 'Bincike mai ƙarfi na AI na dangantakar kasuwanci da dama', 
        getInsights: 'Samu Mawazo na AI', 
        startAnalysis: 'Fara Bincike na AI', 
        welcome: 'Barka da zuwa Mawazo na AI, {name}!', 
        description: 'Samu bincike mai ƙarfi na AI na {count} lambobin hulɗa da dama na kasuwanci. Danna "Samu Mawazo na AI" a sama don fara binciken ku.', 
        authRequired: 'Ana Bukatar Tabbatarwa', 
        authDescription: 'Don Allah shiga don samun bincike mai ƙarfi na AI.', 
        goToLogin: 'Je zuwa Shiga', 
        businessInsights: 'Mawazo na Kasuwanci na AI', 
        contactsEngagement: 'CRM naku yana nuna {count} lambobin hulɗa tare da ƙarfin shiga mai ƙarfi.', 
        keyInsights: 'Mawazo Muhimmanci:', 
        highPriorityContacts: 'Kuna da {count} lambobin hulɗa masu fifiko waɗanda ke buƙatar bi da su nan da nan', 
        activeSectors: 'Mafi yawan lambobin hulɗa masu aiki suna cikin sassan fasaha da shawarwari', 
        responseRate: 'Matsakaicin adadin amsa shine {rate}% - sama da ma\'aunin masana\'antu', 
        aiRecommendations: 'Shawarwari na AI:', 
        focusContacts: 'Mayar da hankali kan {count} lambobin hulɗa tare da mafi girman maki na shiga', 
        scheduleFollowups: 'Tsara bi da su tare da masu tsammani waɗanda suka buɗe imel ɗin ku na ƙarshe', 
        leadScoring: 'Sakamakon Maki na Jagora - {count} lambobin hulɗa da aka bincika', 
        hot: 'ZAFI', 
        warm: 'DUMI', 
        cold: 'SANYI', 
        recentEngagement: 'Shiga na kwanan nan, Daraja mai girma', 
        multipleInteractions: 'Hulɗa da yawa, Mai yanke shawara', 
        showedInterest: 'Ya nuna sha\'awa, Kasafin kuɗi ya tabbata' 
      },
      automations: {
        title: 'Adaṣe',
        subtitle: 'Ṣẹda awọn ofin ti o rọrun lati ṣe adaṣe awọn iṣẹ nigba ti awọn iṣẹlẹ pataki ba waye.',
        createTitle: 'Ṣẹda Ofin',
        name: 'Orukọ',
        trigger: 'Gbigbe',
        triggerContact: 'Olubasọrọ: Ipo Ti Yipada',
        triggerDeal: 'Iṣowo: Ipele Ti Yipada',
        enabled: 'Mu ṣiṣẹ',
        newStatus: 'Ipo Tuntun',
        newStage: 'Ipele Tuntun',
        action: 'Iṣẹ',
        createTask: 'Ṣẹda Iṣẹ',
        sendEmail: 'Fi Imẹẹli Ranṣẹ',
        dueDays: 'Awọn ọjọ Ti O To',
        taskTitle: 'Akọle Iṣẹ',
        priority: 'Ipataki',
        low: 'Kere',
        medium: 'Aarin',
        high: 'Ga',
        emailSubject: 'Akọle Imẹẹli',
        templateId: 'ID Awoṣe (ayẹ)',
        bodyHtml: 'Ara (HTML, ayẹ)',
        createBtn: 'Ṣẹda Ofin',
        yourRules: 'Awọn Ofin Rẹ',
        loading: 'N gba…',
        noRules: 'Ko si awọn ofin sibẹsibẹ.',
        enabledBadge: 'MU ṢIṢẸ',
        disabledBadge: 'KO MU ṢIṢẸ',
        disable: 'Mu Duro',
        enable: 'Mu Ṣiṣẹ',
        delete: 'Paarẹ',
        deleteConfirm: 'Paarẹ ofin yii?'
      },
      help: {
        title: 'Msaada na Usaidizi',
        searchPlaceholder: 'Tafuta makala za msaada...',
        quickStart: 'Mwongozo wa Kuanza Haraka',
        contactSupport: 'Wasiliana na Usaidizi',
        learnBasics: 'Jifunze mambo ya msingi kwa dakika 5',
        getHelp: 'Pata msaada kutoka kwa timu yetu',
        commonQuestions: 'Maswali ya Kawaida',
        howToImport: 'Je, ninawezaje kuagiza wasiliani wangu?',
        howToCreateDeal: 'Je, ninawezaje kuunda mkakati?',
        canIUpgrade: 'Je, naweza kuboresha mpango wangu?',
        importAnswer: 'Nenda kwenye ukurasa wa Kuagiza/Kusafirisha na upakia faili yako ya CSV. Tunasaidia miundo ya kawaida kutoka kwa CRMs nyingi.',
        dealAnswer: 'Tumia kitufe cha Ongeza Haraka au nenda kwenye ukurasa wa Mikakati na bonyeza "Ongeza Mkakati".',
        upgradeAnswer: 'Ndiyo! Nenda kwenye Mipangilio ili kuona na kubadilisha mpango wako wa usajili wakati wowote.'
      },
      support: {
        title: 'Wasiliana na Usaidizi',
        name: 'Jina',
        email: 'Barua Pepe',
        subject: 'Mada',
        category: 'Kategoria',
        message: 'Ujumbe',
        priority: 'Kipaumbele',
        general: 'Swali la Jumla',
        technical: 'Tatizo la Kiufundi',
        billing: 'Ulipaji na Akaunti',
        feature: 'Ombi la Kipengele',
        bug: 'Ripoti ya Hitilafu',
        low: 'Chini - Uchunguzi wa jumla',
        medium: 'Kati - Usaidizi wa kawaida',
        high: 'Juu - Tatizo la haraka',
        send: 'Tuma Ujumbe',
        sending: 'Inatumwa...',
        success: 'Ujumbe umetumwa kwa mafanikio!',
        error: 'Imeshindwa kutuma ujumbe. Tafadhali jaribu tena.'
      },
      tutorial: {
        title: 'Karibu Bissa!',
        subtitle: 'Tujenge mahusiano ya maana pamoja',
        ready: 'Tayari kujenga miunganisho kama Bissa?',
        description: 'Mafunzo haya yatakuonyesha jinsi Bissa.ai inawezesha biashara yako kwa vifaa vya kujenga mahusiano ambavyo vinahonoi jadi wakati vinakumbatia ubunifu wa AI. Jifunze kuimarisha kila muunganisho.',
        timeRequired: 'Muda unaohitajika: dakika 5',
        letsStart: 'Tuanze!',
        next: 'Ifuatayo',
        previous: 'Iliyotangulia',
        finish: 'Maliza',
        close: 'Funga'
      }
    },
    onboarding: {
      welcomeTitle: 'Barka da zuwa Bissa.ai!',
      welcomeSubtitle: 'Bari mu saita ka don nasara',
      roleTitle: 'Menene matsayin ka?',
      roleSubtitle: 'Taimaka mana mu keɓance abubuwan ka',
      companyTitle: 'Faɗa mana game da kamfanin ka',
      companySubtitle: 'Wannan yana taimaka mana mu keɓance albarkatu gare ka',
      goalsTitle: 'Menene makasudin ka na farko?',
      goalsSubtitle: 'Za mu mai da hankali kan abin da ya fi muhimmanci',
      setupTitle: 'Bari mu ƙirƙiri bayanan ka na farko',
      setupSubtitle: 'Fara da wasu bayanai na misali',
      completeTitle: 'Duk abu ya shirya!',
      completeSubtitle: 'Barka da zuwa al\'ummar Bissa.ai',
      step: 'Mataki',
      of: 'na',
      back: 'Komawa',
      next: 'Na gaba',
      getStarted: 'Fara',
      welcomeMessage: 'Barka da zuwa!',
      welcomeDescription: 'Kamar yadda mutanen Bissa suka gina al\'ummomi masu ci gaba ta hanyar dangantaka mai ƙarfi, za mu taimake ka gina hulɗar kasuwanci mai dorewa tare da fahimta ta AI.',
      timeEstimate: '✨ Wannan zai ɗauki mintuna 2'
    }
  },
  am: {
    nav: {
      dashboard: 'ዳሽቦርድ',
      aiInsights: 'AI ግንዛቤ',
      contacts: 'እውቂያዎች',
      companies: 'ኩባንያዎች',
      deals: 'ስምምነቶች',
      marketing: 'ማርኬቲንግ',
      tasks: 'ተግባራት',
      reports: 'ሪፖርቶች',
      activities: 'እንቅስቃሴዎች',
      settings: 'መቼቶች',
      billing: 'ክፍያ',
      automations: 'ማቀናበር በራስ',
      importExport: 'አስገባ/አስወጣ',
      calendar: 'የቀን መቁጠሪያ',
    },
    header: { 
      searchPlaceholder: 'ፈልግ እውቂያዎች፣ ኩባንያዎች፣ ስምምነቶች...',
      notifications: 'ማስታወቂያዎች',
      profile: 'መገለጫ',
      settings: 'መቼቶች',
      logout: 'ውጣ',
      language: 'ቋንቋ'
    },
    auth: {
      authenticating: 'በማረጋገጥ ላይ...'
    },
    landing: {
      heroBadge: 'እዚህ የተለዋዋጭ ርስት ከAI ጋር ይገናኛል',
      heroTitlePrefix: 'ግንኙነቶችን ይገንቡ',
      heroTitleHighlight: ' እንደ ቢሳ',
      heroTitle: 'Bissa.ai: ለትናንሽ ኩባንያዎች የተስተካከለ CRM',
      heroSubtitle: 'እውቂያዎች፣ ስምምነቶች እና ዘመቻዎችን በAI ያስተዳድሩ። ከተለዋዋጭ የቢሳ ንግድ ታሪክ ተነስተው።',
      heroCta: 'ነፃ ጀምር',
      heroSecondary: 'ማሳያ ተመልከት',
      ctaPrimary: 'ነፃ ጀምር',
      ctaSecondary: 'ባህሪያትን ይመልከቱ',
      featuresTitle: 'ለምን Bissa.ai ይመርጣሉ?',
      featuresSubtitle: 'ንግድዎን ለማዳበር የሚያስፈልጉት ሁሉ',
      feature1Title: 'የተዋሃደ AI',
      feature1Desc: 'ለሽያጭ ማሻሻያ የተስተካከሉ ግንዛቤዎች እና ራስ-ሰር',
      feature2Title: 'ሙሉ አስተዳደር',
      feature2Desc: 'እውቂያዎች፣ ኩባንያዎች፣ ስምምነቶች እና ዘመቻዎች በአንድ ቦታ',
      feature3Title: 'ለመጠቀም ቀላል',
      feature3Desc: 'በማንኛውም መሳሪያ ላይ የሚሰራ የተስተካከለ በይነገጽ',
      pricingTitle: 'ቀላል እቅዶች',
      pricingSubtitle: 'ለንግድዎ ተስማሚ እቅድ ይምረጡ',
      pricingFree: 'ነፃ',
      pricingFreeDesc: 'ለመጀመር ተስማሚ',
      pricingGrowth: 'እድገት',
      pricingGrowthDesc: 'ለሚስፋፉ ኩባንያዎች',
      pricingOwner: 'ባለቤት',
      pricingOwnerDesc: 'ያልተገደቡ ባህሪያት',
      footerFeatures: 'ባህሪያት',
      footerPricing: 'ዋጋዎች',
      footerSignUp: 'ተመዝግብ',
      footerSignIn: 'ግባ',
      footerLegal: 'ሕጋዊ',
      footerTerms: 'የአገልግሎት ውሎች',
      footerPrivacy: 'የግላዊነት ፖሊሲ',
      footerCookies: 'የኩኪ ፖሊሲ',
      footerRefunds: 'የመመለሻ ፖሊሲ',
      footerDpa: 'የውሂብ አስተዳደር',
      footerInspire: 'ከተለዋዋጭ የቢሳ ንግድ ታሪክ ተነስተው በተለዋዋጭ እና የማህበረሰብ ግንባታ።',
      allPlansNote: 'ሁሉም እቅዶች 14 ቀን ነፃ ሙከራ ያካተቱ። ካርድ አያስፈልግም።',
      fallbackImage: 'የCRM ሙያዊ የስራ ቦታ',
      storyBadge: 'የእኛ ታሪክ',
      storyTitle: 'እዚህ ባህል ከፍጥነት ጋር ይገናኛል',
      storyP1: 'የቢሳ ሕዝብ በቡርኪና ፋሶ ለዘመናት በጠንካራ ኔትዎርኮች፣ በመቋቋም የሚችሉ ማህበረሰቦች እና በመተማመን የተመሰረቱ ግንኙነቶች በንግድ እና በንግድ ውስጥ ተስፋ ተሰጥቷል።',
      storyP2: 'ዛሬ፣ Bissa.ai ይህንን ባህል ይቀጥላል — ዘመናዊ ንግዶችን በብልህ ግንኙነት እና በAI የሚመራ ግንዛቤ በኩል የሚቆይ ግንኙነቶችን ለመገንባት ያስችላል።',
      legacyTitle: 'የቢሳ ባህል',
      legacyP1: 'በመላመድ እና በኢንተርፕራይዝ መንፈስ ታዋቂዎች፣ የቢሳ ሕዝብ በጥልቅ ግንኙነቶች፣ በመተማመን እና በባህላዊ ልውውጥ በኩል በምዕራብ አፍሪካ ሁሉ የተሳካ የንግድ ኔትወርኮችን ገንብተዋል።',
      legacyQuote: 'እንደ ቢሳ በጠንካራ ኔትዎርኮች እና ግንኙነቶች በኩል እንደተስፋ ተሰጥቷል፣ Bissa.ai ንግዶችን በብልህ ግንኙነቶች በኩል እንዲተስፋ ያስችላል።',
      buildRelationships: 'ግንኙነቶችን እንደ ቢሳ ይገንቡ?',
      btnGetStarted: 'ነፃ ጀምር',
      btnStartTrial: 'ነፃ ሙከራ ጀምር',
      btnContactSales: 'ሽያጭ አግኝ',
      free: 'ነፃ',
      growth: 'እድገት',
      pro: 'ፕሮ',
      business: 'ንግድ',
      mostPopular: 'በጣም ተወዳጅ',
      perMonth: '/ወር',
      footerCopyright: '© 2024 Bissa.ai',
      footerMade: 'በ ❤️ ለትናንሽ ኩባንያዎች ተሰራ',
      footerDescription: 'እንደ ቢሳ ሕዝብ በቡርኪና ፋሶ ያሉ ሕዝቦች ያሉ ግንኙነቶችን ይገንቡ — በመተማመን፣ በግንኙነት እና በAI የሚመራ ግንዛቤ በኩል እያንዳንዱን የንግድ ግንኙነት ያጠናክራል።',
      heroDescription: 'እንደ ቢሳ ሕዝብ በቡርኪና ፋሶ ያሉ ሕዝቦች በመተማመን እና በግንኙነት በኩል የሚያድጉ ማህበረሰቦችን እንደገነቡ፣ Bissa.ai ንግድዎን በAI የሚመራ ግንዛቤ በኩል ያጠናክራል።',
      cardRelationshipDesc: 'ከተለያዩ የግንኙነት መገለጫዎች፣ የግንኙነት ታሪክ እና የግል ማስታወሻዎች ጋር የበለጠ ጥልቅ ግንኙነቶችን ይገንቡ።',
      cardTrustSalesDesc: 'በመተማመን ማጠናከር ጉዞ የሚከበር የማየት ፓይፕላይን በኩል በእያንዳንዱ ደረጃ ግንኙነቶችን ይከታተሉ።',
      cardOutreachDesc: 'የባህል ልዩነትን እና የግል ምርጫዎችን የሚከበር የተበጀ ዘመቻዎች በኩል በእውነተኛነት ይገናኙ።',
      cardInsightsDesc: 'የግንኙነት ጥንካሬ፣ የግንኙነት ተግባሮች እና የማህበረሰብ እድገትን በAI የሚመራ ትንተና በኩል ይረዱ።',
      legacyDescription: 'በማስተካከል እና በኢንተርፕራይዝ መንፈስ የሚታወቁ፣ የቢሳ ሕዝብ በምዕራብ አፍሪካ በኩል በጥልቅ ግንኙነቶች፣ በመተማመን እና በባህላዊ ልውውጥ በኩል የሚያድጉ የንግድ ኔትዎርኮችን ገነቡ።',
      bottomPrivacy: 'ግላዊነት',
      bottomTerms: 'ውሎች',
      bottomRefunds: 'መመለሻ',
      badgeNoCard: 'ምንም ካርድ አይጠበቅም',
      badgeFreeForever: 'ለዘለአለም ነፃ',
      previewTitle: 'የBissa.ai ዳሽቦርድ',
      previewConnections: 'ግንኙነቶች',
      previewRevenue: 'ገቢ',
      signIn: 'ግባ',
      featuresTitle: 'ለግንኙነት ተሰርቷል',
      featuresSubtitle: 'የግንኙነት ግንባታ ጥበብን የሚያከብሩ ኃይለኛ መሳሪያዎች፣ በAI እውቀት የተሻሻሉ',
      cardRelationship: 'የግንኙነት አስተዳደር',
      cardTrustSales: 'በመተማመን ያሉ ሽያጭ',
      cardOutreach: 'ትርጉም ያለው ተደራሽነት',
      cardInsights: 'የግንኙነት ግንዛቤዎች',
      pricingTitle: 'ከእርስዎ ጋር የሚያድጉ ግንኙነቶች',
      pricingSubtitle: 'ግንኙነቶችን በነፃ መገንባት ይጀምሩ። ማህበረሰብዎ እያደገ ሲሄድ ያድጉ። በአንድ ተጠቃሚ ክፍያ የለም።',
      legacyTagline: 'ቅርስ • ፈጠራ • ግንኙነት',
      footerProduct: 'ምርት',
      pricingFeatures: {
        free: {
          contacts: '100 አድራሻዎች',
          users: '1 ተጠቃሚ',
          crm: 'መሰረታዊ CRM',
          tasks: '50 ተግባራት/ወር',
          support: 'የማህበረሰብ ድጋፍ'
        },
        growth: {
          contacts: '5,000 አድራሻዎች',
          users: '3 ተጠቃሚዎች',
          tasks: '500 ተግባራት/ወር',
          campaigns: '25 ዘመቻዎች',
          integration: 'ኢሜል ውህደት',
          support: 'ኢሜል ድጋፍ'
        },
        pro: {
          contacts: '10,000 አድራሻዎች',
          users: '10 ተጠቃሚዎች',
          everything: 'ሁሉም ነገር በመጨመር',
          api: 'API መዳረሻ',
          reports: 'የላቀ ሪፖርቶች',
          support: 'የቅድም ተርፍ ድጋፍ'
        },
        business: {
          contacts: 'ያልተገደበ አድራሻዎች',
          users: 'ያልተገደበ ተጠቃሚዎች',
          everything: 'ሁሉም ነገር በPro',
          whiteLabel: 'የነጭ መለያ አማራጭ',
          integrations: 'ብጁ ውህደቶች',
          manager: 'የተሰጠ አስተዳዳሪ'
        }
      }
    },
    dashboard: {
      title: 'ዳሽቦርድ',
      welcome: 'እንኳን ደህና መጡ! በንግድዎ ውስጥ የሚከሰተውን ይመልከቱ።',
      totalContacts: 'ጠቅላላ እውቂያዎች',
      companies: 'ኩባንያዎች',
      activeDeals: 'ንቁ ስምምነቶች',
      pendingTasks: 'በጥበቃ ላይ ያሉ ተግባራት',
      totalRevenue: 'ጠቅላላ ገቢ',
      dealValue: 'የስምምነት ዋጋ',
      conversionRate: 'የመቀየሪያ መጠን',
      campaignsSent: 'የተላኩ ዘመቻዎች',
      quickActions: 'ፈጣን ተግባራት',
      addContact: 'አዲስ እውቂያ ያክሉ',
      createTask: 'ተግባር ይፍጠሩ',
      createDeal: 'ስምምነት ይፍጠሩ',
      newCampaign: 'አዲስ ዘመቻ',
      recentActivity: 'የቅርብ ጊዜ እንቅስቃሴ',
      noRecent: 'የቅርብ ጊዜ እንቅስቃሴ የለም',
      due: 'መጠበቅ:'
    },
    pages: {
      marketing: {
        title: 'የማርኬቲንግ ማዕከል',
        subtitle: 'የኢሜይል ዘመቻዎችን ይፍጠሩ እና ያስተዳድሩ',
        createCampaign: 'ዘመቻ ይፍጠሩ',
        campaignName: 'የዘመቻ ስም',
        subject: 'ርዕስ',
        content: 'ይዘት',
        sendTo: 'ወደ ላክ',
        schedule: 'ጊዜ ያስቀምጡ',
        send: 'ላክ',
        campaigns: 'ዘመቻዎች',
        noCampaigns: 'ዘመቻ የለም',
        createFirst: 'የመጀመሪያውን ዘመቻዎን ይፍጠሩ!'
      },
      contacts: {
        title: 'እውቂያዎች',
        subtitle: 'የንግድ ግንኙነቶችዎን ያስተዳድሩ',
        addBtn: 'እውቂያ ያክሉ',
        noItems: 'እውቂያ አልተገኘም። የመጀመሪያውን እውቂያዎን ይፍጠሩ!',
        searchPlaceholder: 'በስም፣ ኢሜይል፣ ወይም ኩባንያ እውቂያዎችን ይፈልጉ...'
      },
      companies: {
        title: 'ኩባንያዎች',
        subtitle: 'የኩባንያ አካዎንቶችዎን ያስተዳድሩ',
        addBtn: 'ኩባንያ ያክሉ',
        noItems: 'ኩባንያ አልተገኘም። የመጀመሪያውን ኩባንያዎን ይፍጠሩ!',
        searchPlaceholder: 'በስም፣ ጎራ፣ ወይም ኢንዱስትሪ ኩባንያዎችን ይፈልጉ...'
      },
      deals: {
        title: 'የሽያጭ ፓይፕላይን',
        subtitle: 'ስምምነቶችዎን ይከታተሉ እና ያስተዳድሩ',
        addBtn: 'ስምምነት ያክሉ',
        noItems: 'ስምምነት የለም'
      },
      tasks: {
        title: 'ተግባራት እና ማስታወሻዎች',
        subtitle: 'ያስተዳድሩ እና ቀን አያምሉ',
        newBtn: 'አዲስ ተግባር'
      },
      activities: {
        title: 'የእንቅስቃሴ ዝርዝር',
        subtitle: 'በመለያዎ ውስጥ ያሉ ሁሉንም እንቅስቃሴዎች ይከታተሉ',
        empty: 'እንቅስቃሴ የለም። በእውቂያዎች፣ ስምምነቶች ወይም ዘመቻዎች መፍጠር ይጀምሩ!'
      },
      billing: {
        title: 'ክፍያ እና አጠቃቀም',
        subtitle: 'የምዝገባዎን ያስተዳድሩ እና አጠቃቀምን ይከታተሉ',
        currentUsage: 'የአሁኑ አጠቃቀም',
        subscription: 'የምዝገባ ዝርዝሮች',
        features: 'ባህሪያት',
        availablePlans: 'የሚገኙ እቅዶች',
        currentPlan: 'የአሁኑ እቅድ',
        upgrade: 'አሻሽል',
        downgrade: 'ቀንስ',
        approaching: 'ወሰኖች ይቀርባሉ',
        approachingDesc: 'የእቅድዎ ወሰኖች ይቀርባሉ። ያለማቋረጥ ለመቀጠል ስለ ማሻሻያ አስቡ።'
      },
      calendar: {
        title: 'የቀን መቁጠሪያ',
        subtitle: 'የGoogle የቀን መቁጠሪያ ክስተቶችን ይመልከቱ እና ይፍጠሩ',
        refresh: 'አደስ',
        createQuick: 'ፈጣን ክስተት ይፍጠሩ',
        titleLabel: 'ርዕስ',
        startIso: 'መጀመሪያ (ISO)',
        endIso: 'መጨረሻ (ISO)',
        create: 'ይፍጠሩ',
        upcoming: 'የሚመጡ ክስተቶች',
        noEvents: 'ክስተት የለም።',
        integrations: 'የቀን መቁጠሪያ መገናኛዎች',
        googleConnected: 'ከGoogle ቀን መቁጠሪያ ተገናኝቷል',
        googleDisconnected: 'ከGoogle ቀን መቁጠሪያ አልተገናኝም',
        connectGoogle: 'Google ያገናኙ',
        disconnect: 'መገናኛውን ያሰናክሩ',
        outlookComingSoon: 'Outlook መገናኛው በጣም ቀረበ',
        comingSoon: 'በጣም ቀረበ'
      },
      importExport: {
        title: 'አስገባ/አስወጣ',
        subtitle: 'በCSV ፋይሎች የውሂብዎን በጅምላ ያስተዳድሩ',
        contacts: 'እውቂያዎች',
        companies: 'ኩባንያዎች',
        importContacts: 'እውቂያዎችን አስገባ',
        importCompanies: 'ኩባንያዎችን አስገባ',
        uploadCsv: 'CSV ይጭኑ',
        importing: 'በማስገባት ላይ...',
        template: 'አብነት',
        exportContacts: 'እውቂያዎችን አስወጣ',
        exportCompanies: 'ኩባንያዎችን አስወጣ',
        exportCsv: 'CSV አስወጣ',
        importComplete: 'አስገባት ተጠናቋል!',
        importFailed: 'አስገባት አልተሳካም',
        exportComplete: 'አስወጣት ተጠናቋል!',
        exportFailed: 'አስወጣት አልተሳካም',
        templateDownloaded: 'አብነት ተወርዷል',
        uploadHelp: 'በአንድ ጊዜ ብዙ እውቂያዎችን ለማስገባት CSV ፋይል ይጭኑ',
        uploadCompaniesHelp: 'በአንድ ጊዜ ብዙ ኩባንያዎችን ለማስገባት CSV ፋይል ይጭኑ'
      },
      reports: {
        title: 'ሪፖርቶች እና ትንተና',
        subtitle: 'አፈጻጸምን ይከታተሉ እና በውሂብ የተመሰረቱ ውሳኔዎች ይስሩ',
        overview: 'አጠቃላይ እይታ',
        sales: 'የሽያጭ ፓይፕላይን',
        contacts: 'እውቂያዎች',
        email: 'የኢሜይል አፈጻጸም',
        export: 'ውሂብ አስወጣ',
        revenueByMonth: 'በወር ገቢ',
        dealsByStage: 'በደረጃ ስምምነቶች',
        pipelineValue: 'በደረጃ ዋጋ',
        activity30: 'እንቅስቃሴ (የመጨረሻው 30 ቀናት)',
        contactsByStatus: 'በሁኔታ እውቂያዎች',
        recentContacts: 'የቅርብ ጊዜ እውቂያዎች',
        exportReports: 'ሪፖርቶች አስወጣ',
        dealsReport: 'የስምምነት ሪፖርት',
        contactsReport: 'የእውቂያ ሪፖርት'
      },
      aiInsights: { 
        title: 'የAI ግንዛቤ', 
        subtitle: 'በAI የሚመራ የንግድ ግንኙነቶችዎ እና እድሎች ትንተና', 
        getInsights: 'የAI ግንዛቤ ያግኙ', 
        startAnalysis: 'የAI ትንተና ይጀምሩ', 
        welcome: 'ወደ AI ግንዛቤ እንኳን ደህና መጡ, {name}!', 
        description: 'የ{count} እውቂያዎችዎ እና የንግድ እድሎች ዎ በAI የሚመራ ትንተና ያግኙ። ትንተናዎን ለመጀመር ከላይ "የAI ግንዛቤ ያግኙ" የሚለውን ይጫኑ።', 
        authRequired: 'ማረጋገጫ ያስፈልጋል', 
        authDescription: 'እባክዎን በAI የሚመራ ትንተናን ለመድረስ ይግቡ።', 
        goToLogin: 'ወደ መግቢያ ይሂዱ', 
        businessInsights: 'የንግድ AI ግንዛቤዎች', 
        contactsEngagement: 'የእርስዎ CRM {count} እውቂያዎችን ከጠንካራ ተሳትፎ አቅም ጋር ያሳያል።', 
        keyInsights: 'ቁልፍ ግንዛቤዎች:', 
        highPriorityContacts: 'ወዲያውኑ ክትትል የሚያስፈልጋቸው {count} ከፍተኛ ቅድሚያ የሚሰጣቸው እውቂያዎች አሉዎት', 
        activeSectors: 'በጣም ንቁ እውቂያዎች በቴክኖሎጂ እና የማማከር ዘርፎች ውስጥ ናቸው', 
        responseRate: 'አማካይ የምላሽ መጠን {rate}% ነው - ከኢንዱስትሪ መደበኛ በላይ', 
        aiRecommendations: 'የAI ምክሮች:', 
        focusContacts: 'በከፍተኛ የተሳትፎ ነጥቦች ላይ ያሉትን {count} እውቂያዎች ያተኩሩ', 
        scheduleFollowups: 'የመጨረሻ ኢሜይሎችዎን ከከፈቱ ተስፋዎች ጋር ክትትሎችን ያቅዱ', 
        leadScoring: 'የእድል ውጤቶች - {count} እውቂያዎች ተተንተዋል', 
        hot: 'ሙቅ', 
        warm: 'ሞቅታ', 
        cold: 'ቀዝቃዛ', 
        recentEngagement: 'የቅርብ ጊዜ ተሳትፎ, ከፍተኛ ዋጋ', 
        multipleInteractions: 'በርካታ ግንኙነቶች, ወሳኝ', 
        showedInterest: 'ፍላጎት አሳይቷል, በጀት ተረጋግጧል' 
      },
      automations: {
        title: 'ማቀናበር በራስ',
        subtitle: 'ዋና ክስተቶች ሲከሰቱ ተግባራትን ለማቀናበር ቀላል ደንቦች ይፍጠሩ።',
        createTitle: 'ደንብ ፍጠር',
        name: 'ስም',
        trigger: 'ማስነሻ',
        triggerContact: 'እውቂያ: ሁኔታ ተቀይሯል',
        triggerDeal: 'ስምምነት: ደረጃ ተቀይሯል',
        enabled: 'ነቅቷል',
        newStatus: 'አዲስ ሁኔታ',
        newStage: 'አዲስ ደረጃ',
        action: 'ተግባር',
        createTask: 'ተግባር ፍጠር',
        sendEmail: 'ኢሜይል ላክ',
        dueDays: 'የመጨረሻ ቀኖች',
        taskTitle: 'የተግባሩ ርዕስ',
        priority: 'ቅድሚያ',
        low: 'ዝቅተኛ',
        medium: 'መካከለኛ',
        high: 'ከፍተኛ',
        emailSubject: 'የኢሜይል ርዕስ',
        templateId: 'የቅጥ መለያ (አማራጭ)',
        bodyHtml: 'ይዘት (HTML፣ አማራጭ)',
        createBtn: 'ደንብ ፍጠር',
        yourRules: 'ደንቦችዎ',
        loading: 'በመጫን ላይ…',
        noRules: 'እስካሁን ደንቦች የሉም።',
        enabledBadge: 'ነቅቷል',
        disabledBadge: 'ተሰናክሏል',
        disable: 'አሰናክል',
        enable: 'አንቅ',
        delete: 'ሰርዝ',
        deleteConfirm: 'ይህን ደንብ ሰርዝ?'
      },
      help: {
        title: 'እርዳታ እና ድጋፍ',
        searchPlaceholder: 'የእርዳታ ጽሁፎችን ፈልግ...',
        quickStart: 'ፈጣን ጅምር መመሪያ',
        contactSupport: 'ድጋፍን ያግኙ',
        learnBasics: 'መሰረታዊ ነገሮችን በ5 ደቂቃ ይማሩ',
        getHelp: 'ከቡድናችን እርዳታ ያግኙ',
        commonQuestions: 'የተለመዱ ጥያቄዎች',
        howToImport: 'እውቂያዎቼን እንዴት አስገባለሁ?',
        howToCreateDeal: 'ስምምነት እንዴት መፍጠር እችላለሁ?',
        canIUpgrade: 'እቅዴን ማሻሻል እችላለሁ?',
        importAnswer: 'ወደ አስገባ/አስወጣ ገጽ ይሂዱ እና የCSV ፋይልዎን ይጫኑ። ከብዙ CRMs መደበኛ ቅርጸቶችን እንደግፋለን።',
        dealAnswer: 'ፈጣን ጨምር ቁልፍን ይጠቀሙ ወይም ወደ ስምምነቶች ገጽ ይሂዱ እና "ስምምነት ያክሉ" ይጫኑ።',
        upgradeAnswer: 'አዎ! የምዝገባ እቅድዎን በማንኛውም ጊዜ ለማየት እና ለመቀየር ወደ መቼቶች ይሂዱ።'
      },
      support: {
        title: 'ድጋፍን ያግኙ',
        name: 'ስም',
        email: 'ኢሜይል',
        subject: 'ርዕስ',
        category: 'ምድብ',
        message: 'መልእክት',
        priority: 'ቅድሚያ',
        general: 'አጠቃላይ ጥያቄ',
        technical: 'ቴክኒካዊ ችግር',
        billing: 'ክፍያ እና መለያ',
        feature: 'የባህሪ ጥያቄ',
        bug: 'የስህተት ሪፖርት',
        low: 'ዝቅተኛ - አጠቃላይ ጥያቄ',
        medium: 'መካከለኛ - መደበኛ ድጋፍ',
        high: 'ከፍተኛ - አስቸኳይ ችግር',
        send: 'መልእክት ላክ',
        sending: 'በመላክ ላይ...',
        success: 'መልእክት በተሳካ ሁኔታ ተልኳል!',
        error: 'መልእክት መላክ አልተሳካም። እባክዎ እንደገና ይሞክሩ።'
      },
      tutorial: {
        title: 'እንኳን ወደ Bissa በደህና መጡ!',
        subtitle: 'አብረን ትርጉም ያላቸውን ግንኙነቶች እንገንባ',
        ready: 'እንደ ቢሳ ግንኙነቶችን ለመገንባት ዝግጁ ነዎት?',
        description: 'ይህ ትምህርት Bissa.ai ባህልን እያከበረ AI ፈጠራን እንዴ ተቀበለው የንግድዎን እንደሚያጠናክር የግንኙነት ግንባታ መሳሪያዎችን ያሳያል። እያንዳንዱን ግንኙነት ለማጠናከር ይማሩ።',
        timeRequired: 'የሚፈለግ ጊዜ: 5 ደቂቃ',
        letsStart: 'እንጀምር!',
        next: 'ቀጣይ',
        previous: 'ቀዳሚ',
        finish: 'ጨርስ',
        close: 'ዝጋ'
      }
    },
    onboarding: {
      welcomeTitle: 'እንኳን ወደ Bissa.ai በደህና መጡ!',
      welcomeSubtitle: 'ለስኬት እናዘጋጅዎት',
      roleTitle: 'ሚናዎ ምንድን ነው?',
      roleSubtitle: 'ልምድዎን ለማብጠር ይረዱን',
      companyTitle: 'ስለ ኩባንያዎ ይንገሩን',
      companySubtitle: 'ይህ ባህሪያትን ለእርስዎ ለማብጠር ይረዳናል',
      goalsTitle: 'ዋና ግብዎ ምንድን ነው?',
      goalsSubtitle: 'በጣም አስፈላጊ ባህሪያት ላይ እናተተኩራለን',
      setupTitle: 'የመጀመሪያ ምዝግብሮችዎን እንፍጠር',
      setupSubtitle: 'በናሙና ውሂብ ይጀምሩ',
      completeTitle: 'ሁሉም ዝግጁ ነው!',
      completeSubtitle: 'ወደ Bissa.ai ማህበረሰብ እንኳን ደህና መጡ',
      step: 'ደረጃ',
      of: 'ከ',
      back: 'ተመለስ',
      next: 'ቀጣይ',
      getStarted: 'ጀምር',
      welcomeMessage: 'እንኳን ደህና መጡ!',
      welcomeDescription: 'እንደ የቢሳ ሕዝብ በጠንካራ ግንኙነቶች ተስፋ የሚሰጡ ማህበረሰቦችን እንደገነቡ፣ በAI የተመሰረቱ ግንዛቤዎች የሚቆዩ የንግድ ግንኙነቶችን ለመገንባት እንረዳዎታለን።',
      timeEstimate: '✨ ይህ በግምት 2 ደቂቃ ይወስዳል'
    }
  },
  yo: {
    nav: {
      dashboard: 'Dasibodu',
      aiInsights: 'Ìmọ̀ràn AI',
      contacts: 'Olubasọrọ',
      companies: 'Ilé iṣẹ́',
      deals: 'Ìdúnàdé',
      marketing: 'Ọjà',
      tasks: 'Àwọn iṣẹ́',
      reports: 'Ìròyìn',
      activities: 'Àwọn ìṣe',
      settings: 'Ètò',
      billing: 'Ìsanwó',
      automations: 'Adaṣe',
      importExport: 'Wọlé/Jádè',
      calendar: 'Kálẹ́ńdà',
    },
    header: { 
      searchPlaceholder: 'Ṣàwárí olubasọrọ, ilé iṣẹ́, ìdúnàdé...',
      notifications: 'Àkọsílẹ̀',
      profile: 'Profaili',
      settings: 'Ètò',
      logout: 'Jáde',
      language: 'Èdè'
    },
    auth: {
      authenticating: 'N ṣe ìjẹrisi...'
    },
    landing: {
      heroBadge: 'Nibi ni ọjà ti o yipada pẹlu AI',
      heroTitle: 'Bissa.ai: CRM ti o yẹ fun awọn ile-iṣẹ kekere',
      heroSubtitle: 'Ṣakoso awọn olubasọrọ, awọn ilé-iṣẹ, awọn iṣowo ati awọn ipolongo pẹlu AI. Gba agbara lati itan owo Bissa.',
      heroCta: 'Bẹrẹ ọfẹ',
      heroSecondary: 'Wo iṣẹlẹ',
      featuresTitle: 'Kí lọ ti o yan Bissa.ai?',
      featuresSubtitle: 'Ohun gbogbo ti o nilo lati dagba iṣowo rẹ',
      feature1Title: 'AI ti o ṣe pọ',
      feature1Desc: 'Awọn imọran ti o yẹ ati awọn iṣẹ ti o ṣe adaṣe fun idagbasoke tita',
      feature2Title: 'Gbigbe kikun',
      feature2Desc: 'Awọn olubasọrọ, awọn ilé-iṣẹ, awọn iṣowo ati awọn ipolongo ni ibi kan',
      feature3Title: 'Rọrun lati lo',
      feature3Desc: 'Aaye ti o yẹ fun gbogbo ẹrọ',
      pricingTitle: 'Awọn ero ti o rọrun',
      pricingSubtitle: 'Yan ero ti o yẹ fun iṣowo rẹ',
      pricingFree: 'Ọfẹ',
      pricingFreeDesc: 'Yẹ fun ibẹrẹ',
      pricingGrowth: 'Idagbasoke',
      pricingGrowthDesc: 'Fun awọn ilé-iṣẹ ti n dagba',
      pricingOwner: 'Oluwa',
      pricingOwnerDesc: 'Awọn ẹya ti ko nilo iye',
      footerFeatures: 'Awọn ẹya',
      footerPricing: 'Awọn owo',
      footerSignUp: 'Forukọsilẹ',
      footerSignIn: 'Wọlé',
      footerLegal: 'Ofin',
      footerTerms: 'Awọn ofin iṣẹ',
      footerPrivacy: 'Eto ikọkọ',
      footerCookies: 'Eto awọn kuki',
      footerRefunds: 'Eto iṣanwo',
      footerDpa: 'Eto awọn data',
      footerInspire: 'Gba agbara lati itan owo Bissa ti o yipada pẹlu iwọle ati kikọ agbegbe.',
      allPlansNote: 'Gbogbo awọn ero pẹlu idanwo ọfẹ ti ọjọ 14. Ko nilo kaadi.',
      fallbackImage: 'Agbara iṣẹ CRM ti o yẹ',
      footerCopyright: '© 2024 Bissa.ai',
      footerMade: 'Ti ṣe pẹlu ❤️ fun awọn ilé-iṣẹ kekere',
      footerDescription: 'Kọ awọn ibatan bi awọn eniyan Bissa ti Burkina Faso — nipasẹ igbekẹle, ibatan ati awọn imọ AI ti o mu gbogbo ibatan iṣowo le.',
      heroDescription: 'Bi awọn eniyan Bissa ti Burkina Faso ṣe kọ awọn agbegbe alagbara nipasẹ igbekẹle ati ibatan, Bissa.ai mu iṣowo rẹ le pẹlu awọn imọ AI ti o mu gbogbo ibatan le.',
      cardRelationshipDesc: 'Kọ awọn ibatan jinlẹ pẹlu awọn profaili olubasọrọ ti o kun fun akọle, itan ibaraẹnisọrọ, ati awọn akiyesi ti ara.',
      cardTrustSalesDesc: 'Tọka awọn ibatan nipasẹ gbogbo ipin pẹlu pipeline ti o ri eyi ti o buwọlu irin-ajo kikọ igbekẹle.',
      cardOutreachDesc: 'So mọ ni otitọ pẹlu awọn ipolongo ti ara eyi ti o buwọlu iyato asa ati awọn ifẹ ti ara.',
      cardInsightsDesc: 'Loye agbara ibatan, awọn apẹrẹ ibaraẹnisọrọ, ati idagbasoke agbegbe pẹlu awọn iṣiro AI.',
      legacyDescription: 'Ti a mọ fun agbara wọn lati yipada ati ẹmi iṣowo, awọn eniyan Bissa kọ awọn ẹka iṣowo alagbara kọja Iwọ-oorun Afirika nipasẹ awọn ibatan jinlẹ, igbekẹle, ati ayipada asa.',
      bottomPrivacy: 'Ikọkọ',
      bottomTerms: 'Awọn ofin',
      bottomRefunds: 'Iṣanwo',
      heroTitlePrefix: 'Kọ Awọn Ibatan',
      heroTitleHighlight: ' Bii Bissa',
      ctaPrimary: 'Bẹrẹ Ọfẹ',
      ctaSecondary: 'Wo Awọn Ẹya',
      badgeNoCard: 'Ko nilo kaadi kankan',
      badgeFreeForever: 'Ọfẹ lailai',
      previewTitle: 'Dasibodu Bissa.ai',
      previewConnections: 'Awọn Asopọ',
      previewRevenue: 'Owo wiwọle',
      signIn: 'Wọle',
      featuresTitle: 'Ti a Kọ Fun Asopọ',
      featuresSubtitle: 'Awọn ohun elo alagbara ti o bọwọ fun iṣẹ kikọ ibatan, ti a mu dara pẹlu oye AI',
      cardRelationship: 'Iṣakoso Ibatan',
      cardTrustSales: 'Tita Ti o Da Lori Igbekẹle',
      cardOutreach: 'Ifiranṣẹ Ti O Ni Itumọ',
      cardInsights: 'Awọn Imọran Asopọ',
      pricingTitle: 'Awọn Ibatan Ti O Dagba Pẹlu Rẹ',
      pricingSubtitle: 'Bẹrẹ kikọ awọn asopọ lọfẹ. Dagba bi agbegbe rẹ ṣe n dagba. Ko si owo olumulo kọọkan.',
      free: 'Ọfẹ',
      growth: 'Idagbasoke',
      pro: 'Pro',
      business: 'Iṣowo',
      mostPopular: 'Olokiki Julọ',
      perMonth: '/oṣu',
      btnGetStarted: 'Bẹrẹ',
      btnStartTrial: 'Bẹrẹ Idanwo Ọfẹ',
      btnContactSales: 'Kan si Tita',
      storyBadge: 'Itan Wa',
      storyTitle: 'Nibi Ti Aṣa Ti Pade Imudara',
      storyP1: 'Awọn eniyan Bissa ti Burkina Faso ti ṣaṣeyọri fun ọgọrun ọdun nipasẹ awọn nẹtiwọọki to lagbara, awọn agbegbe ti o lagbara, ati awọn ibatan iṣowo ti o da lori igbekẹle.',
      storyP2: 'Loni, Bissa.ai n gbe ogún yii siwaju — n fun awọn iṣowo ode oni ni agbara lati kọ awọn ibatan pipẹ nipasẹ asopọ oloye ati awọn imọran AI.',
      legacyTitle: 'Ogún Bissa',
      legacyP1: 'Ti a mọ fun ifarada wọn ati ẹmi iṣowo wọn, awọn eniyan Bissa kọ awọn nẹtiwọọki iṣowo alaafia ni gbogbo apá ìwọ̀-oòrùn Afirika nipasẹ awọn ibatan jinlẹ, igbagbọ, ati ayẹyẹ ẹsin.',
      legacyQuote: 'Gẹgẹbi awọn Bissa ṣe ṣaṣeyọri nipasẹ awọn nẹtiwọọki ati awọn asopọ to lagbara, Bissa.ai n fun awọn iṣowo ni agbara lati ṣaṣeyọri nipasẹ awọn ibatan oloye.',
      legacyTagline: 'Ogún • Imudara • Asopọ',
      footerProduct: 'Ọja',
      pricingFeatures: {
        free: {
          contacts: '100 awọn olubasọrọ',
          users: 'Olumulo 1',
          crm: 'CRM ipilẹ',
          tasks: 'Iṣẹ 50/oṣu',
          support: 'Atilẹyin agbegbe'
        },
        growth: {
          contacts: '5,000 awọn olubasọrọ',
          users: '3 awọn olumulo',
          tasks: '500 iṣẹ/oṣu',
          campaigns: '25 ipolongo',
          integration: 'Isopọ imeeli',
          support: 'Atilẹyin imeeli'
        },
        pro: {
          contacts: '10,000 awọn olubasọrọ',
          users: '10 awọn olumulo',
          everything: 'Ohun gbogbo ni Growth',
          api: 'Wiwọle API',
          reports: 'Awọn iroyin to ga',
          support: 'Atilẹyin akọkọ'
        },
        business: {
          contacts: 'Awọn olubasọrọ ailopin',
          users: 'Awọn olumulo ailopin',
          everything: 'Ohun gbogbo ni Pro',
          whiteLabel: 'Aṣayan funfun-aami',
          integrations: 'Awọn isopọ akanṣe',
          manager: 'Oluṣakoso igbẹsẹ'
        }
      }
    },
    dashboard: {
      title: 'Dasibodu',
      welcome: 'Kaabo! Wo ohun ti o n ṣẹlẹ ni iṣowo rẹ.',
      totalContacts: 'Apapọ Olubasọrọ',
      companies: 'Ilé iṣẹ́',
      activeDeals: 'Ìdúnàdé Ti N Ṣiṣẹ',
      pendingTasks: 'Àwọn Iṣẹ́ Ti N Duro',
      totalRevenue: 'Apapọ Owo',
      dealValue: 'Iye Ìdúnàdé',
      conversionRate: 'Iye Iyipada',
      campaignsSent: 'Àwọn Ipolongo Ti Fi Ranṣẹ',
      quickActions: 'Àwọn Iṣẹ́ Kíkọ',
      addContact: 'Fi Olubasọrọ Tuntun Kun',
      createTask: 'Ṣẹda Iṣẹ́',
      createDeal: 'Ṣẹda Ìdúnàdé',
      newCampaign: 'Ipolongo Tuntun',
      recentActivity: 'Iṣẹ́ Ti O Ṣẹlẹ Lẹhinna',
      noRecent: 'Ko si iṣẹ́ ti o ṣẹlẹ lẹhinna',
      due: 'O ye ki:'
    },
    pages: {
      marketing: {
        title: 'Ibi Ipolongo',
        subtitle: 'Ṣẹda ati ṣakoso àwọn ipolongo imeeli',
        createCampaign: 'Ṣẹda Ipolongo',
        campaignName: 'Orukọ Ipolongo',
        subject: 'Koko ọrọ',
        content: 'Akoonu',
        sendTo: 'Fi Ranṣẹ Si',
        schedule: 'Ṣeto Akoko',
        send: 'Fi Ranṣẹ',
        campaigns: 'Àwọn Ipolongo',
        noCampaigns: 'Ko si ipolongo kan',
        createFirst: 'Ṣẹda ipolongo akọkọ rẹ!'
      },
      contacts: {
        title: 'Olubasọrọ',
        subtitle: 'Ṣakoso awọn ibatan iṣowo rẹ',
        addBtn: 'Fi Olubasọrọ Kun',
        noItems: 'Ko ri olubasọrọ kan. Ṣẹda olubasọrọ akọkọ rẹ!',
        searchPlaceholder: 'Wa olubasọrọ nipasẹ orukọ, imeeli, tabi ile-iṣẹ...'
      },
      companies: {
        title: 'Àwọn Ile-iṣẹ',
        subtitle: 'Ṣakoso awọn akaụntụ ile-iṣẹ rẹ',
        addBtn: 'Fi Ile-iṣẹ Kun',
        noItems: 'Ko ri ile-iṣẹ kan. Ṣẹda ile-iṣẹ akọkọ rẹ!',
        searchPlaceholder: 'Wa ile-iṣẹ nipasẹ orukọ, aagun, tabi ile-iṣẹ...'
      },
      deals: {
        title: 'Ọna Tita',
        subtitle: 'Tọka ati ṣakoso awọn idunade rẹ',
        addBtn: 'Fi Idunade Kun',
        noItems: 'Ko si idunade'
      },
      tasks: {
        title: 'Àwọn Iṣẹ́ ati Awọn Iṣẹ́',
        subtitle: 'Duro ni eto ati ma gbagbe akoko',
        newBtn: 'Iṣẹ́ Tuntun'
      },
      activities: {
        title: 'Oju Iṣẹ́',
        subtitle: 'Tọka gbogbo awọn iṣẹ́ lori akaụntụ rẹ',
        empty: 'Ko si iṣẹ́ kan. Bẹrẹ nipasẹ ṣiṣẹda olubasọrọ, idunade, tabi ipolongo!'
      },
      billing: {
        title: 'Iṣanwo ati Lilo',
        subtitle: 'Ṣakoso iforukọsilẹ rẹ ati tọka lilo',
        currentUsage: 'Lilo Lọwọlọwọ',
        subscription: 'Alaye Iforukọsilẹ',
        features: 'Àwọn Ẹya',
        availablePlans: 'Àwọn Ero Ti Wa',
        currentPlan: 'Ero Lọwọlọwọ',
        upgrade: 'Gbe Ga',
        downgrade: 'Gbe Sọkalẹ',
        approaching: 'Sunmọ Awọn Iye',
        approachingDesc: 'O sunmọ awọn iye ero rẹ. Ronu nipa gbigbe ga lati tẹsiwaju laisi idaduro.'
      },
      calendar: {
        title: 'Kálẹ́ńdà',
        subtitle: 'Wo ati ṣẹda awọn iṣẹlẹ Kálẹ́ńdà Google',
        refresh: 'Mu pada',
        createQuick: 'Ṣẹda Iṣẹlẹ Kíkọ',
        titleLabel: 'Akọle',
        startIso: 'Bibẹrẹ (ISO)',
        endIso: 'Ipari (ISO)',
        create: 'Ṣẹda',
        upcoming: 'Àwọn Iṣẹlẹ Ti N Bo',
        noEvents: 'Ko si iṣẹlẹ kan.',
        integrations: 'Takammanin Kalandar',
        googleConnected: 'An jira da Kalandar Google',
        googleDisconnected: 'Ba a jira da Kalandar Google',
        connectGoogle: 'Jira Google',
        disconnect: 'Kusa Jirar',
        outlookComingSoon: 'Takammanin Outlook yana zuwa',
        comingSoon: 'Yana zuwa'
      },
      importExport: {
        title: 'Gbe Wọle/Jade',
        subtitle: 'Ṣakoso data rẹ ni ọpọlọpọ pẹlu awọn faili CSV',
        contacts: 'Olubasọrọ',
        companies: 'Àwọn Ile-iṣẹ',
        importContacts: 'Gbe Olubasọrọ Wọle',
        importCompanies: 'Gbe Àwọn Ile-iṣẹ Wọle',
        uploadCsv: 'Gbe CSV Soke',
        importing: 'N gbe wọle...',
        template: 'Awoṣe',
        exportContacts: 'Mu Olubasọrọ Jade',
        exportCompanies: 'Mu Àwọn Ile-iṣẹ Jade',
        exportCsv: 'Mu CSV Jade',
        importComplete: 'Gbigbe Wọle Pari!',
        importFailed: 'Gbigbe Wọle Kuna',
        exportComplete: 'Gbigbe Jade Pari!',
        exportFailed: 'Gbigbe Jade Kuna',
        templateDownloaded: 'Awoṣe Ti Gbe Sọkalẹ',
        uploadHelp: 'Gbe faili CSV soke lati gbe ọpọlọpọ olubasọrọ wọle ni akoko kan',
        uploadCompaniesHelp: 'Gbe faili CSV soke lati gbe ọpọlọpọ ile-iṣẹ wọle ni akoko kan'
      },
      reports: {
        title: 'Àwọn Iroyin ati Awọn Iṣiro',
        subtitle: 'Tọka iṣẹ ati ṣe awọn ipinnu pẹlu data',
        overview: 'Akopọ',
        sales: 'Ọna Tita',
        contacts: 'Olubasọrọ',
        email: 'Iṣẹ Imeeli',
        export: 'Mu Data Jade',
        revenueByMonth: 'Owo nipasẹ oṣu',
        dealsByStage: 'Idunade nipasẹ ipele',
        pipelineValue: 'Iye nipasẹ ipele',
        activity30: 'Iṣẹ (Ọjọ 30 ti o kọja)',
        contactsByStatus: 'Olubasọrọ nipasẹ ipo',
        recentContacts: 'Olubasọrọ Ti O Ṣẹlẹ Lẹhinna',
        exportReports: 'Mu Àwọn Iroyin Jade',
        dealsReport: 'Iroyin Idunade',
        contactsReport: 'Iroyin Olubasọrọ'
      },
      aiInsights: { 
        title: 'Ìmọ̀ràn AI', 
        subtitle: 'Àgbàyé AI ti awọn ibatan iṣowo ati awọn anfani', 
        getInsights: 'Gba Ìmọ̀ràn AI', 
        startAnalysis: 'Bẹrẹ Àgbàyé AI', 
        welcome: 'Kaabo si Ìmọ̀ràn AI, {name}!', 
        description: 'Gba àgbàyé AI ti awọn olubasọrọ {count} ati awọn anfani iṣowo rẹ. Tẹ "Gba Ìmọ̀ràn AI" loke lati bẹrẹ àgbàyé rẹ.', 
        authRequired: 'Igbẹkẹle Ni O Nilọ', 
        authDescription: 'Jọwọ wọle lati gba àgbàyé AI.', 
        goToLogin: 'Lọ si Wọle', 
        businessInsights: 'Ìmọ̀ràn Iṣowo AI', 
        contactsEngagement: 'CRM rẹ fi awọn olubasọrọ {count} han pẹlu agbara iṣẹlẹ to lagbara.', 
        keyInsights: 'Ìmọ̀ràn Pataki:', 
        highPriorityContacts: 'O ni awọn olubasọrọ {count} ti o ni iyọnu to ga ti o nilo itọsi lailai', 
        activeSectors: 'Awọn olubasọrọ to ṣiṣẹ pupọ wa ni awọn ẹka ẹrọ ati imọran', 
        responseRate: 'Iwọn idahun aarin jẹ {rate}% - o ga ju iwọn ile-iṣẹ', 
        aiRecommendations: 'Awọn Imọran AI:', 
        focusContacts: 'Daju awọn olubasọrọ {count} pẹlu awọn aami iṣẹlẹ to ga julọ', 
        scheduleFollowups: 'Ṣeto awọn itọsi pẹlu awọn olutọkọ ti o ṣii awọn imeeli rẹ to kẹhin', 
        leadScoring: 'Esi Awọn Aami Oludari - awọn olubasọrọ {count} ti a ṣe ayẹwo', 
        hot: 'GBONA', 
        warm: 'TUTU', 
        cold: 'TUTU', 
        recentEngagement: 'Iṣẹlẹ laipe, Iye to ga', 
        multipleInteractions: 'Awọn ibaraẹnisọrọ pupọ, Oludamoran', 
        showedInterest: 'Fi ifẹ han, Owo ti jẹrisi' 
      },
      automations: {
        title: 'Adaṣe',
        subtitle: 'Ṣẹda awọn ofin ti o rọrun lati ṣe adaṣe awọn iṣẹ nigba ti awọn iṣẹlẹ pataki ba waye.',
        createTitle: 'Ṣẹda Ofin',
        name: 'Orukọ',
        trigger: 'Gbigbe',
        triggerContact: 'Olubasọrọ: Ipo Ti Yipada',
        triggerDeal: 'Iṣowo: Ipele Ti Yipada',
        enabled: 'Mu ṣiṣẹ',
        newStatus: 'Ipo Tuntun',
        newStage: 'Ipele Tuntun',
        action: 'Iṣẹ',
        createTask: 'Ṣẹda Iṣẹ',
        sendEmail: 'Fi Imẹẹli Ranṣẹ',
        dueDays: 'Awọn ọjọ Ti O To',
        taskTitle: 'Akọle Iṣẹ',
        priority: 'Ipataki',
        low: 'Kere',
        medium: 'Aarin',
        high: 'Ga',
        emailSubject: 'Akọle Imẹẹli',
        templateId: 'ID Awoṣe (ayẹ)',
        bodyHtml: 'Ara (HTML, ayẹ)',
        createBtn: 'Ṣẹda Ofin',
        yourRules: 'Awọn Ofin Rẹ',
        loading: 'N gba…',
        noRules: 'Ko si awọn ofin sibẹsibẹ.',
        enabledBadge: 'MU ṢIṢẸ',
        disabledBadge: 'KO MU ṢIṢẸ',
        disable: 'Mu Duro',
        enable: 'Mu Ṣiṣẹ',
        delete: 'Paarẹ',
        deleteConfirm: 'Paarẹ ofin yii?'
      },
      help: {
        title: 'Iranlọwọ ati Atilẹyin',
        searchPlaceholder: 'Wa awọn nkan iranlọwọ...',
        quickStart: 'Itọsọna Ibẹrẹ Kiakia',
        contactSupport: 'Kan si Atilẹyin',
        learnBasics: 'Kọ awọn ipilẹ ni iṣẹju 5',
        getHelp: 'Gba iranlọwọ lati ọdọ ẹgbẹ wa',
        commonQuestions: 'Awọn Ibeere Ti O Wọpọ',
        howToImport: 'Bawo ni MO ṣe le gbe awọn olubasọrọ mi wọle?',
        howToCreateDeal: 'Bawo ni MO ṣe le ṣẹda iṣowo kan?',
        canIUpgrade: 'Ṣe MO le gbe ero mi ga?',
        importAnswer: 'Lọ si oju-iwe Gbe Wọle/Jade ki o si gbe faili CSV rẹ soke. A ṣe atilẹyin awọn ọna deede lati ọpọlọpọ CRMs.',
        dealAnswer: 'Lo bọtini Fi Kun Kiakia tabi lọ si oju-iwe Awọn Iṣowo ki o tẹ "Fi Iṣowo Kun".',
        upgradeAnswer: 'Bẹẹni! Lọ si Ètò lati wo ati yipada ero iforukọsilẹ rẹ nigbakugba.'
      },
      support: {
        title: 'Kan si Atilẹyin',
        name: 'Orukọ',
        email: 'Imeeli',
        subject: 'Koko-ọrọ',
        category: 'Ẹka',
        message: 'Ifiranṣẹ',
        priority: 'Ipataki',
        general: 'Ibeere Gbogbogbo',
        technical: 'Iṣoro Imọ-ẹrọ',
        billing: 'Iṣanwo & Akaụntụ',
        feature: 'Ibeere Ẹya',
        bug: 'Iroyin Aṣiṣe',
        low: 'Kere - Ibeere gbogbogbo',
        medium: 'Aarin - Atilẹyin deede',
        high: 'Ga - Iṣoro kiakia',
        send: 'Fi Ifiranṣẹ Ranṣẹ',
        sending: 'N fi ranṣẹ...',
        success: 'A ti fi ifiranṣẹ ranṣẹ ni aṣeyọri!',
        error: 'Ikuna lati fi ifiranṣẹ ranṣẹ. Jọwọ gbiyanju lẹẹkansi.'
      },
      tutorial: {
        title: 'Kaabo si Bissa!',
        subtitle: 'Jẹ ki a kọ awọn ibatan ti o ni itumọ papọ',
        ready: 'Ṣetan lati kọ awọn asopọ bii Bissa?',
        description: 'Ẹkọ yii yoo fi han ọ bi Bissa.ai ṣe n fun iṣowo rẹ ni agbara pẹlu awọn ohun elo kikọ ibatan ti o bọwọ fun aṣa lakoko ti o gba imudara AI. Kọ lati mu gbogbo asopọ lagbara.',
        timeRequired: 'Akoko ti a nilo: iṣẹju 5',
        letsStart: 'Jẹ ki a bẹrẹ!',
        next: 'Itele',
        previous: 'Tẹlẹ',
        finish: 'Pari',
        close: 'Pa'
      }
    },
    onboarding: {
      welcomeTitle: 'Kaabo si Bissa.ai!',
      welcomeSubtitle: 'Jẹ ki a ṣeto ọ fun aṣeyọri',
      roleTitle: 'Kini ipa rẹ?',
      roleSubtitle: 'Ran wa lọwọ lati ṣe iriri rẹ pataki',
      companyTitle: 'Sọ fun wa nipa ile-iṣẹ rẹ',
      companySubtitle: 'Eyi yoo ran wa lọwọ lati ṣe awọn ẹya pataki fun ọ',
      goalsTitle: 'Kini ero akọkọ rẹ?',
      goalsSubtitle: 'A o da lori awọn ẹya ti o ṣe pataki julọ',
      setupTitle: 'Jẹ ki a ṣe awọn akọkọ rẹ',
      setupSubtitle: 'Bẹrẹ pẹlu awọn data apẹrẹ',
      completeTitle: 'O ti ṣetan!',
      completeSubtitle: 'Kaabo si agbegbe Bissa.ai',
      step: 'Igbesẹ',
      of: 'lati',
      back: 'Pada',
      next: 'Tẹle',
      getStarted: 'Bẹrẹ',
      welcomeMessage: 'Kaabo!',
      welcomeDescription: 'Bi awọn eniyan Bissa ṣe kọ awọn agbegbe ti o ni aṣeyọri nipasẹ awọn ibatan ti o lagbara, a o ran ọ lọwọ lati kọ awọn ibatan owo ti o duro pẹlu awọn imọran AI.',
      timeEstimate: '✨ Eyi yoo gba nkan bi iṣẹju 2'
    }
  },
  ig: {
    nav: {
      dashboard: 'Dashbọọdụ',
      aiInsights: 'Amamihe AI',
      contacts: 'Kọntaktị',
      companies: 'Ụlọ ọrụ',
      deals: 'Nkwekọrịta',
      marketing: 'Mākẹtịn',
      tasks: 'Ọrụ',
      reports: 'Akụkọ',
      activities: 'Omume',
      settings: 'Ntọala',
      billing: 'Ịkwụ ụgwọ',
      automations: 'Akpaka',
      importExport: 'Mbubata/Mbugharị',
      calendar: 'Kalennda',
    },
    header: { 
      searchPlaceholder: 'Chọọ kọntaktị, ụlọ ọrụ, nkwekọrịta...',
      notifications: 'Ozi',
      profile: 'Profailụ',
      settings: 'Ntọala',
      logout: 'Pụọ',
      language: 'Asụsụ'
    },
    auth: {
      authenticating: 'Na-akwado...'
    },
    landing: {
      heroBadge: 'Nke a bụ ahịa na-agbanwe agbanwe na AI',
      heroTitlePrefix: 'Wulite mmekọrịta',
      heroTitleHighlight: ' dị ka Bissa',
      heroTitle: 'Bissa.ai: CRM dabara adaba maka ụlọ ọrụ ndị pere mpe',
      heroSubtitle: 'Jikwaa kọntaktị, ụlọ ọrụ, azụmahịa na mkpọsa site na AI. Nweta ike site na akụkọ azụmahịa Bissa.',
      heroCta: 'Malite n\'efu',
      heroSecondary: 'Hụ ngosi',
      ctaPrimary: 'Malite n\'efu',
      ctaSecondary: 'Hụ atụmatụ',
      featuresTitle: 'Gịnị mere ị họrọ Bissa.ai?',
      featuresSubtitle: 'Ihe niile ị chọrọ iji mee ka azụmahịa gị too',
      feature1Title: 'AI jikọtara',
      feature1Desc: 'Nghọta dabara adaba na ọrụ akpaaka maka mmepe ire ahịa',
      feature2Title: 'Nchịkwa zuru ezu',
      feature2Desc: 'Kọntaktị, ụlọ ọrụ, azụmahịa na mkpọsa n\'otu ebe',
      feature3Title: 'Dị mfe iji',
      feature3Desc: 'Interface dabara adaba maka ngwaọrụ ọ bụla',
      pricingTitle: 'Atụmatụ dị mfe',
      pricingSubtitle: 'Họrọ atụmatụ dabara adaba maka azụmahịa gị',
      pricingFree: 'N\'efu',
      pricingFreeDesc: 'Dabara adaba maka mmalite',
      pricingGrowth: 'Uto',
      pricingGrowthDesc: 'Maka ụlọ ọrụ na-eto eto',
      pricingOwner: 'Onye nwe',
      pricingOwnerDesc: 'Atụmatụ na-enweghị oke',
      footerFeatures: 'Atụmatụ',
      footerPricing: 'Ọnụ ego',
      footerSignUp: 'Debanye aha',
      footerSignIn: 'Banye',
      footerLegal: 'Iwu',
      footerTerms: 'Usoro ọrụ',
      footerPrivacy: 'Iwu nzuzo',
      footerCookies: 'Iwu kuki',
      footerRefunds: 'Iwu nkwụghachi',
      footerDpa: 'Iwu data',
      footerInspire: 'Nweta ike site na akụkọ azụmahịa Bissa na-agbanwe agbanwe na iwulite obodo.',
      allPlansNote: 'Atụmatụ niile gụnyere nnwale n\'efu nke ụbọchị 14. Achọghị kaadị.',
      fallbackImage: 'Ebe ọrụ CRM ọkachamara',
      storyBadge: 'Akụkọ Anyị',
      storyTitle: 'Ebe omenala na-ezute ihe ọhụrụ',
      storyP1: 'Ndị Bissa na Burkina Faso enwetawo ọganihu n\'azụmahịa na azụmahịa ruo ọtụtụ narị afọ site na netwọk siri ike, obodo na-agbanwe agbanwe, na mmekọrịta dabere na ntụkwasị obi.',
      storyP2: 'Taa, Bissa.ai na-aga n\'ihu n\'omenala a — na-enye azụmahịa ọgbara ọhụrụ ike iwulite mmekọrịta na-adịgide adịgide site na njikọta nwere ọgụgụ isi na nghọta AI na-eduzi.',
      legacyTitle: 'Omenala Bissa',
      legacyP1: 'A maara ha maka mgbanwe ha na mmụọ azụmahịa, ndị Bissa wuru netwọk azụmahịa na-aga nke ọma n\'ofe West Africa site na mmekọrịta miri emi, ntụkwasị obi, na mgbanwe omenala.',
      legacyQuote: 'Dị ka ndị Bissa si nweta ọganihu site na netwọk na mmekọrịta siri ike, Bissa.ai na-enye azụmahịa ike ịga nke ọma site na njikọta nwere ọgụgụ isi.',
      buildRelationships: 'Wulite mmekọrịta dị ka Bissa?',
      btnGetStarted: 'Malite n\'efu',
      btnStartTrial: 'Malite nwale n\'efu',
      btnContactSales: 'Kpọtụrụ ahịa',
      free: 'N\'efu',
      growth: 'Uto',
      pro: 'Pro',
      business: 'Azụmahịa',
      mostPopular: 'Kacha ewu ewu',
      perMonth: '/ọnwa',
      footerCopyright: '© 2024 Bissa.ai',
      footerMade: 'Emebere ya na ❤️ maka ụlọ ọrụ ndị pere mpe',
      footerDescription: 'Wulite mmekọrịta dị ka ndị Bissa nke Burkina Faso — site na ntụkwasị obi, njikọ na nghọta AI nke na-ewusi mmekọrịta azụmahịa ọ bụla ike.',
      heroDescription: 'Dị ka ndị Bissa nke Burkina Faso si wulite obodo ndị na-eme nke ọma site na ntụkwasị obi na njikọ, Bissa.ai na-enye azụmahịa gị ike site na nghọta AI nke na-ewusi mmekọrịta ọ bụla ike.',
      cardRelationshipDesc: 'Wulite mmekọrịta miri emi na profaịlụ kọntaktị bara ụba na gburugburu, akụkọ mmekọrịta, na ndetu nkeonwe.',
      cardTrustSalesDesc: 'Soro mmekọrịta site na ọkwa ọ bụla na pipeline a na-ahụ anya nke na-asọpụrụ njem nke iwulite ntụkwasị obi.',
      cardOutreachDesc: 'Jikọọ n\'eziokwu na mgbasa ozi ahaziri nke na-asọpụrụ ọdịiche omenala na mmasị onye ọ bụla.',
      cardInsightsDesc: 'Ghọta ike mmekọrịta, usoro ntinye aka, na uto obodo na nyocha AI.',
      legacyDescription: 'A maara ha maka ikike ha ịgbanwe na mmụọ azụmahịa, ndị Bissa wuru netwọk azụmahịa na-aga nke ọma n\'ofe West Africa site na mmekọrịta miri emi, ntụkwasị obi, na mgbanwe omenala.',
      bottomPrivacy: 'Nzuzo',
      bottomTerms: 'Usoro',
      bottomRefunds: 'Nkwụghachi',
      badgeNoCard: 'Achọghị kaadị',
      badgeFreeForever: 'N\'efu ruo mgbe ebighị ebi',
      previewTitle: 'Dashbọọdụ Bissa.ai',
      previewConnections: 'Njikọ',
      previewRevenue: 'Ego',
      signIn: 'Banye',
      featuresTitle: 'Emebere Maka Njikọ',
      featuresSubtitle: 'Ngwaọrụ dị ike nke na-asọpụrụ nkà nke iwulite mmekọrịta, emere ka ọ ka mma na ọgụgụ isi AI',
      cardRelationship: 'Njikwa Mmekọrịta',
      cardTrustSales: 'Ahịa Dabere na Ntụkwasị obi',
      cardOutreach: 'Iru eru Nwere Ihe Ọ Pụtara',
      cardInsights: 'Nghọta Njikọ',
      pricingTitle: 'Mmekọrịta Na-eto eto Na Gị',
      pricingSubtitle: 'Malite iwulite njikọ n\'efu. Too ka obodo gị na-eto. Enweghị ụgwọ kwa onye ọrụ.',
      legacyTagline: 'Ihe Nketa • Innovation • Njikọ',
      footerProduct: 'Ngwaahịa',
      pricingFeatures: {
        free: {
          contacts: '100 kọntaktị',
          users: 'Onye ọrụ 1',
          crm: 'CRM nkịtị',
          tasks: 'Ọrụ 50/ọnwa',
          support: 'Nkwado obodo'
        },
        growth: {
          contacts: '5,000 kọntaktị',
          users: '3 ndị ọrụ',
          tasks: '500 ọrụ/ọnwa',
          campaigns: '25 mkpọsa',
          integration: 'Njikọ email',
          support: 'Nkwado email'
        },
        pro: {
          contacts: '10,000 kọntaktị',
          users: '10 ndị ọrụ',
          everything: 'Ihe niile na Uto',
          api: 'Ịnweta API',
          reports: 'Akụkọ dị elu',
          support: 'Nkwado mbụ'
        },
        business: {
          contacts: 'Kọntaktị na-enweghị oke',
          users: 'Ndị ọrụ na-enweghị oke',
          everything: 'Ihe niile na Pro',
          whiteLabel: 'Nhọrọ ọcha-label',
          integrations: 'Njikọ ahaziri',
          manager: 'Onye njikwa a họpụtara'
        }
      }
    },
    dashboard: {
      title: 'Dashbọọdụ',
      welcome: 'Nabata! Hụ ihe na-eme na azụmahịa gị.',
      totalContacts: 'Ngụkọta Kọntaktị',
      companies: 'Ụlọ ọrụ',
      activeDeals: 'Nkwekọrịta Na-arụ Ọrụ',
      pendingTasks: 'Ọrụ Na-echere',
      totalRevenue: 'Ngụkọta Ego',
      dealValue: 'Uru Nkwekọrịta',
      conversionRate: 'Ọnụego Ntụgharị',
      campaignsSent: 'Mkpọsa Ezigara',
      quickActions: 'Omume Ngwa Ngwa',
      addContact: 'Tinye Kọntaktị Ọhụrụ',
      createTask: 'Mepụta Ọrụ',
      createDeal: 'Mepụta Nkwekọrịta',
      newCampaign: 'Mkpọsa Ọhụrụ',
      recentActivity: 'Omume Na-adịbeghị anya',
      noRecent: 'Enweghị ọrụ na-adịbeghị anya',
      due: 'Kwesịrị:'
    },
    pages: {
      marketing: {
        title: 'Ebe Mkpọsa',
        subtitle: 'Mepụta mmekọrịta azụmahịa gị',
        createCampaign: 'Mepụta Mkpọsa',
        campaignName: 'Aha Mkpọsa',
        subject: 'Isiokwu',
        content: 'Ọdịnaya',
        sendTo: 'Ziga Nye',
        schedule: 'Hazie Oge',
        send: 'Ziga',
        campaigns: 'Mkpọsa',
        noCampaigns: 'Enweghị mkpọsa ọ bụla',
        createFirst: 'Mepụta mkpọsa mbụ gị!'
      },
      contacts: {
        title: 'Kọntaktị',
        subtitle: 'Jikwaa mmekọrịta azụmahịa gị',
        addBtn: 'Tinye Kọntaktị',
        noItems: 'Achọtaghị kọntaktị ọ bụla. Mepụta kọntaktị mbụ gị!',
        searchPlaceholder: 'Chọọ kọntaktị site na aha, email, ma ọ bụ ụlọ ọrụ...'
      },
      companies: {
        title: 'Ụlọ Ọrụ',
        subtitle: 'Jikwaa akaụntụ ụlọ ọrụ gị',
        addBtn: 'Tinye Ụlọ Ọrụ',
        noItems: 'Achọtaghị ụlọ ọrụ ọ bụla. Mepụta ụlọ ọrụ mbụ gị!',
        searchPlaceholder: 'Chọọ ụlọ ọrụ site na aha, ngalaba, ma ọ bụ ụlọ ọrụ...'
      },
      deals: {
        title: 'Usoro Ahịa',
        subtitle: 'Soro ma jikwaa nkwekọrịta gị',
        addBtn: 'Tinye Nkwekọrịta',
        noItems: 'Enweghị nkwekọrịta'
      },
      tasks: {
        title: 'Ọrụ na Ncheta',
        subtitle: 'Nọrọ n\'usoro ma ghara ịhapụ oge',
        newBtn: 'Ọrụ Ọhụrụ'
      },
      activities: {
        title: 'Akara Omume',
        subtitle: 'Soro ọrụ niile n\'akaụntụ gị',
        empty: 'Ọ dịghị omume. Malite site na imepụta kọntaktị, nkwekọrịta, ma ọ bụ mkpọsa!'
      },
      billing: {
        title: 'Ịkwụ Ụgwọ na Ojiji',
        subtitle: 'Jikwaa ndebanye aha gị ma soro ojiji',
        currentUsage: 'Ojiji Ugbu a',
        subscription: 'Nkọwa Ndebanye Aha',
        features: 'Atụmatụ',
        availablePlans: 'Atụmatụ Dị',
        currentPlan: 'Atụmatụ Ugbu a',
        upgrade: 'Kwalite',
        downgrade: 'Belata',
        approaching: 'Oke Na-abịaru Nso',
        approachingDesc: 'Ị na-abịaru nso oke atụmatụ gị. Chee echiche banyere ịkwalite ka ị nọgide na-eji atụmatụ niile na-enweghị nkwụsị.'
      },
      calendar: {
        title: 'Calendar',
        subtitle: 'View and create Google Calendar events',
        refresh: 'Refresh',
        createQuick: 'Create Quick Event',
        titleLabel: 'Title',
        startIso: 'Start (ISO)',
        endIso: 'End (ISO)',
        create: 'Create',
        upcoming: 'Upcoming Events',
        noEvents: 'No events.',
        integrations: 'Calendar Integrations',
        googleConnected: 'Connected to Google Calendar',
        googleDisconnected: 'Not connected to Google Calendar',
        connectGoogle: 'Connect Google',
        disconnect: 'Disconnect',
        outlookComingSoon: 'Outlook integration coming soon',
        comingSoon: 'Coming Soon'
      },
      importExport: {
        title: 'Bubata/Bugharịa',
        subtitle: 'Jikwaa data gị n\'ụzọ ọtụtụ site na faịlụ CSV',
        contacts: 'Kọntaktị',
        companies: 'Ụlọ Ọrụ',
        importContacts: 'Bubata Kọntaktị',
        importCompanies: 'Bubata Ụlọ Ọrụ',
        uploadCsv: 'Bulite CSV',
        importing: 'Na-ebubata...',
        template: 'Ndebiri',
        exportContacts: 'Bugharịa Kọntaktị',
        exportCompanies: 'Bugharịa Ụlọ Ọrụ',
        exportCsv: 'Bugharịa CSV',
        importComplete: 'Mbubata Gwụchara!',
        importFailed: 'Mbubata Akwụsịghị',
        exportComplete: 'Mbugharị Gwụchara!',
        exportFailed: 'Mbugharị Akwụsịghị',
        templateDownloaded: 'Ebudata Ndebiri',
        uploadHelp: 'Bulite faịlụ CSV iji bubata ọtụtụ kọntaktị otu oge',
        uploadCompaniesHelp: 'Bulite faịlụ CSV iji bubata ọtụtụ ụlọ ọrụ otu oge'
      },
      reports: {
        title: 'Akụkọ na Nyocha',
        subtitle: 'Soro ọrụ ma mee mkpebi dabere na data',
        overview: 'Nlebanya',
        sales: 'Usoro Ahịa',
        contacts: 'Kọntaktị',
        email: 'Ọrụ Email',
        export: 'Bugharịa Data',
        revenueByMonth: 'Ego site na ọnwa',
        dealsByStage: 'Nkwekọrịta site na ọkwa',
        pipelineValue: 'Uru site na ọkwa',
        activity30: 'Omume (Ụbọchị 30 gara aga)',
        contactsByStatus: 'Kọntaktị site na ọnọdụ',
        recentContacts: 'Kọntaktị Na-adịbeghị Anya',
        exportReports: 'Bugharịa Akụkọ',
        dealsReport: 'Akụkọ Nkwekọrịta',
        contactsReport: 'Akụkọ Kọntaktị'
      },
      aiInsights: { 
        title: 'Amamihe AI', 
        subtitle: 'Nyocha AI nke mmekọrịta azụmahịa gị na ohere', 
        getInsights: 'Nweta Amamihe AI', 
        startAnalysis: 'Malite Nyocha AI', 
        welcome: 'Nabata na Amamihe AI, {name}!', 
        description: 'Nweta nyocha AI nke kọntaktị {count} gị na ohere azụmahịa. Pịa "Nweta Amamihe AI" n\'elu iji malite nyocha gị.', 
        authRequired: 'Achọrọ Nkwado', 
        authDescription: 'Biko banye iji nweta nyocha AI.', 
        goToLogin: 'Gaa na Nbanye', 
        businessInsights: 'Amamihe Azụmahịa AI', 
        contactsEngagement: 'CRM gị na-egosi kọntaktị {count} nwere ike itinye aka siri ike.', 
        keyInsights: 'Amamihe Isi:', 
        highPriorityContacts: 'Ị nwere kọntaktị {count} dị mkpa nke chọrọ nleta ozugbo', 
        activeSectors: 'Ọtụtụ kọntaktị na-arụ ọrụ nọ na ngalaba teknụzụ na ndụmọdụ', 
        responseRate: 'Ọnụego nzaghachi nkezi bụ {rate}% - karịrị ọkọlọtọ ụlọ ọrụ', 
        aiRecommendations: 'Ndụmọdụ AI:', 
        focusContacts: 'Lekwasị anya na kọntaktị {count} nwere akara ntinye aka kacha elu', 
        scheduleFollowups: 'Hazie nleta na ndị nwere mmasị meghere email gị ikpeazụ', 
        leadScoring: 'Nsonaazụ Akara Ndu - nyochara kọntaktị {count}', 
        hot: 'NA-EKU ỌKỤ', 
        warm: 'NA-EKPO ỌKỤ', 
        cold: 'OYI', 
        recentEngagement: 'Ntinye aka na-adịbeghị anya, Uru dị elu', 
        multipleInteractions: 'Mmekọrịta ọtụtụ, Onye na-eme mkpebi', 
        showedInterest: 'Gosiri mmasị, Akwadoro ego' 
      },
      automations: {
        title: 'Akpaaka',
        subtitle: 'Mepụta iwu dị mfe iji mee ka ọrụ bụrụ akpaaka mgbe ihe ndị dị mkpa mere.',
        createTitle: 'Mepụta Iwu',
        name: 'Aha',
        trigger: 'Ihe Mkpali',
        triggerContact: 'Kọntaktị: Ọnọdụ Gbanwere',
        triggerDeal: 'Nkwekọrịta: Ọkwa Gbanwere',
        enabled: 'Kwadoro',
        newStatus: 'Ọnọdụ Ọhụrụ',
        newStage: 'Ọkwa Ọhụrụ',
        action: 'Omume',
        createTask: 'Mepụta Ọrụ',
        sendEmail: 'Ziga Email',
        dueDays: 'Ụbọchị Kwesịrị',
        taskTitle: 'Isiokwu Ọrụ',
        priority: 'Ihe Kacha Mkpa',
        low: 'Dị ala',
        medium: 'Etiti',
        high: 'Dị elu',
        emailSubject: 'Isiokwu Email',
        templateId: 'ID Ndebiri (nhọrọ)',
        bodyHtml: 'Ahụ (HTML, nhọrọ)',
        createBtn: 'Mepụta Iwu',
        yourRules: 'Iwu Gị',
        loading: 'Na-ebu…',
        noRules: 'Enweghị iwu ugbu a.',
        enabledBadge: 'KWADORO',
        disabledBadge: 'AKWADOGHỊ',
        disable: 'Kwụsị',
        enable: 'Kwado',
        delete: 'Hichapụ',
        deleteConfirm: 'Hichapụ iwu a?'
      },
      help: {
        title: 'Enyemaka na Nkwado',
        searchPlaceholder: 'Chọọ edemede enyemaka...',
        quickStart: 'Ntuziaka Mmalite Ngwa Ngwa',
        contactSupport: 'Kpọtụrụ Nkwado',
        learnBasics: 'Mụta ihe ndị bụ isi na nkeji 5',
        getHelp: 'Nweta enyemaka site na ndị otu anyị',
        commonQuestions: 'Ajụjụ A Na-ajụkarị',
        howToImport: 'Kedu ka m ga-esi bubata kọntaktị m?',
        howToCreateDeal: 'Kedu ka m ga-esi mepụta nkwekọrịta?',
        canIUpgrade: 'Enwere m ike ịkwalite atụmatụ m?',
        importAnswer: 'Gaa na ibe Bubata/Bugharịa wee bulite faịlụ CSV gị. Anyị na-akwado usoro ọkọlọtọ sitere na ọtụtụ CRMs.',
        dealAnswer: 'Jiri bọtịnụ Tinye Ngwa Ngwa ma ọ bụ gaa na ibe Nkwekọrịta wee pịa "Tinye Nkwekọrịta".',
        upgradeAnswer: 'Ee! Gaa na Ntọala ka ịhụ ma gbanwee atụmatụ ndebanye aha gị mgbe ọ bụla.'
      },
      support: {
        title: 'Kpọtụrụ Nkwado',
        name: 'Aha',
        email: 'Email',
        subject: 'Isiokwu',
        category: 'Ụdị',
        message: 'Ozi',
        priority: 'Ihe Kacha Mkpa',
        general: 'Ajụjụ Izugbe',
        technical: 'Nsogbu Teknụzụ',
        billing: 'Ịkwụ Ụgwọ na Akaụntụ',
        feature: 'Arịrịọ Atụmatụ',
        bug: 'Akụkọ Mperi',
        low: 'Dị ala - Ajụjụ izugbe',
        medium: 'Etiti - Nkwado ọkọlọtọ',
        high: 'Dị elu - Nsogbu ngwa ngwa',
        send: 'Ziga Ozi',
        sending: 'Na-eziga...',
        success: 'Ezigara ozi nke ọma!',
        error: 'Enweghị ike iziga ozi. Biko nwaa ọzọ.'
      },
      tutorial: {
        title: 'Nabata na Bissa!',
        subtitle: 'Ka anyị wuo mmekọrịta nwere isi ọnụ',
        ready: 'Ị dị njikere ịwulite njikọ dị ka Bissa?',
        description: 'Nkuzi a ga-egosi gị ka Bissa.ai si enye azụmahịa gị ike site na ngwaọrụ iwulite mmekọrịta nke na-asọpụrụ ọdịnala mgbe ọ na-anabata ọhụụ AI. Mụta isi mee ka njikọ ọ bụla sie ike.',
        timeRequired: 'Oge achọrọ: nkeji 5',
        letsStart: 'Ka anyị malite!',
        next: 'Ọzọ',
        previous: 'Nke Gara Aga',
        finish: 'Mechaa',
        close: 'Mechie'
      }
    },
    onboarding: {
      welcomeTitle: 'Nabata na Bissa.ai!',
      welcomeSubtitle: 'Ka anyị hazie gị maka ọganiihu',
      roleTitle: 'Gịnị bụ ọrụ gị?',
      roleSubtitle: 'Nyere anyị aka ime ka ahụmịhe gị bụrụ nke gị',
      companyTitle: 'Gwa anyị banyere ụlọ ọrụ gị',
      companySubtitle: 'Nke a na-enyere anyị aka ịhazi atụmatụ maka gị',
      goalsTitle: 'Gịnị bụ ebumnuche gị bụ isi?',
      goalsSubtitle: 'Anyị ga-elekwasị anya n\'ihe kacha mkpa',
      setupTitle: 'Ka anyị mepụta ndekọ mbụ gị',
      setupSubtitle: 'Malite na data nlele',
      completeTitle: 'Ị dị njikere!',
      completeSubtitle: 'Nabata na obodo Bissa.ai',
      step: 'Nzọụkwụ',
      of: 'nke',
      back: 'Azụ',
      next: 'Ọzọ',
      getStarted: 'Malite',
      welcomeMessage: 'Nabata!',
      welcomeDescription: 'Dị ka ndị Bissa si wulite obodo ndị na-eme nke ọma site na mmekọrịta siri ike, anyị ga-enyere gị aka ịwulite njikọ azụmahịa na-adịgide adịgide site na nghọta AI.',
      timeEstimate: '✨ Nke a ga-ewe ihe dị ka nkeji 2'
    },
    aiInsights: {
      title: 'Nghọta AI',
      subtitle: 'Ntụle AI nke mmekọrịta azụmahịa gị na ohere',
      getInsights: 'Nweta Nghọta AI',
      startAnalysis: 'Malite Ntụle AI',
      welcome: 'Nnọọ na Nghọta AI, {name}!',
      description: 'Nweta ntụle AI nke ndị kọntaktị gị {count} na ohere azụmahịa. Pịa "Nweta Nghọta AI" n\'elu iji malite ntụle gị.',
      authRequired: 'Nkwado Chọrọ',
      authDescription: 'Biko banye iji nweta ntụle AI.',
      goToLogin: 'Gaa na Nbanye',
      businessInsights: 'Nghọta Azụmahịa AI',
      contactsEngagement: 'CRM gị na-egosi ndị kọntaktị {count} nwere ikike itinye aka siri ike.',
      keyInsights: 'Nghọta Isi:',
      highPriorityContacts: 'Ị nwere ndị kọntaktị {count} dị elu chọrọ nleba anya ozugbo',
      activeSectors: 'Ndị kọntaktị kachasị arụ ọrụ nọ na ngalaba teknụzụ na ndụmọdụ',
      responseRate: 'Ọnụ ọgụgụ nzaghachi nkezi bụ {rate}% - karịa ọkọlọtọ ụlọ ọrụ',
      aiRecommendations: 'Ndụmọdụ AI:',
      focusContacts: 'Lekwasị anya na ndị kọntaktị {count} nwere akara itinye aka kachasị elu',
      scheduleFollowups: 'Hazie nleba anya na ndị mepere email ikpeazụ gị',
      leadScoring: 'Akara ndu - A tụlere ndị kọntaktị {count}',
      recentEngagement: 'Itinye aka na nso nso a',
      multipleInteractions: 'Ọtụtụ mmekọrịta',
      showedInterest: 'Gosipụtara mmasị',
      hot: 'Na-ekpo ọkụ',
      warm: 'Na-ekpo ọkụ',
      cold: 'Oyi'
    }
  }
};

// Merge extra translations
Object.assign(translations, extraTranslations);

export const I18nProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('language');
    return saved || 'en';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const t = (key, fallback = key) => {
    const keys = key.split('.');
    let value = translations[language];
    
    for (const k of keys) {
      value = value?.[k];
      if (!value) break;
    }
    
    return value || fallback;
  };

  const value = useMemo(() => ({ t, language, setLanguage }), [language]);

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within I18nProvider');
  }
  return context;
};